import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';

/*
 * ── Decomposition Hooks ──
 * The following custom hooks have been extracted from this file and are
 * available under src/customHooks/. They encapsulate self-contained domains
 * and can be incrementally wired into this component to replace the inline
 * logic below.
 *
 *   useSessionTimer      — session countdown timer logic  (lines ~320-370)
 *   useKeyboardShortcuts — R/A/C/Escape handlers          (lines ~370-400)
 *   useEC2Session        — EC2 lifecycle + health check    (lines ~750-1060, 1935-2020, 2789-2830)
 *   useMaskOperations    — RLE decode, canvas draw, worker (lines ~2017-2290, bitmap cache)
 *   useObjectManager     — object CRUD + refs              (lines ~132-265, 2495-2510)
 *   useAnalysisRestore   — restore mode data loading       (lines ~415-750)
 *
 */
import {
  Box,
  Typography,
  Button,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getS3Client, getDynamoDBClient } from '../utils/awsClients';
import { decodeCOCORLE } from '../utils/rle';
import { buildTimeAxis } from '../utils/frameTime';
import useS3Images from '../customHooks/useS3Images';
import useEC2Session from '../customHooks/useEC2Session';
import GrowthAnalysisChart from '../components/GrowthAnalysisChart';
import RugosityAnalysisChart from '../components/RugosityAnalysisChart';
import PigmentationAnalysisChart from '../components/PigmentationAnalysisChart';
import SpiralAnalysisChart from '../components/SpiralAnalysisChart';
import { useSnackbar } from 'notistack';
import { debounce } from 'lodash';
import pako from 'pako';
import useFrameManager from '../customHooks/useFrameManager';
import FrameCanvas from '../components/FrameCanvas';
import FrameScrubber, { TimestampOverlay } from '../components/FrameScrubber';
import AssistantPanel from '../components/AssistantPanel';
import ChatAgentPanel from '../components/ChatAgentPanel';
import { CircularProgress, Tabs, Tab, Menu, MenuItem, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import ObjectManagerPanel from '../components/ObjectManagerPanel';
import ColonySummaryPanel from '../components/ColonySummaryPanel';
import ColonyPipelinePicker from '../components/ColonyPipelinePicker';
import MorphThresholdDialog from '../components/MorphThresholdDialog';
import FindSimilarDialog from '../components/FindSimilarDialog';
import SingleImageRestoreVizPanel from '../components/SingleImageRestoreVizPanel';
import ShapeMetricsPanel from '../components/ShapeMetricsPanel';
import Navbar from '../components/Navbar';
import { Slider, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
// Aliased: the bare `Tooltip` in this file is chart.js's plugin, imported below.
import { Tooltip as MuiTooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { buildWellExport, inferExperimentType, EXPERIMENT_I } from '../utils/wellExport';
// objectIds is aliased: this file already has four local `const objectIds`
// arrays, and the bare name collided with them under webpack scope
// hoisting -- the imported function was dropped from the bundle while the
// call site survived, binding to an unrelated vendor global at runtime.
import { bundleFor, objectIds as wellObjectIds, identityFromName, timeResolver } from '../utils/wellExportData';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

const HSVVisualization = React.lazy(() => import('../components/HSVVisualization'));

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend, zoomPlugin);

// ── Colony Count floating toolbars ──
// Shared between the editing view and the timeline-with-masks view.

const ALIGN_TOOLBAR_SX = {
  position: 'absolute',
  bottom: 16,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  bgcolor: 'rgba(0,0,0,0.85)',
  px: 3,
  py: 1.5,
  borderRadius: 2,
  zIndex: 20,
  border: '1px solid rgba(255,220,50,0.5)',
  flexWrap: 'wrap',
  maxWidth: '90vw',
};

function AlignCircleToolbar({ countMode, onConfirm, onCancel }) {
  const title = countMode === '90mm' ? 'Align 90mm Dish' : 'Align Spiral Grid';
  return (
    <Box sx={ALIGN_TOOLBAR_SX}>
      <Typography variant="body2" sx={{ color: '#ffd632', fontWeight: 600 }}>{title}</Typography>
      <Typography variant="caption" sx={{ color: '#aaa', maxWidth: 280 }}>
        Drag to move, drag edge to resize, scroll to adjust radius
      </Typography>
      <Button variant="contained" size="small" onClick={onConfirm}
        sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' }, minWidth: 80 }}>
        Confirm
      </Button>
      <Button variant="outlined" size="small" onClick={onCancel}
        sx={{ color: '#fff', borderColor: '#666', '&:hover': { borderColor: '#aaa' }, minWidth: 80 }}>
        Cancel
      </Button>
    </Box>
  );
}

function AlignRectangleToolbar({ rectGrid, onGridSizeChange, onAddHorizontal, onAddVertical, onConfirm, onCancel }) {
  const sizes = [2, 3, 4, 5, 6];
  const activeSize = rectGrid.rows === rectGrid.cols ? rectGrid.rows : null;
  return (
    <Box sx={ALIGN_TOOLBAR_SX}>
      <Typography variant="body2" sx={{ color: '#ffd632', fontWeight: 600 }}>Align Rectangular Dish</Typography>
      <Typography variant="caption" sx={{ color: '#aaa' }}>Grid:</Typography>
      {sizes.map((n) => (
        <Button key={n} size="small"
          variant={activeSize === n ? 'contained' : 'outlined'}
          onClick={() => onGridSizeChange(n, n)}
          sx={{ minWidth: 44, color: activeSize === n ? '#fff' : '#ddd', borderColor: '#666' }}>
          {n}x{n}
        </Button>
      ))}
      <Button size="small" variant="outlined" onClick={onAddHorizontal}
        sx={{ color: '#ddd', borderColor: '#666' }}>+H line</Button>
      <Button size="small" variant="outlined" onClick={onAddVertical}
        sx={{ color: '#ddd', borderColor: '#666' }}>+V line</Button>
      <Button variant="contained" size="small" onClick={onConfirm}
        sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' }, minWidth: 80 }}>
        Confirm
      </Button>
      <Button variant="outlined" size="small" onClick={onCancel}
        sx={{ color: '#fff', borderColor: '#666', '&:hover': { borderColor: '#aaa' }, minWidth: 80 }}>
        Cancel
      </Button>
    </Box>
  );
}

// AWS clients initialized lazily via awsClients.js (supports Identity Pool + static key fallback)
let s3 = null;
let dynamoDB = null;
async function ensureClients() {
  if (!s3) s3 = await getS3Client();
  if (!dynamoDB) dynamoDB = await getDynamoDBClient();
}

const Segmentation = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialPath = params.get('path') || '';
  const multiPaths = params.get('paths') ? params.get('paths').split(',') : [];
  const sessionIdQuery = params.get('sessionId');
  const frameIdxQuery = parseInt(params.get('frameIdx') || '0', 10);
  const analysisId = params.get('analysisId') || '';
  const deviceBucketParam = params.get('deviceBucket') || '';
  const singleImageMode = params.get('mode') === 'single';
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://a0kv32c0yj.execute-api.us-east-1.amazonaws.com/proxy';
  // REACT_APP_GPU_URL (when set) routes GPU traffic to an alternate host such as
  // a RunPod pod proxy URL serving FastAPI at root. cpuUrl is left on the ALB.
  const gpuUrl = process.env.REACT_APP_GPU_URL || (process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/gpu` : backendUrl);
  const cpuUrl = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/cpu` : backendUrl;
  // REACT_APP_LOCATE_URL (mira04 dev only) points at the LocateAnything grounding
  // service. When unset (prod/stable-08), the Assistant UI is hidden entirely.
  const locateUrl = process.env.REACT_APP_LOCATE_URL || null;
  // REACT_APP_AGENT_URL (mira04 dev only) points at the Nemotron chat-agent service.
  // When unset, the Lab Assistant chat is hidden (stable/08 unaffected).
  const agentUrl = process.env.REACT_APP_AGENT_URL || null;
  const { enqueueSnackbar } = useSnackbar();

  const [path] = useState(initialPath);

  // Save / Restore states
  const [analysisRestoreMode] = useState(!!analysisId);

  // Session lifecycle (replaces inline EC2 management code)
  const {
    settingUpResources,
    serverReady,
    sessionProgress,
    sessionError,
    isEnding: isEndingSession,
    checkSessionReadiness,
    handleEndSession: endSession,
    backendUrl: sessionBackendUrl,
    resolvedSessionId,
  } = useEC2Session({
    initialPath,
    sessionIdQuery,
    analysisRestoreMode,
    backendUrl: gpuUrl,
    singleImageMode,
  });
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveAnalysisName, setSaveAnalysisName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [restoreData, setRestoreData] = useState(null); // holds S3 file listing for restore mode
  // For mode='colony_count' analyses, the saved phenotypic JSON is loaded
  // once here so ColonySummaryPanel can render the HSV/Color/Area scatters
  // without a live backend session. Null until the file is fetched.
  const [restoredColonies, setRestoredColonies] = useState(null);
  // Object metadata (user-given names, custom colors, species labels) saved
  // alongside an analysis at Analysis/{sid}/object_meta.json. null means
  // not loaded yet; {} means loaded but empty (legacy analysis pre-dating
  // the meta sidecar). Restore + compare views fall back to "Object N"
  // when a row is missing.
  const [restoredObjectMeta, setRestoredObjectMeta] = useState(null);
  // Saved per-object RLE masks for single-image restore mode (rle_data/
  // frame_000000.json contents). Loaded once per restore and reused by the
  // new SingleImageRestoreVizPanel so the image overlay can paint masks
  // without re-fetching for every tab switch.
  const [restoredSingleFrameRle, setRestoredSingleFrameRle] = useState(null);
  const [restoreSelectedFile, setRestoreSelectedFile] = useState(null); // currently selected file for preview
  const [restoreFileContent, setRestoreFileContent] = useState(null); // loaded content for preview
  // {backendFrameIdx -> elapsedSeconds} for a restored analysis, loaded from the
  // persisted frame_timestamps.json so charts can put real time on the x-axis
  // (and break across the merged-experiment outage). null until loaded/absent.
  const [restoreFrameTimestamps, setRestoreFrameTimestamps] = useState(null);
  // {backendFrameIdx -> absolute UTC ISO} companion to the above, for exports.
  const [restoreFrameTimeIso, setRestoreFrameTimeIso] = useState(null);
  const [restoreCategory, setRestoreCategory] = useState('experiment'); // active category: experiment, growth, color, texture
  const [computingCategory, setComputingCategory] = useState(null); // key of category being recomputed
  const [restorePigTab, setRestorePigTab] = useState(0); // pigmentation chart tab index (0=Hue, 1=Sat, 2=Val)
  const [restoreMaskFrame, setRestoreMaskFrame] = useState(0); // mask viewer frame slider index
  const maskCanvasRef = useRef(null); // canvas ref for mask viewer
  const [maskCanvasMounted, setMaskCanvasMounted] = useState(false); // track canvas mount for drawing
  const [maskedVideoState, setMaskedVideoState] = useState({ status: 'idle' }); // idle | checking | generating | downloading | ready
  const [maskedVideoUrl, setMaskedVideoUrl] = useState(null); // blob URL for inline playback
  const [restoreFrameImages, setRestoreFrameImages] = useState([]); // original frame image URLs for mask overlay
  const [experimentTempData, setExperimentTempData] = useState(null); // parsed temperature CSV rows
  const [experimentParams, setExperimentParams] = useState(null); // Incubator.json parsed object
  const [experimentName, setExperimentName] = useState(''); // experiment name extracted from source_path
  const [experimentTempGraph, setExperimentTempGraph] = useState('Temperature'); // toggle: Temperature or Humidity
  const [restoreGrowthView, setRestoreGrowthView] = useState('area'); // 'area' | 'rate' | 'appearance'
  const experimentChartRef = useRef(null); // ref for experiment chart reset zoom
  const growthChartRef = useRef(null); // ref for growth chart reset zoom
  const colorChartRef = useRef(null); // ref for color chart reset zoom
  const textureChartRef = useRef(null); // ref for texture chart reset zoom
  const [colorDownloadAnchor, setColorDownloadAnchor] = useState(null); // anchor element for color download menu

  const [taskProgress, setTaskProgress] = useState({ stage: '', progress: 0, message: '' });
  const [propagationProgress, setPropagationProgress] = useState({ inProgress: false, stage: '', progress: 0, message: '' });

  const [autoSegProgress, setAutoSegProgress] = useState({ inProgress: false, progress: 0, message: '' });

  // Session timer state (replaces DOM manipulation)
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(null); // ms remaining, null = hidden
  const [sessionExpired, setSessionExpired] = useState(false);



  const [currentObjectId, setCurrentObjectId] = useState(1);
  const [mode, setMode] = useState(1);

  const [propagatedMasks, setPropagatedMasks] = useState({}); // Store masks for all frames
  const [showTimelineWithMasks, setShowTimelineWithMasks] = useState(false); // Toggle between canvas and timeline view
  // Post-propagation per-frame correction. null = not editing; otherwise the
  // chosen mode dictates what "Apply" does:
  //   'retrack' — call /gpu/add_prompt then /gpu/propagate_in_video with frame_idx=current
  //               (SAM2 re-propagates the correction forward/backward through the window).
  //   'single'  — call /gpu/edit_frame_mask to overwrite *only* this frame's RLE in S3,
  //               no re-propagation. Faster, doesn't ripple to neighbors.
  const [editFrameMode, setEditFrameMode] = useState(null);
  const [applyingCorrection, setApplyingCorrection] = useState(false);
  const [editToolbarCollapsed, setEditToolbarCollapsed] = useState(false);
  // Snapshot of objectMasksRef + promptMapRef + objectMetaRef at the moment the user
  // entered edit mode, so Cancel can revert. Only captures the current frame's state.
  const editFrameSnapshotRef = useRef(null);
  const [showMasks, setShowMasks] = useState(true); // Toggle mask overlay visibility
  const [drawingMasks, setDrawingMasks] = useState(false); // Track mask drawing progress
  const [showGrowthChart, setShowGrowthChart] = useState(false); // Show growth analysis chart
  const [growthData, setGrowthData] = useState({ csvUrl: null, timeInterval: 30 }); // Growth analysis data
  const [showRugosityChart, setShowRugosityChart] = useState(false); // Show rugosity analysis chart
  const [showPigmentationChart, setShowPigmentationChart] = useState(false); // Show pigmentation analysis chart
  const [showHSVVisualization, setShowHSVVisualization] = useState(false);
  const [restorePigmentationData, setRestorePigmentationData] = useState(null);
  const [showSpiralChart, setShowSpiralChart] = useState(false);
  const [showSpiralConfigDialog, setShowSpiralConfigDialog] = useState(false);
  // Colony Count unified flow: picks which alignment overlay + detection pipeline to use.
  // null = no alignment in progress; 'spiral' / '90mm' / 'rectangular' = active alignment mode.
  const [countMode, setCountMode] = useState(null);
  const [showCountModeDialog, setShowCountModeDialog] = useState(false);
  // null = backend default (currently two_stage); otherwise one of
  // colony_pipeline.PIPELINES on the GPU service. Sent as `pipeline` on every
  // /automask and /automask_grid call so users can A/B without a redeploy.
  const [colonyPipeline, setColonyPipeline] = useState(null);
  // Morphological pipeline: opens a sliders+preview dialog after dish-shape
  // selection. Holds the (mode, roi-or-cells) so the dialog's Run handler can
  // dispatch the right automask vs automask_grid call.
  const [morphDialogState, setMorphDialogState] = useState(null);
  // Find-All-Similar pipeline: opens an exemplar-picker dialog after dish
  // selection. Same intercept pattern as morphDialogState.
  const [findSimilarDialogState, setFindSimilarDialogState] = useState(null);
  const spiralAlignMode = countMode === 'spiral' || countMode === '90mm';
  const rectAlignMode = countMode === 'rectangular';
  const [spiralCircle, setSpiralCircle] = useState({ cx: 0, cy: 0, radius: 0 });
  const spiralDragRef = useRef({ active: false, mode: null, startX: 0, startY: 0, origCx: 0, origCy: 0, origR: 0 });
  // Rectangular alignment state
  const [rectROI, setRectROI] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [rectGrid, setRectGrid] = useState({
    rows: 4,
    cols: 4,
    horizontals: [], // y coordinates of interior horizontal lines (image space)
    verticals: [],   // x coordinates of interior vertical lines (image space)
  });
  const rectDragRef = useRef({ active: false, mode: null, lineIndex: null, startX: 0, startY: 0, orig: null });
  const [spiralPlateConfig, setSpiralPlateConfig] = useState({
    num_sectors: 6,
    num_quadrants: 4,
    total_volume_uL: 50,
    plate_diameter_mm: 90,
    sensitivity: 'medium',
    center_x: null,
    center_y: null,
    radius: null,
  });
  const [bgAnalysisTasks, setBgAnalysisTasks] = useState({ growth: null, pigmentation: null, rugosity: null, spiral: null }); // Background analysis task IDs
  const [precomputeTaskId, setPrecomputeTaskId] = useState(null); // task ID for background precompute job
  const [precomputeDone, setPrecomputeDone] = useState(false); // whether precompute has completed
  const frameCanvasRef = useRef(null);
  const {
    frameCount,
    fps,
    frameTimestamps,
    currentIndex,
    currentBitmap,
    frameSize,
    isPlaying,
    framesReady,
    framesError,
    goToFrame,
    togglePlay,
    toBackendIndex,
    toDisplayIndex,
    currentFrameUrl,
  } = useFrameManager({
    gpuUrl,
    sessionId: resolvedSessionId,
    serverReady,
  });
  const [frameStride, setFrameStride] = useState(10);
  // Inclusive frame window for propagation. null == "use full range";
  // resolved at submit time against frameCount so the user can leave defaults.
  const [propagateStart, setPropagateStart] = useState(null);
  const [propagateEnd, setPropagateEnd] = useState(null);
  // ROI state (in original image coordinates)
  const [roi, setRoi] = useState(null); // { x, y, w, h } | null
  const [roiMode, setRoiMode] = useState(false); // when true, dragging defines ROI
  const [roiDraft, setRoiDraft] = useState(null); // transient during drag
  // Dish selection for Auto Segment
  const DISH_OBJ_ID = 9999;
  const [dishSelectMode, setDishSelectMode] = useState(false); // waiting for user to click dish
  const [dishMaskData, setDishMaskData] = useState(null); // decoded mask from dish click
  const shouldSubmitRef = useRef(false);   // set true only when user adds a point
  const lastPayloadRef = useRef('');      // stringify payload to avoid repeats

  // ===== Heavy maps as refs (no O(n) cloning on each update) =====
  const objectMetaRef = useRef({ 1: { name: 'Object 1' } });
  const promptMapRef = useRef({ 1: { points: [], labels: [] } });
  const objectMasksRef = useRef(Object.create(null)); // { [objId]: decodedMask }
  const [roiShape, setRoiShape] = useState('rect'); // 'rect' | 'circle' | 'both'

  // ===== Shape drawing tool =====
  const [drawingMode, setDrawingMode] = useState('sam'); // 'sam' | 'rect' | 'ellipse'
  const [shapeDraft, setShapeDraft] = useState(null); // {x, y, w, h} in canvas coords
  const shapeDrag = useRef({ active: false, startX: 0, startY: 0 });
  // {[objId]: { kind: 'rect'|'ellipse', ... }} — kept across re-renders so
  // /freeze_propagation can be triggered without recomputing geometry, and
  // the editing UI can render movable handles for the existing shape.
  const shapeGeomRef = useRef({});
  // Active shape edit (move / resize). When set, the next mouseup commits
  // the new geometry and rebuilds the rasterised mask.
  const shapeEditRef = useRef({ active: false, mode: null, handle: null, origGeom: null, startX: 0, startY: 0 });
  const SHAPE_HANDLE_SIZE = 8; // px on canvas — tolerance for handle hits




  // Lightweight identity/control state
  const [objectIds, setObjectIds] = useState([1]);
  const nextObjectIdRef = useRef(2);
  // Which object the user is currently hovering in the ObjectManager panel.
  // Held in a ref (not state) because canvas redraw reads it inside a draw
  // loop — a ref keeps every row hover from triggering React reconciliation;
  // hoveredTick bumps a counter so the draw effect re-runs on hover changes.
  const hoveredObjectIdRef = useRef(null);
  const [hoveredTick, setHoveredTick] = useState(0);
  const setHoveredObjectId = useCallback((objId) => {
    const next = objId == null ? null : parseInt(objId, 10);
    if (hoveredObjectIdRef.current === next) return;
    hoveredObjectIdRef.current = next;
    setHoveredTick(t => t + 1);
  }, []);


  // One small “UI tick” to re-render when refs change
  const [uiTick, setUiTick] = useState(0);
  const bumpUi = useCallback(() => setUiTick(t => t + 1), []);

  // --- Lab Assistant (Nemotron chat agent) state ---
  const agentMessagesRef = useRef([]);            // full OpenAI convo incl tool turns
  const [agentMessages, setAgentMessagesState] = useState([]);
  const [agentBusy, setAgentBusy] = useState(false);
  // Reserved chat pod is started manually for testing/demo. Probe its status so
  // the panel shows "Chat Servers Unavailable" when it is off. The analysis runs
  // independently via miravision-gpu, so this never blocks counting/propagation.
  const [agentAvailable, setAgentAvailable] = useState(false);
  const experimentContextRef = useRef(null);
  const [experimentContextName, setExperimentContextName] = useState(null);
  const setAgentConvo = useCallback((c) => { agentMessagesRef.current = c; setAgentMessagesState(c); }, []);

  useEffect(() => {
    if (!agentUrl) return undefined;
    let cancelled = false;
    const probe = async () => {
      try {
        const r = await fetch(`${agentUrl}/status`);
        const d = await r.json();
        if (!cancelled) setAgentAvailable(!!d?.available);
      } catch {
        if (!cancelled) setAgentAvailable(false);
      }
    };
    probe();
    const t = setInterval(probe, 30000); // pick up a manual start/stop within 30s
    return () => { cancelled = true; clearInterval(t); };
  }, [agentUrl]);

  // Snapshot views (read-only) for render/children
  const objectMeta = useMemo(() => objectMetaRef.current, [uiTick]);
  const promptMap = useMemo(() => promptMapRef.current, [uiTick]);
  const objectMasks = useMemo(() => objectMasksRef.current, [uiTick]);
  // NEW: caches for super-fast drawing
  const maskBitmapCacheRef = useRef(new WeakMap()); // key: maskData.mask.buffer -> { bitmap, width, height, meta, colorKey }
  // Raster cache (decoded maskData -> Map<stateKey, { offscreen, geom }>).
  // Skips the downsample + edge-detect + putImageData pipeline on repeat draws
  // of the same mask at the same display scale. Cleared on canvas resize.
  const maskRasterCacheRef = useRef(new WeakMap());
  // Pending requestAnimationFrame id used to coalesce overlay redraws so that
  // multiple bumpUi() calls inside a single tick collapse to one draw.
  const overlayRafIdRef = useRef(null);
  const maskWorkerRef = useRef(null);

  // init worker once
  useEffect(() => {
    maskWorkerRef.current = new Worker(new URL('../workers/mask-worker.js', import.meta.url), { type: 'module' });
    return () => { maskWorkerRef.current?.terminate(); };
  }, []);

  const colorForId = (id) => {
    const [r, g, b, a] = generateObjectColor(id, 0.85); // your existing fn (0..1 alpha)
    return [r, g, b, Math.round(a * 255)];
  };

  // Build (or get) ImageBitmap for a mask ONCE
  const getOrBuildMaskBitmap = (objId, maskData) => new Promise((resolve) => {
    const key = maskData?.mask?.buffer;
    if (!key) return resolve(null);

    const cached = maskBitmapCacheRef.current.get(key);
    const colorKey = colorForId(objId).join(',');
    if (cached && cached.colorKey === colorKey) return resolve(cached);

    const postBuild = (record) => {
      maskBitmapCacheRef.current.set(key, record);
      resolve(record);
    };

    const tryCreateBitmapFromPixels = async (pixels, w, h, meta) => {
      try {
        // Create ImageBitmap from pixels on main thread (fallback)
        const cnv = document.createElement('canvas');
        cnv.width = w; cnv.height = h;
        const ctx = cnv.getContext('2d');
        const img = new ImageData(new Uint8ClampedArray(pixels), w, h);
        ctx.putImageData(img, 0, 0);
        const bitmap = await createImageBitmap(cnv);
        postBuild({ bitmap, width: w, height: h, meta, colorKey });
      } catch {
        postBuild(null);
      }
    };

    // ask worker
    const id = `${objId}-${crypto.getRandomValues(new Uint32Array(1))[0]}`;
    const onMessage = (e) => {
      const { id: backId, meta, bitmap, pixels, width, height } = e.data || {};
      if (backId !== id) return;
      maskWorkerRef.current?.removeEventListener('message', onMessage);

      if (bitmap) {
        postBuild({ bitmap, width: imageCacheRef.current?.width || width, height: imageCacheRef.current?.height || height, meta, colorKey });
      } else if (pixels) {
        tryCreateBitmapFromPixels(pixels, width, height, meta);
      } else {
        postBuild(null);
      }
    };
    maskWorkerRef.current?.addEventListener('message', onMessage);

    maskWorkerRef.current?.postMessage({
      id,
      maskBuffer: maskData.mask.buffer,
      width: maskData.width,
      height: maskData.height,
      color: colorForId(objId),
    }, [maskData.mask.buffer]);
  });


  const canvasRef = useRef(null);
  const imageCacheRef = useRef(null);

  const { images, loading: imagesLoading } = useS3Images(process.env.REACT_APP_USER_BUCKET, path);
  const [localImages, setLocalImages] = useState([]);
  // currentIndex + goToFrame are provided by useFrameManager (replaces local state)
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);

  const setObjectMeta = useCallback((updater) => {
    const prev = objectMetaRef.current;
    const next = typeof updater === 'function' ? updater({ ...prev }) : updater;
    objectMetaRef.current = next;
    bumpUi();
  }, [bumpUi]);

  const updateObjectMeta = useCallback((id, patch) => {
    objectMetaRef.current[id] = { ...(objectMetaRef.current[id] || {}), ...patch };
    bumpUi();
  }, [bumpUi]);

  const setPromptMapCompat = useCallback((updater) => {
    const prev = promptMapRef.current;
    const next = typeof updater === 'function' ? updater({ ...prev }) : updater;
    promptMapRef.current = next;
    bumpUi();
  }, [bumpUi]);

  const setObjectMasksCompat = useCallback((nextOrMutator) => {
    const prev = objectMasksRef.current;

    if (typeof nextOrMutator === 'function') {
      const maybeNext = nextOrMutator(prev);
      if (maybeNext && maybeNext !== prev) objectMasksRef.current = maybeNext;
    } else {
      if (nextOrMutator && nextOrMutator !== prev) objectMasksRef.current = nextOrMutator;
    }

    bumpUi();
  }, [bumpUi]);



  // Function to generate colors for multiple objects (up to 25+ objects)
  const generateObjectColor = useCallback((objectId, alpha = 0.85) => {
    if (typeof objectId !== 'number' || objectId < 0) {
      console.warn('Invalid objectId:', objectId);
      return [255, 255, 255, alpha]; // fallback to white
    }

    // Golden ratio for hue spread
    const goldenRatio = 0.6180339887;
    const hue = (objectId * goldenRatio * 360) % 360;  // 0-360 degrees
    const saturation = 65;  // % saturation
    const lightness = 55;   // % lightness

    // Convert HSL to RGB
    const h = hue / 360;
    const s = saturation / 100;
    const l = lightness / 100;

    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [
      Math.round(r * 255),
      Math.round(g * 255),
      Math.round(b * 255),
      alpha // transparency
    ];
  }, []);

  // ── Session countdown timer (React state-driven) ──
  useEffect(() => {
    if (analysisRestoreMode) return; // no timer in restore mode

    const SESSION_DURATION = 2 * 60 * 60 * 1000 + 10 * 60 * 1000; // 2h 10m

    // Get or set session start time in sessionStorage
    let sessionStart = sessionStorage.getItem('sessionStart');
    if (!sessionStart) {
      sessionStart = Date.now();
      sessionStorage.setItem('sessionStart', String(sessionStart));
    } else {
      sessionStart = parseInt(sessionStart, 10);
    }

    function tick() {
      const remaining = SESSION_DURATION - (Date.now() - sessionStart);
      if (remaining <= 0) {
        setSessionTimeRemaining(0);
        setSessionExpired(true);
        return false; // signal to stop interval
      }
      setSessionTimeRemaining(remaining);
      return true; // keep ticking
    }

    // First tick after 1-minute delay (matches original behaviour)
    const delayId = setTimeout(() => {
      if (!tick()) return;
      // Then tick every minute
      const intervalId = setInterval(() => {
        if (!tick()) clearInterval(intervalId);
      }, 60 * 1000);
      // store intervalId so cleanup can clear it
      timerIntervalRef.current = intervalId;
    }, 60 * 1000);

    return () => {
      clearTimeout(delayId);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [analysisRestoreMode]);

  // Show expiration notification once
  useEffect(() => {
    if (sessionExpired) {
      enqueueSnackbar('Your analysis time has ended.', { variant: 'warning', persist: true });
    }
  }, [sessionExpired, enqueueSnackbar]);

  // ref to hold interval ID for cleanup
  const timerIntervalRef = useRef(null);

  // Spawn a fresh object id and switch focus to it. Centralised here so the
  // "+" hotkey, the ObjectManager "+" button, and any future entry point all
  // go through one place — keeps objectMetaRef, objectIds state, and
  // nextObjectIdRef in sync.
  const handleNewObject = useCallback(() => {
    // Self-correct: auto-detect paths populate objectMetaRef / objectMasksRef
    // up to id=N but historically didn't always bump nextObjectIdRef. If we
    // trust nextObjectIdRef blindly we can hand out a colliding id, which
    // makes "+" silently overwrite an existing colony's metadata and makes
    // the subsequent click look like it "didn't add anything". Recompute the
    // ceiling from the actual registries every time.
    const existingMetaIds = Object.keys(objectMetaRef.current || {}).map(Number).filter(Number.isFinite);
    const existingMaskIds = Object.keys(objectMasksRef.current || {}).map(Number).filter(Number.isFinite);
    const ceiling = Math.max(0, ...existingMetaIds, ...existingMaskIds);
    const newId = Math.max(nextObjectIdRef.current, ceiling + 1);
    nextObjectIdRef.current = newId + 1;
    objectMetaRef.current[newId] = { name: `Object ${newId}` };
    promptMapRef.current[newId] = { points: [], labels: [] };
    // do NOT touch objectMasks until you actually have a mask
    setObjectIds(ids => ids.includes(newId) ? ids : [...ids, newId]);
    setCurrentObjectId(newId);
    bumpUi();
  }, [bumpUi]);

  // Single-image colony-count save state. The handler that owns the actual
  // save flow lives below `encodeCOCORLE` (TDZ on the dep would crash render).
  const [isSavingColonyCount, setIsSavingColonyCount] = useState(false);
  const handleSaveColonyCountRef = useRef(null);
  const handleSaveColonyCount = useCallback((args) => handleSaveColonyCountRef.current?.(args), []);

  // ── Keyboard shortcuts (single unified handler) ──
  useEffect(() => {
    const onKey = (e) => {
      // Skip when user is typing in any input/contenteditable; otherwise
      // every form on the page swallows the shortcut.
      const tgt = e.target;
      const isTyping = tgt && (
        tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' ||
        tgt.isContentEditable
      );
      if (isTyping) return;
      const key = e.key.toLowerCase();
      if (key === 'r') setRoiMode(v => !v);
      if (key === 'c') setRoiShape(s => (s === 'rect' ? 'circle' : s === 'circle' ? 'both' : 'rect'));
      // "+" creates a new tracking object. Accept both Shift+= (US layout) and
      // numpad "+" (event.key === '+'); ignore when ctrl/cmd modifiers are
      // held so browser shortcuts (Ctrl++ zoom) still work.
      if ((e.key === '+' || (e.key === '=' && e.shiftKey)) && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        handleNewObject();
      }
      if (e.key === 'Escape') { setRoiDraft(null); setRoiMode(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleNewObject]);

  // Session restore and boot sequence are now handled by the useEC2Session hook.
  // The hook checks analysisRestoreMode, existing sessionStorage, and handles
  // health checks + session creation automatically.

  // ─── Restore mode: load analysis metadata + list S3 files ───
  useEffect(() => {
    if (!analysisRestoreMode || !analysisId || !deviceBucketParam) return;

    const loadRestoreData = async () => {
      await ensureClients();
      try {
        // 1. Look up the DynamoDB entry to get session_id and s3_location
        const dbResult = await dynamoDB.get({
          TableName: 'Analysis_folders',
          Key: { device_bucket: deviceBucketParam, analysis_id: analysisId }
        }).promise();

        const item = dbResult.Item;
        if (!item) {
          enqueueSnackbar('Analysis not found.', { variant: 'error' });
          return;
        }

        const sessionId = item.session_id || '';
        // Ensure s3Prefix ends with /
        let s3Prefix = item.s3_location || `${deviceBucketParam}/Analysis/${sessionId}/`;
        if (!s3Prefix.endsWith('/')) s3Prefix += '/';
        const bucket = item.s3_bucket || process.env.REACT_APP_USER_BUCKET;

        // 2. List ALL objects under the S3 prefix (handle pagination)
        let allContents = [];
        let continuationToken = null;
        do {
          const params = {
            Bucket: bucket,
            Prefix: s3Prefix,
            ...(continuationToken ? { ContinuationToken: continuationToken } : {})
          };
          const listResult = await s3.listObjectsV2(params).promise();
          allContents = allContents.concat(listResult.Contents || []);
          continuationToken = listResult.IsTruncated ? listResult.NextContinuationToken : null;
        } while (continuationToken);

        const files = allContents.map(obj => ({
          key: obj.Key,
          // relative name from prefix root
          name: obj.Key.substring(s3Prefix.length),
          size: obj.Size,
          lastModified: obj.LastModified
        })).filter(f => f.name && f.size > 0); // filter out prefix markers and empty objects

        // 3. Build a tree structure for folder navigation
        const folderTree = {};
        files.forEach(f => {
          const parts = f.name.split('/');
          if (parts.length > 1) {
            const folder = parts[0];
            if (!folderTree[folder]) folderTree[folder] = [];
            folderTree[folder].push({
              ...f,
              fileName: parts.slice(1).join('/')
            });
          } else {
            // root-level file
            if (!folderTree['']) folderTree[''] = [];
            folderTree[''].push({
              ...f,
              fileName: f.name
            });
          }
        });

        setRestoreData({
          analysisName: item.analysis_name || analysisId,
          sessionId,
          s3Prefix,
          bucket,
          files,
          folderTree,
          metadata: item
        });
      } catch (err) {
        console.error('Failed to load restore data:', err);
        enqueueSnackbar('Failed to load saved analysis data.', { variant: 'error' });
      }
    };

    loadRestoreData();
  }, [analysisRestoreMode, analysisId, deviceBucketParam]);

  // For mode='colony_count' analyses, preload the phenotypic JSON so the
  // ColonySummaryPanel can render without a live session. Skipped silently
  // for any other mode (Growth/Color/Texture tabs handle their own fetches).
  useEffect(() => {
    if (!restoreData?.metadata) return;
    if (restoreData.metadata.mode !== 'colony_count') return;
    const { bucket, s3Prefix } = restoreData;
    if (!bucket || !s3Prefix) return;
    let cancelled = false;
    (async () => {
      try {
        await ensureClients();
        const obj = await s3.getObject({
          Bucket: bucket,
          Key: `${s3Prefix}phenotypic/results.json`,
        }).promise();
        if (cancelled) return;
        const body = JSON.parse(
          typeof obj.Body === 'string' ? obj.Body : obj.Body.toString('utf8'),
        );
        setRestoredColonies(Array.isArray(body?.colonies) ? body.colonies : []);
      } catch (e) {
        if (!cancelled) {
          console.warn('No saved phenotypic data for colony_count restore:', e?.code || e?.message);
          setRestoredColonies([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [restoreData]);

  // Load the object_meta.json sidecar (user-given names + custom colors +
  // species labels) so restore/compare views can render real names instead
  // of "Object N" / "Region N". Runs for ALL restore modes, not just
  // colony_count, since timelapse beta analyses also carry per-object
  // names worth surfacing. Missing/legacy: set to {} so consumers stop
  // showing spinners.
  useEffect(() => {
    if (!restoreData) return undefined;
    const { bucket, s3Prefix } = restoreData;
    if (!bucket || !s3Prefix) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await ensureClients();
        const obj = await s3.getObject({
          Bucket: bucket,
          Key: `${s3Prefix}object_meta.json`,
        }).promise();
        if (cancelled) return;
        const body = JSON.parse(
          typeof obj.Body === 'string' ? obj.Body : obj.Body.toString('utf8'),
        );
        setRestoredObjectMeta(body && typeof body === 'object' ? body : {});
      } catch (e) {
        if (!cancelled) {
          // NoSuchKey is expected for analyses saved before the sidecar
          // landed. Log at debug volume only.
          if (e?.code !== 'NoSuchKey') {
            console.warn('object_meta.json load failed:', e?.code || e?.message);
          }
          setRestoredObjectMeta({});
        }
      }
    })();
    return () => { cancelled = true; };
  }, [restoreData]);

  // Load the persisted frame_idx -> timestamp mapping for a restored analysis.
  // Prefer the save-snapshot copy ({s3Prefix}frame_timestamps.json); fall back
  // to the session-level copy (Analysis/{sessionId}/frame_timestamps.json) for
  // analyses saved before the sidecar was written into the snapshot. Absent for
  // legacy analyses -> stays null and charts fall back to "Frame N" labels.
  useEffect(() => {
    if (!restoreData) return undefined;
    const { bucket, s3Prefix, sessionId } = restoreData;
    if (!bucket || !s3Prefix) return undefined;
    let cancelled = false;
    setRestoreFrameTimestamps(null);
    setRestoreFrameTimeIso(null);
    (async () => {
      await ensureClients();
      const candidates = [`${s3Prefix}frame_timestamps.json`];
      if (sessionId) candidates.push(`Analysis/${sessionId}/frame_timestamps.json`);
      for (const Key of candidates) {
        try {
          const obj = await s3.getObject({ Bucket: bucket, Key }).promise();
          if (cancelled) return;
          const body = JSON.parse(
            typeof obj.Body === 'string' ? obj.Body : obj.Body.toString('utf8'),
          );
          const ts = body && typeof body === 'object' ? body.frame_timestamps : null;
          if (ts && typeof ts === 'object' && Object.keys(ts).length > 0) {
            setRestoreFrameTimestamps(ts);
            if (body.frame_timestamps_iso && typeof body.frame_timestamps_iso === 'object') {
              setRestoreFrameTimeIso(body.frame_timestamps_iso);
            }
            return;
          }
        } catch (e) {
          if (e?.code && e.code !== 'NoSuchKey') {
            console.warn('frame_timestamps.json load failed:', e.code);
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [restoreData]);

  // Shared inference for the single-image colony-count restore branch. The
  // URL ?mode=single param isn't reliable (older share/restore links omit
  // it), so fall back to metadata signals: source_path is an image file,
  // or frame_count <= 1.
  const isSingleImageColonyRestoreActive = useMemo(() => {
    if (!restoreData?.metadata) return false;
    if (restoreData.metadata.mode !== 'colony_count') return false;
    if (singleImageMode) return true;
    const sp = (restoreData.metadata.source_path || '').toLowerCase();
    const fc = Number(restoreData.metadata.frame_count);
    return /\.(jpg|jpeg|png|webp)$/.test(sp) || (Number.isFinite(fc) && fc <= 1);
  }, [restoreData, singleImageMode]);

  // When the categories list changes (e.g. switching into single-image
  // colony-count restore which hides Experiment), the previously-chosen
  // restoreCategory may no longer exist. Snap to the first available so the
  // preview pane isn't stuck on an absent tab.
  useEffect(() => {
    if (!isSingleImageColonyRestoreActive) return;
    const allowed = ['growth', 'color'];
    if (!allowed.includes(restoreCategory)) {
      setRestoreCategory('growth');
    }
  }, [isSingleImageColonyRestoreActive, restoreCategory]);

  // Single-image colony-count restore also needs the saved RLE masks so the
  // viz panel can overlay them on the image. Fetch rle_data/frame_000000.json
  // once per restore.
  useEffect(() => {
    if (!isSingleImageColonyRestoreActive) return;
    const { bucket, s3Prefix } = restoreData || {};
    if (!bucket || !s3Prefix) return;
    let cancelled = false;
    (async () => {
      try {
        await ensureClients();
        const obj = await s3.getObject({
          Bucket: bucket,
          Key: `${s3Prefix}rle_data/frame_000000.json`,
        }).promise();
        if (cancelled) return;
        const body = JSON.parse(
          typeof obj.Body === 'string' ? obj.Body : obj.Body.toString('utf8'),
        );
        setRestoredSingleFrameRle(body);
      } catch (e) {
        if (!cancelled) {
          console.warn('No saved rle_data for single-image colony_count restore:', e?.code || e?.message);
          setRestoredSingleFrameRle(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isSingleImageColonyRestoreActive, restoreData]);

  // Auto-load the default category when restore data first loads
  useEffect(() => {
    if (!restoreData || restoreSelectedFile) return;
    // Experiment category needs no file loading
    if (restoreCategory === 'experiment') {
      setRestoreSelectedFile({ _virtual: true, ext: 'experiment' });
      return;
    }
    const CATEGORY_MAP = {
      growth: { folder: 'growth', ext: 'json', extFallback: 'csv' },
      color: { folder: 'pigmentation', ext: 'json' },
      texture: { folder: 'rugosity', ext: 'json' },
      spiral: { folder: 'spiral', ext: 'json' },
    };
    const cat = CATEGORY_MAP[restoreCategory];
    if (!cat) return;
    const folderFiles = restoreData.folderTree[cat.folder] || [];
    let file = folderFiles.find(f => (f.fileName || f.name || '').toLowerCase().endsWith(`.${cat.ext}`));
    if (!file && cat.extFallback) {
      file = folderFiles.find(f => (f.fileName || f.name || '').toLowerCase().endsWith(`.${cat.extFallback}`));
    }
    if (!file && cat.folderFallback) {
      const fallbackFiles = restoreData.folderTree[cat.folderFallback] || [];
      file = fallbackFiles.find(f => (f.fileName || f.name || '').toLowerCase().endsWith(`.${cat.ext}`));
    }
    if (!file) return;
    const ext = (file.fileName || file.name || '').split('.').pop().toLowerCase();
    const signParams = {
      Bucket: restoreData.bucket,
      Key: file.key,
      Expires: 3600
    };
    const presignedUrl = s3.getSignedUrl('getObject', signParams);
    setRestoreSelectedFile({ ...file, url: presignedUrl, ext });
    if (ext === 'csv' || ext === 'json') {
      fetch(presignedUrl).then(r => r.text()).then(text => setRestoreFileContent(text)).catch(console.error);
    }
  }, [restoreData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load original frame images from S3 using source_path for mask overlay background
  useEffect(() => {
    if (!restoreData?.metadata?.source_path) return;
    const sourcePath = restoreData.metadata.source_path;
    const bucket = restoreData.bucket || process.env.REACT_APP_USER_BUCKET;
    let cancelled = false;

    const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.bmp', '.tiff', '.tif', '.webp'];

    const loadFrameImages = async () => {
      await ensureClients();
      try {
        // Short-circuit single-image saves: source_path IS the image file.
        // The listObjectsV2 path expects a folder prefix and its depth filter
        // would drop the file we already know about.
        const lowerSrc = (sourcePath || '').toLowerCase();
        if (IMAGE_EXTS.some((ext) => lowerSrc.endsWith(ext))) {
          const url = s3.getSignedUrl('getObject', {
            Bucket: bucket,
            Key: sourcePath,
            Expires: 3600,
          });
          if (!cancelled) setRestoreFrameImages([url]);
          return;
        }
        // Paginate to get all objects
        let allContents = [];
        let continuationToken = null;
        do {
          const params = {
            Bucket: bucket,
            Prefix: sourcePath,
            ...(continuationToken ? { ContinuationToken: continuationToken } : {})
          };
          const listResult = await s3.listObjectsV2(params).promise();
          allContents = allContents.concat(listResult.Contents || []);
          continuationToken = listResult.IsTruncated ? listResult.NextContinuationToken : null;
        } while (continuationToken);

        if (cancelled || !allContents.length) return;

        // Filter to only direct image children and sort by key
        const prefixDepth = sourcePath.replace(/\/$/, '').split('/').length;
        const imageFiles = allContents
          .filter(obj => {
            const key = obj.Key || obj.key || '';
            if (key.endsWith('/') || obj.Size === 0) return false;
            const keyParts = key.split('/');
            // Direct children only (one level deeper than prefix)
            if (keyParts.length !== prefixDepth + 1) return false;
            // Filter to image extensions only (exclude movie.mp4 etc.)
            const lower = key.toLowerCase();
            return IMAGE_EXTS.some(ext => lower.endsWith(ext));
          })
          .sort((a, b) => (a.Key || '').localeCompare(b.Key || ''));

        // Generate presigned URLs for each frame
        const urls = imageFiles.map(obj => s3.getSignedUrl('getObject', {
          Bucket: bucket,
          Key: obj.Key,
          Expires: 3600
        }));

        if (!cancelled) setRestoreFrameImages(urls);
      } catch (err) {
        console.error('Failed to load original frame images:', err);
      }
    };

    loadFrameImages();
    return () => { cancelled = true; };
  }, [restoreData]);

  // Load experiment data (temperature CSV + Incubator.json) from S3
  useEffect(() => {
    if (!restoreData?.metadata?.source_path) return;
    const sourcePath = restoreData.metadata.source_path;
    const bucket = restoreData.bucket || process.env.REACT_APP_USER_BUCKET;

    // source_path = "<device_bucket>/Experiments/<experiment_name>/<sample>/"
    // We need "<device_bucket>/Experiments/<experiment_name>/"
    const parts = sourcePath.replace(/\/$/, '').split('/');
    // parts: [device_bucket, "Experiments", experiment_name, sample]
    if (parts.length < 3) return;
    const expName = parts[2]; // experiment_name
    const expPrefix = parts.slice(0, 3).join('/'); // device_bucket/Experiments/experiment_name
    setExperimentName(expName);

    let cancelled = false;

    const loadExperimentData = async () => {
      await ensureClients();
      // Load temperature CSV
      try {
        const tempKey = `${expPrefix}/temperature_data.csv`;
        const tempResult = await s3.getObject({ Bucket: bucket, Key: tempKey }).promise();
        if (!cancelled) {
          const csvText = tempResult.Body.toString('utf-8');
          const lines = csvText.trim().split('\n');
          if (lines.length > 1) {
            const headers = lines[0].split(',').map(h => h.trim());
            const rows = [];
            for (let i = 1; i < lines.length; i++) {
              const cols = lines[i].split(',').map(c => c.trim());
              const row = {};
              headers.forEach((h, idx) => { row[h] = cols[idx] || ''; });
              rows.push(row);
            }
            setExperimentTempData(rows);
          }
        }
      } catch (err) {
        if (err.code !== 'NoSuchKey') console.error('Failed to load temperature CSV:', err);
      }

      // Load Incubator.json
      try {
        const paramsKey = `${expPrefix}/Incubator.json`;
        const paramsResult = await s3.getObject({ Bucket: bucket, Key: paramsKey }).promise();
        if (!cancelled) {
          const jsonData = JSON.parse(paramsResult.Body.toString('utf-8'));
          setExperimentParams(jsonData);
        }
      } catch (err) {
        if (err.code !== 'NoSuchKey') console.error('Failed to load Incubator.json:', err);
      }
    };

    loadExperimentData();
    return () => { cancelled = true; };
  }, [restoreData]);

  // Decode RLE masks (base64 + zlib compressed, Uint32 value/count pairs, row-major)
  const decodeRLE = useCallback((rle, shape) => {
    try {
      // Handle 2D or 3D shape arrays
      let height, width;
      if (Array.isArray(shape) && shape.length === 3) {
        height = shape[1];
        width = shape[2];
      } else if (Array.isArray(shape) && shape.length === 2) {
        height = shape[0];
        width = shape[1];
      } else {
        console.error('Invalid shape format:', shape);
        return null;
      }

      // Decode base64
      const binaryString = atob(rle);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);

      // Decompress with pako
      const decompressed = pako.inflate(bytes);

      // Convert to Uint32Array - RLE is stored as (value, count) pairs
      const rleData = new Uint32Array(decompressed.buffer);

      const totalPixels = height * width;
      const mask = new Uint8Array(totalPixels);
      let pos = 0;

      for (let i = 0; i < rleData.length; i += 2) {
        const val = rleData[i];
        const count = rleData[i + 1];
        if (pos + count > totalPixels) break;
        for (let j = 0; j < count; j++) {
          mask[pos + j] = val;
        }
        pos += count;
      }
      return { mask, height, width };
    } catch (err) {
      console.error('RLE decode error:', err);
      return null;
    }
  }, []);

  // decodeCOCORLE is imported from ../utils/rle. It memoizes results by the
  // rle object's identity, so revisiting a frame on scrub is O(1).

  // Encode a row-major Uint8Array mask to COCO RLE {counts, size} (column-major order)
  const encodeCOCORLE = useCallback((mask, width, height) => {
    // Convert row-major to column-major
    const colMajor = new Uint8Array(width * height);
    for (let col = 0; col < width; col++) {
      for (let row = 0; row < height; row++) {
        colMajor[col * height + row] = mask[row * width + col];
      }
    }
    // Build run-length counts (alternating bg, fg, bg, fg, ...)
    const counts = [];
    let run = 0;
    let currentVal = 0; // start with background
    for (let i = 0; i < colMajor.length; i++) {
      const val = colMajor[i] > 0 ? 1 : 0;
      if (val === currentVal) {
        run++;
      } else {
        counts.push(run);
        run = 1;
        currentVal = val;
      }
    }
    counts.push(run);
    return { counts, size: [height, width] };
  }, []);

  // ── Single-image colony-count save flow ─────────────────────────────────
  // Different from the multi-image "Save Analysis": there's no propagated
  // mask data on S3 yet, no time series to persist, and no precompute task
  // running. This handler does it all client-side:
  //   1. RLE-encode every in-memory object mask, upload to
  //      Analysis/{session_id}/rle_data/frame_000000.json so the same path
  //      every other flow assumes exists is populated.
  //   2. Call /cpu/phenotypic/measure with the same inline masks to capture
  //      the colony-level measurements (area, color, etc).
  //   3. Upload that response to Analysis/{session_id}/phenotypic/results.json
  //      so a future restore view can render the panel without re-hitting GPU.
  //   4. Write an Analysis_folders row with mode='colony_count' so the file
  //      explorer lists it and a restore handler can distinguish it from a
  //      timelapse beta analysis.
  useEffect(() => {
    handleSaveColonyCountRef.current = async ({ rows: phenoRows } = {}) => {
      const name = (window.prompt('Save colony-count analysis as:', '')) || '';
      const trimmed = name.trim();
      if (!trimmed) return;

      const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
      if (!sessionId) {
        enqueueSnackbar('No active session.', { variant: 'error' });
        return;
      }
      const pathParts = (initialPath || '').split('/');
      // Prefer the path's own device prefix over sessionStorage. sessionStorage
      // is tab-wide and stays set from earlier sessions; if the user had a
      // different device's session open before navigating to this single-image
      // view, the stale value would mis-index the analysis under the wrong
      // device and hide it from the Analysis folder listing. The path string
      // identifies exactly which device this file belongs to.
      const devBucket =
        deviceBucketParam ||
        pathParts[0] ||
        sessionStorage.getItem('device_bucket') ||
        '';
      if (!devBucket) {
        enqueueSnackbar('Cannot determine device.', { variant: 'error' });
        return;
      }

      setIsSavingColonyCount(true);
      try {
        await Promise.race([
          ensureClients(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('AWS client init timed out')), 15000)),
        ]);

        const objects = [];
        for (const [oid, m] of Object.entries(objectMasksRef.current || {})) {
          if (String(oid) === String(DISH_OBJ_ID)) continue;
          if (!m || !m.mask || !m.width || !m.height) continue;
          try {
            const rle = encodeCOCORLE(m.mask, m.width, m.height);
            objects.push({ obj_id: parseInt(oid, 10), rle });
          } catch { /* skip bad masks */ }
        }
        if (objects.length === 0) {
          enqueueSnackbar('No colonies to save.', { variant: 'warning' });
          setIsSavingColonyCount(false);
          return;
        }

        const bucket = process.env.REACT_APP_USER_BUCKET;
        // Each colony-count save gets its own snapshot prefix so two saves
        // inside one session do not overwrite each other. Mirrors the
        // timelapse snapshot scheme.
        const analysisIdSlug = String(trimmed)
          .trim()
          .replace(/\s+/g, '_')
          .replace(/[^A-Za-z0-9._-]/g, '') || `save_${Date.now()}`;
        const savePrefix = `Analysis/${sessionId}/saves/${analysisIdSlug}/`;
        const rleKey = `${savePrefix}rle_data/frame_000000.json`;
        const phenoKey = `${savePrefix}phenotypic/results.json`;

        await s3.putObject({
          Bucket: bucket, Key: rleKey,
          Body: JSON.stringify({ frame_idx: 0, objects }),
          ContentType: 'application/json',
        }).promise();

        // Re-measure now to lock the exact set the user is saving (panel data
        // could be stale if they edited since the last fetch).
        let colonies = [];
        try {
          const r = await fetch(`${cpuUrl}/phenotypic/measure`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_id: sessionId,
              frame_idx: 0,
              s3_path: initialPath || '',
              masks: objects,
            }),
          });
          if (r.ok) {
            const d = await r.json();
            colonies = Array.isArray(d.colonies) ? d.colonies : [];
          }
        } catch (_) { /* non-fatal */ }
        const payload = colonies.length > 0
          ? { session_id: sessionId, frame_idx: 0, colonies }
          : { session_id: sessionId, frame_idx: 0, colonies: phenoRows || [] };
        await s3.putObject({
          Bucket: bucket, Key: phenoKey,
          Body: JSON.stringify(payload),
          ContentType: 'application/json',
        }).promise();

        const now = new Date().toISOString();
        const storedDevice = JSON.parse(localStorage.getItem('selectedDevice') || '{}');
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const groupId = userData['custom:bucket']
          ? `${userData['custom:bucket']}_users`
          : storedDevice.MIRA_group_id || '';
        await dynamoDB.put({
          TableName: 'Analysis_folders',
          Item: {
            device_bucket: devBucket,
            analysis_id: trimmed,
            MIRA_group_id: groupId,
            device_name: storedDevice.device_name || sessionStorage.getItem('device_name') || '',
            analysis_name: trimmed,
            s3_location: savePrefix,
            s3_bucket: bucket,
            source_path: initialPath || '',
            created_at: now,
            updated_at: now,
            frame_count: 1,
            measurement_count: objects.length,
            object_count: objects.length,
            thumbnail_key: '',
            status: 'active',
            mode: 'colony_count',
            session_id: sessionId,
          },
        }).promise();

        // Persist per-object metadata (user-given names + custom colors +
        // species labels) so restore/compare can render real names instead
        // of "Object N". Non-fatal: if it fails the analysis is still saved.
        try {
          const liveMeta = objectMetaRef.current || {};
          const objectMetaSerializable = Object.fromEntries(
            objects.map((o) => {
              const v = liveMeta[o.obj_id] || liveMeta[String(o.obj_id)] || {};
              return [
                String(o.obj_id),
                {
                  name: v.name || `Object ${o.obj_id}`,
                  customColor: v.customColor || null,
                  species_top1: v.species_top1 || null,
                  species_top1_score: typeof v.species_top1_score === 'number' ? v.species_top1_score : null,
                  species_scores: v.species_scores || null,
                  species_top1_v2: v.species_top1_v2 || null,
                  species_top1_score_v2: typeof v.species_top1_score_v2 === 'number' ? v.species_top1_score_v2 : null,
                  species_scores_v2: v.species_scores_v2 || null,
                  pipeline: v.pipeline || null,
                },
              ];
            }),
          );
          await s3.putObject({
            Bucket: bucket,
            Key: `${savePrefix}object_meta.json`,
            Body: JSON.stringify(objectMetaSerializable),
            ContentType: 'application/json',
          }).promise();
        } catch (metaErr) {
          console.error('Failed to write object_meta.json (analysis still saved):', metaErr);
        }

        enqueueSnackbar(`Saved "${trimmed}" with ${objects.length} colonies.`, { variant: 'success' });
      } catch (e) {
        console.error('Save colony count failed:', e);
        enqueueSnackbar(`Save failed: ${e.message}`, { variant: 'error' });
      } finally {
        setIsSavingColonyCount(false);
      }
    };
  }, [sessionIdQuery, initialPath, deviceBucketParam, cpuUrl, encodeCOCORLE, enqueueSnackbar]);

  // Register a user-drawn mask with SAM2 backend for tracking
  const registerMaskWithBackend = useCallback(async (objId, mask, width, height) => {
    const sid = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (!sid || !serverReady) return;
    const rle = encodeCOCORLE(mask, width, height);
    try {
      const res = await fetch(`${gpuUrl}/add_mask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sid,
          frame_idx: toBackendIndex(currentIndex),
          obj_id: objId,
          mask_rle: rle,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('add_mask failed:', err);
      }
    } catch (e) {
      console.error('Failed to register mask with backend:', e);
    }
  }, [sessionIdQuery, serverReady, gpuUrl, currentIndex, encodeCOCORLE]);

  // Draw mask overlay on canvas when frame slider changes or canvas mounts
  const drawRestoreMasks = useCallback(() => {
    if (!maskCanvasRef.current || restoreCategory !== 'objects' || !restoreFileContent) return;
    let parsedJson;
    try { parsedJson = JSON.parse(restoreFileContent); } catch { return; }
    if (!parsedJson?.frames) return;

    const frames = parsedJson.frames;
    const frameKeys = Object.keys(frames).map(Number).sort((a,b) => a-b);
    const currentFrameKey = String(frameKeys[restoreMaskFrame] ?? frameKeys[0]);
    const frameData = frames[currentFrameKey] || {};
    const objectIds = Object.keys(frameData).sort((a,b) => parseInt(a)-parseInt(b));

    // Get dimensions from first object's shape
    let imgW = 800, imgH = 600;
    if (objectIds.length > 0 && frameData[objectIds[0]]?.shape) {
      const s = frameData[objectIds[0]].shape;
      if (s.length === 3) { imgH = s[1]; imgW = s[2]; }
      else if (s.length === 2) { imgH = s[0]; imgW = s[1]; }
    }

    const canvas = maskCanvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    canvas.width = imgW;
    canvas.height = imgH;

    // Helper: draw mask overlays on whatever is currently on the canvas
    const drawMaskOverlays = () => {
      objectIds.forEach((objId) => {
        const objData = frameData[objId];
        if (!objData) return;
        let decoded = null;

        // COCO RLE format: {rle: {counts: [...], size: [H, W]}} or flagged with _cocoRLE
        if (objData._cocoRLE || (objData.rle && objData.rle.counts && objData.rle.size)) {
          decoded = decodeCOCORLE(objData.rle);
        } else if (objData.rle && typeof objData.rle === 'string') {
          // Legacy base64+zlib compressed format
          decoded = decodeRLE(objData.rle, objData.shape);
        }
        if (!decoded) return;

        const [r, g, b] = generateObjectColor(parseInt(objId));
        const imgData = ctx.getImageData(0, 0, imgW, imgH);
        const pixels = imgData.data;
        const alpha = 0.45;
        for (let i = 0; i < decoded.mask.length; i++) {
          if (decoded.mask[i] > 0) {
            const px = i * 4;
            pixels[px]     = Math.min(255, pixels[px]     * (1 - alpha) + r * alpha);
            pixels[px + 1] = Math.min(255, pixels[px + 1] * (1 - alpha) + g * alpha);
            pixels[px + 2] = Math.min(255, pixels[px + 2] * (1 - alpha) + b * alpha);
            pixels[px + 3] = 255;
          }
        }
        ctx.putImageData(imgData, 0, 0);
      });
    };

    // Try to load the original frame image as background.
    // restoreFrameImages is a flat array of presigned URLs sorted by S3 key.
    // Frame keys from masks may use stride (0, 10, 20...) but images are indexed 0, 1, 2...
    // Map frame key to sequential image index: find position of currentFrameKey in sorted frame keys
    // and use that as the image index (frame 0 = image 0, frame 10 = image 10, etc.)
    // The source images are ALL images in the experiment folder, so frame key IS the image index.
    const frameImageUrl = restoreFrameImages[parseInt(currentFrameKey)];
    if (frameImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, imgW, imgH);
        drawMaskOverlays();
      };
      img.onerror = () => {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, imgW, imgH);
        drawMaskOverlays();
      };
      img.src = frameImageUrl;
    } else {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, imgW, imgH);
      drawMaskOverlays();
    }
  }, [restoreMaskFrame, restoreCategory, restoreFileContent, restoreFrameImages, decodeRLE, generateObjectColor]);

  useEffect(() => {
    drawRestoreMasks();
  }, [drawRestoreMasks, maskCanvasMounted]);

  // Session lifecycle is now managed by useEC2Session hook.
  // Inline functions (checkHealth, createSessionAndInit, pollSessionProgress,
  // waitForInferenceReady, checkExistingSessionStatusSimple) and the boot
  // sequence + timeout useEffects have been removed.

  // Frame info + frame URLs are now handled by useFrameManager hook.
  // Navigate to the initial frame from URL params once frames are available.
  useEffect(() => {
    if (frameCount > 0 && frameIdxQuery > 0) {
      goToFrame(toDisplayIndex(frameIdxQuery));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameCount]);

  // After propagation, the scrubber should only let the user navigate through
  // frames that actually have masks (propagation stride + [start,end] window
  // mean intermediate frames have no overlay). Compute the sorted list of
  // *display* indices (newest-first space) that have masks, plus a Set for O(1)
  // membership tests used by the snap-to-nearest effect below.
  // Auto-expand the per-frame edit toolbar whenever the user scrubs to a new
  // frame or finishes an edit (editFrameMode resets to null). Without this,
  // a "Hide" click during one edit would carry over to subsequent frames and
  // make the controls look like they've disappeared.
  useEffect(() => {
    setEditToolbarCollapsed(false);
  }, [currentIndex, editFrameMode]);

  const maskedDisplayIndices = useMemo(() => {
    const keys = Object.keys(propagatedMasks || {});
    if (keys.length === 0) return [];
    const arr = keys
      .map(k => toDisplayIndex(parseInt(k, 10)))
      .filter(i => Number.isFinite(i) && i >= 0)
      .sort((a, b) => a - b);
    return arr;
  }, [propagatedMasks, toDisplayIndex]);

  const maskedDisplaySet = useMemo(
    () => new Set(maskedDisplayIndices),
    [maskedDisplayIndices],
  );

  // Snap currentIndex to the nearest masked frame whenever it drifts onto a
  // frame without a mask (covers Play, keyboard, and any path that bypasses
  // the scrubber's onGoToFrame wrapper).
  useEffect(() => {
    if (!showTimelineWithMasks) return;
    if (maskedDisplayIndices.length === 0) return;
    if (maskedDisplaySet.has(currentIndex)) return;
    let nearest = maskedDisplayIndices[0];
    let best = Math.abs(currentIndex - nearest);
    for (const i of maskedDisplayIndices) {
      const d = Math.abs(currentIndex - i);
      if (d < best) { best = d; nearest = i; }
    }
    goToFrame(nearest);
  }, [showTimelineWithMasks, currentIndex, maskedDisplayIndices, maskedDisplaySet, goToFrame]);

  // Default the propagation window to the full stack once frames are loaded.
  // Frames are stored newest-first, so by request: start defaults to the
  // numerically-last frame (oldest in time) and end to frame 0 (newest).
  useEffect(() => {
    if (frameCount > 0) {
      if (propagateStart === null) setPropagateStart(frameCount - 1);
      if (propagateEnd === null) setPropagateEnd(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameCount]);

  // Sync imageCacheRef dimensions from frame size for ROI coordinate clamping
  useEffect(() => {
    if (frameSize.width && frameSize.height) {
      imageCacheRef.current = { width: frameSize.width, height: frameSize.height };
      const canvas = canvasRef.current;
      if (canvas && canvas.width !== frameSize.width) {
        canvas.width = frameSize.width;
        canvas.height = frameSize.height;
        // Raster cache is keyed by display width; resizing invalidates every
        // cached offscreen so we rebuild on the next draw.
        maskRasterCacheRef.current = new WeakMap();
      }
    }
  }, [frameSize]);

  useEffect(() => {
    if (!imagesLoading) setLocalImages(images);
  }, [images, imagesLoading]);

  // Handle frame switching — decode propagated masks on-demand for the current frame.
  // propagatedMasks stores raw RLE data; we decode only the visible frame to save memory.
  useEffect(() => {
    const frameRLE = propagatedMasks[toBackendIndex(currentIndex)];
    if (frameRLE) {
      const decoded = {};
      Object.entries(frameRLE).forEach(([objId, rle]) => {
        if (rle?.counts && rle?.size) {
          const d = decodeCOCORLE(rle);
          if (d) decoded[objId] = d;
        }
      });
      setObjectMasksCompat(decoded);
    } else if (Object.keys(propagatedMasks).length > 0) {
      // Only clear if we have propagation data but not for this frame
      setObjectMasksCompat({});
    }
  }, [currentIndex, propagatedMasks]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.width) return;

    const ctx = canvas.getContext('2d');

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // masks from ref (video is the background — canvas is transparent overlay)
      if (showMasks) {
        const maskEntries = Object.entries(objectMasksRef.current);
        const hoveredId = hoveredObjectIdRef.current;
        const hasHover = hoveredId != null;
        // Non-selected, non-hovered masks first; dim them when hover is active.
        maskEntries.forEach(([objId, maskData]) => {
          const idNum = parseInt(objId);
          if (!maskData) return;
          if (idNum === currentObjectId) return;
          if (idNum === hoveredId) return;
          drawMaskOnCanvas(canvas, maskData, idNum, { isDimmed: hasHover });
        });
        const selectedMask = objectMasksRef.current[String(currentObjectId)];
        if (selectedMask && currentObjectId !== hoveredId) {
          drawMaskOnCanvas(canvas, selectedMask, currentObjectId, {
            isSelected: true,
            isDimmed: hasHover,
          });
        }
        // Hovered mask drawn LAST so its brighter stroke + fill sits on top.
        if (hasHover) {
          const hoveredMask = objectMasksRef.current[String(hoveredId)];
          if (hoveredMask) {
            drawMaskOnCanvas(canvas, hoveredMask, hoveredId, {
              isHovered: true,
              isSelected: hoveredId === currentObjectId,
            });
          }
        }
      }

      // prompt points from ref
      Object.entries(promptMapRef.current).forEach(([id, v]) => {
        const points = v?.points || [];
        const labels = v?.labels || [];
        points.forEach(([x, y], i) => {
          ctx.beginPath();
          ctx.arc(x, y, 6, 0, 2 * Math.PI);
          ctx.fillStyle = labels[i] === 1 ? 'lime' : 'red';
          ctx.fill();
          ctx.strokeStyle = id === `${currentObjectId}` ? 'blue' : 'gray';
          ctx.lineWidth = 2;
          ctx.stroke();
        });
      });

      // ROI overlay
      if (roi || roiDraft) {
        const rect = roiDraft || roi;
        if (rect && rect.w > 0 && rect.h > 0) {
          ctx.save();

          const scaleX = canvas.width / (imageCacheRef.current?.width || canvas.width);
          const scaleY = canvas.height / (imageCacheRef.current?.height || canvas.height);

          const x = rect.x * scaleX, y = rect.y * scaleY;
          const w = rect.w * scaleX, h = rect.h * scaleY;

          const fill = 'rgba(0, 153, 255, 0.15)';
          const stroke = 'rgba(0, 153, 255, 0.95)';
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 4]);

          if (roiShape === 'rect' || roiShape === 'both') {
            ctx.fillStyle = 'rgba(0, 153, 255, 0.10)';
            ctx.fillRect(x, y, w, h);
            ctx.strokeStyle = stroke;
            ctx.strokeRect(x, y, w, h);
          }

          if (roiShape === 'circle' || roiShape === 'both') {
            const cx = x + w / 2;
            const cy = y + h / 2;
            const r = 0.5 * Math.min(w, h);
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.fillStyle = fill;
            ctx.fill();
            ctx.strokeStyle = stroke;
            ctx.stroke();
          }

          ctx.setLineDash([]);

          const labelRect = `${Math.round(rect.w)}x${Math.round(rect.h)}`;
          const labelCircle = `d ${Math.round(Math.min(rect.w, rect.h))}`;
          const label = roiShape === 'rect' ? labelRect
            : roiShape === 'circle' ? labelCircle
              : `${labelRect} | ${labelCircle}`;

          ctx.font = 'bold 14px Inter, Arial';
          ctx.textBaseline = 'top';
          const pad = 6, tw = ctx.measureText(label).width;
          ctx.fillStyle = stroke;
          ctx.fillRect(x, y - 20, tw + pad * 2, 20);
          ctx.fillStyle = '#fff';
          ctx.fillText(label, x + pad, y - 18);

          ctx.restore();
        }
      }

      // Committed shape + edit handles (rendered when no transient draft exists,
      // so the user sees a stable selection box around the existing rect/ellipse
      // and can grab any of the 8 handles to resize or the body to move it).
      const committedGeom = shapeGeomRef.current[currentObjectId];
      if (
        drawingMode !== 'sam'
        && committedGeom
        && (!shapeDraft || (shapeDraft.w === 0 && shapeDraft.h === 0))
      ) {
        ctx.save();
        const color = generateObjectColor(currentObjectId);
        const [r, g, b] = color;
        const stroke = `rgba(${r}, ${g}, ${b}, 0.95)`;
        const fill = `rgba(${r}, ${g}, ${b}, 0.18)`;
        const b0 = geomToBBox(committedGeom);
        ctx.lineWidth = 2;
        if (committedGeom.kind === 'rect') {
          ctx.fillStyle = fill;
          ctx.fillRect(b0.x0, b0.y0, b0.x1 - b0.x0, b0.y1 - b0.y0);
          ctx.strokeStyle = stroke;
          ctx.strokeRect(b0.x0, b0.y0, b0.x1 - b0.x0, b0.y1 - b0.y0);
        } else {
          const cx = (b0.x0 + b0.x1) / 2, cy = (b0.y0 + b0.y1) / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, (b0.x1 - b0.x0) / 2, (b0.y1 - b0.y0) / 2, 0, 0, 2 * Math.PI);
          ctx.fillStyle = fill; ctx.fill();
          ctx.strokeStyle = stroke; ctx.stroke();
        }
        // Bbox guideline (helps users see the editable region for ellipses)
        if (committedGeom.kind === 'ellipse') {
          ctx.setLineDash([4, 4]);
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.4)`;
          ctx.lineWidth = 1;
          ctx.strokeRect(b0.x0, b0.y0, b0.x1 - b0.x0, b0.y1 - b0.y0);
          ctx.setLineDash([]);
        }
        // 8 resize handles
        const hs = SHAPE_HANDLE_SIZE;
        const handles = [
          [b0.x0, b0.y0], [b0.x1, b0.y0], [b0.x0, b0.y1], [b0.x1, b0.y1],
          [(b0.x0 + b0.x1) / 2, b0.y0], [(b0.x0 + b0.x1) / 2, b0.y1],
          [b0.x0, (b0.y0 + b0.y1) / 2], [b0.x1, (b0.y0 + b0.y1) / 2],
        ];
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 1.5;
        for (const [hx, hy] of handles) {
          ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
          ctx.strokeRect(hx - hs / 2, hy - hs / 2, hs, hs);
        }
        ctx.restore();
      }

      // Shape drawing preview
      if (shapeDraft && shapeDraft.w > 0 && shapeDraft.h > 0 && drawingMode !== 'sam') {
        ctx.save();
        const color = generateObjectColor(currentObjectId);
        const [r, g, b] = color;
        const fill = `rgba(${r}, ${g}, ${b}, 0.15)`;
        const stroke = `rgba(${r}, ${g}, ${b}, 0.9)`;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);

        const { x, y, w, h } = shapeDraft;

        if (drawingMode === 'rect') {
          ctx.fillStyle = fill;
          ctx.fillRect(x, y, w, h);
          ctx.strokeStyle = stroke;
          ctx.strokeRect(x, y, w, h);
        } else if (drawingMode === 'ellipse') {
          const cx = x + w / 2;
          const cy = y + h / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, 2 * Math.PI);
          ctx.fillStyle = fill;
          ctx.fill();
          ctx.strokeStyle = stroke;
          ctx.stroke();
        }

        // Size label
        const label = drawingMode === 'rect'
          ? `${Math.round(w)}x${Math.round(h)}`
          : `${Math.round(w / 2)}x${Math.round(h / 2)} (radii)`;
        ctx.font = 'bold 12px Inter, Arial';
        ctx.textBaseline = 'top';
        const pad = 4, tw = ctx.measureText(label).width;
        ctx.fillStyle = stroke;
        ctx.fillRect(x, y - 18, tw + pad * 2, 18);
        ctx.fillStyle = '#fff';
        ctx.fillText(label, x + pad, y - 16);

        ctx.setLineDash([]);
        ctx.restore();
      }
    };

    // Coalesce: multiple state changes inside one tick collapse to one paint.
    if (overlayRafIdRef.current != null) cancelAnimationFrame(overlayRafIdRef.current);
    overlayRafIdRef.current = requestAnimationFrame(() => {
      overlayRafIdRef.current = null;
      draw();
    });
    return () => {
      if (overlayRafIdRef.current != null) {
        cancelAnimationFrame(overlayRafIdRef.current);
        overlayRafIdRef.current = null;
      }
    };
  }, [currentObjectId, roi, roiDraft, uiTick, roiShape, showMasks, shapeDraft, drawingMode, generateObjectColor, hoveredTick]);


  // ── Shape geometry helpers (rect / ellipse) ────────────────────────────────
  // All work in image-pixel coordinates. The canvas event handlers receive
  // coords already mapped into image space via FrameCanvas.
  const geomToBBox = useCallback((g) => {
    if (!g) return null;
    if (g.kind === 'rect') return { x0: g.x, y0: g.y, x1: g.x + g.w, y1: g.y + g.h };
    return { x0: g.cx - g.rx, y0: g.cy - g.ry, x1: g.cx + g.rx, y1: g.cy + g.ry };
  }, []);
  const bboxToGeom = useCallback((bbox, kind) => {
    const x0 = Math.min(bbox.x0, bbox.x1);
    const x1 = Math.max(bbox.x0, bbox.x1);
    const y0 = Math.min(bbox.y0, bbox.y1);
    const y1 = Math.max(bbox.y0, bbox.y1);
    if (kind === 'rect') return { kind: 'rect', x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    return { kind: 'ellipse', cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, rx: (x1 - x0) / 2, ry: (y1 - y0) / 2 };
  }, []);
  const hitTestShape = useCallback((x, y, geom) => {
    if (!geom) return null;
    const b = geomToBBox(geom);
    const handles = [
      { name: 'nw', hx: b.x0, hy: b.y0 },
      { name: 'ne', hx: b.x1, hy: b.y0 },
      { name: 'sw', hx: b.x0, hy: b.y1 },
      { name: 'se', hx: b.x1, hy: b.y1 },
      { name: 'n',  hx: (b.x0 + b.x1) / 2, hy: b.y0 },
      { name: 's',  hx: (b.x0 + b.x1) / 2, hy: b.y1 },
      { name: 'w',  hx: b.x0, hy: (b.y0 + b.y1) / 2 },
      { name: 'e',  hx: b.x1, hy: (b.y0 + b.y1) / 2 },
    ];
    const tol = SHAPE_HANDLE_SIZE;
    for (const h of handles) {
      if (Math.abs(x - h.hx) <= tol && Math.abs(y - h.hy) <= tol) {
        return { type: 'handle', name: h.name };
      }
    }
    if (x < b.x0 || x > b.x1 || y < b.y0 || y > b.y1) return null;
    if (geom.kind === 'rect') return { type: 'body' };
    const dx = (x - geom.cx) / geom.rx;
    const dy = (y - geom.cy) / geom.ry;
    if (dx * dx + dy * dy <= 1) return { type: 'body' };
    return null;
  }, [geomToBBox]);
  const rasteriseShape = useCallback((geom, imgW, imgH) => {
    const mask = new Uint8Array(imgW * imgH);
    if (!geom) return mask;
    if (geom.kind === 'rect') {
      const x0 = Math.max(0, Math.round(geom.x));
      const y0 = Math.max(0, Math.round(geom.y));
      const x1 = Math.min(imgW, Math.round(geom.x + geom.w));
      const y1 = Math.min(imgH, Math.round(geom.y + geom.h));
      for (let row = y0; row < y1; row++) {
        for (let col = x0; col < x1; col++) mask[row * imgW + col] = 1;
      }
    } else {
      const { cx, cy, rx, ry } = geom;
      const x0 = Math.max(0, Math.floor(cx - rx));
      const y0 = Math.max(0, Math.floor(cy - ry));
      const x1 = Math.min(imgW, Math.ceil(cx + rx) + 1);
      const y1 = Math.min(imgH, Math.ceil(cy + ry) + 1);
      for (let row = y0; row < y1; row++) {
        for (let col = x0; col < x1; col++) {
          const dx = (col - cx) / rx;
          const dy = (row - cy) / ry;
          if (dx * dx + dy * dy <= 1.0) mask[row * imgW + col] = 1;
        }
      }
    }
    return mask;
  }, []);

  const clampToImage = useCallback((x, y, w, h) => {
    const imgW = imageCacheRef.current?.width || 0;
    const imgH = imageCacheRef.current?.height || 0;
    let cx = Math.max(0, Math.min(x, imgW));
    let cy = Math.max(0, Math.min(y, imgH));
    let cw = Math.max(1, Math.min(w, imgW - cx));
    let ch = Math.max(1, Math.min(h, imgH - cy));
    return { x: cx, y: cy, w: cw, h: ch };
  }, []);

  // LocateAnything "Assistant": send the current frame + keywords to the grounding
  // service, then map the returned boxes onto objects -- existing colonies whose
  // centroid falls in a box get selected; described things not yet detected become
  // new (rectangle-seeded) objects the scientist can refine. Dev/R&D only.
  const handleAssistantFind = useCallback(async (query) => {
    const fail = (msg, variant = 'warning') => {
      enqueueSnackbar(msg, { variant });
      return { matched: 0, created: 0 };
    };
    if (!locateUrl) return fail('Assistant is not configured');
    if (!currentFrameUrl) return fail('Frame not ready yet');
    const imgW = imageCacheRef.current?.width || 0;
    const imgH = imageCacheRef.current?.height || 0;
    if (!imgW || !imgH) return fail('Frame not ready yet');

    let boxes;
    let Wi = imgW;
    let Hi = imgH;
    try {
      const res = await fetch(`${locateUrl}/locate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: currentFrameUrl, query }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || 'grounding failed');
      boxes = json.boxes || [];
      if (Array.isArray(json.image_wh) && json.image_wh.length === 2) {
        [Wi, Hi] = json.image_wh;
      }
    } catch (e) {
      return fail(`Assistant error: ${e.message}`, 'error');
    }

    // Scale boxes (original-image px) to working/mask coords, drop lawn-region
    // blobs (>15% of frame) and noise (<25 px^2).
    const sx = imgW / Wi;
    const sy = imgH / Hi;
    const maxArea = 0.15 * imgW * imgH;
    const rects = boxes
      .map((b) => ({ x: b.x1 * sx, y: b.y1 * sy, w: (b.x2 - b.x1) * sx, h: (b.y2 - b.y1) * sy }))
      .filter((r) => { const a = r.w * r.h; return a > 25 && a < maxArea; });

    // Centroids of existing objects (skip the dish).
    const centroids = {};
    for (const [oid, m] of Object.entries(objectMasksRef.current || {})) {
      if (Number(oid) === DISH_OBJ_ID || !m || !m.mask) continue;
      const { mask, width } = m;
      let cxSum = 0; let cySum = 0; let n = 0;
      for (let i = 0; i < mask.length; i++) {
        if (mask[i]) { cxSum += i % width; cySum += (i / width) | 0; n++; }
      }
      if (n) centroids[oid] = { x: cxSum / n, y: cySum / n };
    }

    const matched = new Set();
    const newRects = [];
    for (const r of rects) {
      let hit = false;
      for (const [oid, c] of Object.entries(centroids)) {
        if (c.x >= r.x && c.x <= r.x + r.w && c.y >= r.y && c.y <= r.y + r.h) {
          matched.add(oid); hit = true;
        }
      }
      if (!hit) newRects.push(r);
    }

    // Create new objects (cap 30) seeded as rectangles; user can SAM-refine later.
    const allExisting = Object.keys(objectMetaRef.current || {})
      .concat(Object.keys(objectMasksRef.current || {}))
      .map(Number).filter(Number.isFinite);
    let nextId = Math.max(nextObjectIdRef.current, allExisting.reduce((a, b) => Math.max(a, b), 0) + 1);
    let created = 0;
    for (const r of newRects.slice(0, 30)) {
      const geom = { kind: 'rect', x: r.x, y: r.y, w: r.w, h: r.h };
      objectMasksRef.current[nextId] = { mask: rasteriseShape(geom, imgW, imgH), width: imgW, height: imgH };
      objectMetaRef.current[nextId] = { name: `${query} ${created + 1}` };
      shapeGeomRef.current[nextId] = geom;
      nextId += 1; created += 1;
    }
    nextObjectIdRef.current = nextId;

    const allIds = Array.from(new Set(
      Object.keys(objectMetaRef.current).map(Number).concat(Object.keys(objectMasksRef.current).map(Number)),
    )).filter((id) => Number.isFinite(id) && id !== DISH_OBJ_ID).sort((a, b) => a - b);
    setObjectIds(allIds);
    setObjectMasksCompat({ ...objectMasksRef.current });
    if (matched.size) setCurrentObjectId(Number(Array.from(matched)[0]));
    bumpUi();
    enqueueSnackbar(`Matched ${matched.size}, added ${created} for "${query}"`, { variant: 'success' });
    return { matched: matched.size, created };
  }, [locateUrl, currentFrameUrl, enqueueSnackbar, rasteriseShape, setObjectMasksCompat, bumpUi]);

  const roiDrag = useRef({ active: false, startX: 0, startY: 0 });

  // ── Dish-selection handler: send a point prompt for DISH_OBJ_ID ──
  const handleDishClick = useCallback(async (coords) => {
    if (!serverReady) return;
    const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (!sessionId) return;

    const x = Math.round(coords.x);
    const y = Math.round(coords.y);

    setDishSelectMode(false);
    enqueueSnackbar('Detecting dish boundary...', { variant: 'info', autoHideDuration: 2000 });

    try {
      setIsGlobalLoading(true);
      const payload = {
        sessionId,
        frame_idx: toBackendIndex(currentIndex),
        obj_id: DISH_OBJ_ID,
        points: [[x, y]],
        labels: [1],
      };
      const res = await fetch(`${gpuUrl}/add_prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Dish detection failed');

      let masksPayload = null;
      if (res.status === 202 && json.task_id) {
        await waitForTaskCompletion(gpuUrl, json.task_id, sessionId);
        const resultJson = await fetchTaskResults(gpuUrl, json.task_id, sessionId);
        masksPayload = resultJson?.masks;
      } else if (json?.masks) {
        masksPayload = json.masks;
      }

      if (!masksPayload) throw new Error('No mask returned for dish click');

      // Extract the dish mask (keyed by DISH_OBJ_ID or first available)
      const dishRle = masksPayload[String(DISH_OBJ_ID)] || Object.values(masksPayload)[0];
      if (!dishRle) throw new Error('Empty mask payload');

      let decoded = null;
      if (dishRle?.counts && dishRle?.size) {
        decoded = decodeCOCORLE(dishRle);
      } else if (dishRle?.rle) {
        // Legacy format: rle field can be a base64+zlib string OR a nested COCO RLE dict
        if (typeof dishRle.rle === 'object' && dishRle.rle?.counts) {
          decoded = decodeCOCORLE(dishRle.rle);
        } else {
          decoded = decodeMaskRLE(dishRle.rle, dishRle.shape);
        }
      }

      if (!decoded) throw new Error('Could not decode dish mask');

      // Store in ref for canvas rendering
      objectMasksRef.current[DISH_OBJ_ID] = decoded;
      setDishMaskData(decoded);
      bumpUi();

      enqueueSnackbar('Dish selected. Click "Continue" to auto-detect colonies or click again to reselect.', { variant: 'success', autoHideDuration: 5000 });
    } catch (err) {
      console.error('Dish click error:', err);
      enqueueSnackbar(err.message || 'Failed to detect dish. Please try again.', { variant: 'error' });
      setDishSelectMode(true); // re-enter dish select mode so user can try again
    } finally {
      setIsGlobalLoading(false);
    }
  }, [serverReady, sessionIdQuery, currentIndex, gpuUrl, enqueueSnackbar, bumpUi]);

  // ── FrameCanvas-compatible handlers (receive canvas-space coords directly) ──
  const pickObjectAtCoords = useCallback((x, y) => {
    const masks = objectMasksRef.current || {};
    // Prefer the smallest mask containing the point (topmost in dense layouts).
    let bestId = null;
    let bestArea = Infinity;
    for (const id of Object.keys(masks)) {
      if (String(id) === String(DISH_OBJ_ID)) continue;
      const m = masks[id];
      if (!m || !m.mask || !m.width || !m.height) continue;
      // Translate full-frame x,y into mask-local coords (non-zero offset for
      // bbox-cropped grid masks; zero otherwise).
      const ox = Number.isFinite(m.offsetX) ? m.offsetX : 0;
      const oy = Number.isFinite(m.offsetY) ? m.offsetY : 0;
      const mx = x - ox;
      const my = y - oy;
      if (mx < 0 || my < 0 || mx >= m.width || my >= m.height) continue;
      if (m.mask[my * m.width + mx] !== 1) continue;
      const area = m.mask.length;
      if (area < bestArea) {
        bestArea = area;
        bestId = id;
      }
    }
    return bestId;
  }, []);

  const handleFrameCanvasClick = useCallback((coords, event) => {
    // In shape drawing mode, clicks are handled by mousedown/move/up — ignore here
    if (drawingMode !== 'sam') return;
    if (dishSelectMode) {
      handleDishClick(coords);
      return;
    }
    if (!serverReady) {
      enqueueSnackbar('Please wait for setup to complete before adding points.', { variant: 'info' });
      return;
    }
    const x = Math.round(coords.x);
    const y = Math.round(coords.y);

    // Click-to-select: if the click lands on an existing mask, select it
    // (highlight in ObjectManagerPanel) instead of adding a point prompt.
    // Skip this branch entirely in:
    //   - Exclude mode (clicks inside an existing mask are how SAM2 subtracts)
    //   - When the current object has no mask AND no prompts yet (the user
    //     just hit "+" and is trying to seed a fresh object — stealing the
    //     click to switch to a neighbouring mask is exactly the "+ does
    //     nothing" bug reported in colony-count mode).
    //   - Per-frame edit mode (editFrameMode != null): the user has
    //     explicitly picked the target object in the docked panel, so
    //     auto-switching to whichever mask the click landed on silently
    //     re-routes their edits to a neighbour. This was the bug behind
    //     "the pill says Object X but Object Y's mask updates" — the
    //     first click flipped currentObjectId via this branch, the
    //     second click's point landed on the wrong object.
    const currentHasMask = !!objectMasksRef.current[String(currentObjectId)];
    const currentHasPrompts = (promptMapRef.current[currentObjectId]?.points || []).length > 0;
    const isSeedingFreshObject = !currentHasMask && !currentHasPrompts;
    if (mode !== 0 && !isSeedingFreshObject && !editFrameMode) {
      const hitId = pickObjectAtCoords(x, y);
      if (hitId !== null && hitId !== undefined) {
        const numId = parseInt(hitId, 10);
        if (!Number.isNaN(numId) && numId !== currentObjectId) {
          setCurrentObjectId(numId);
          bumpUi();
          return;
        }
        // Already selected — falls through to add a positive point only if
        // user holds Shift (explicit intent to annotate inside the mask).
        if (!(event && event.shiftKey)) return;
      }
    }

    // Append to the current object's prompt instead of replacing, so users can
    // add multiple include + exclude points to refine a single mask.
    const existing = promptMapRef.current[currentObjectId];
    const newPoints = existing && existing.points ? [...existing.points, [x, y]] : [[x, y]];
    const newLabels = existing && existing.labels ? [...existing.labels, mode] : [mode];
    promptMapRef.current[currentObjectId] = {
      points: newPoints,
      labels: newLabels,
    };
    shouldSubmitRef.current = true;
    lastPayloadRef.current = '';
    bumpUi();
  }, [serverReady, currentObjectId, mode, enqueueSnackbar, dishSelectMode, handleDishClick, drawingMode, pickObjectAtCoords, bumpUi, editFrameMode]);

  const handleFrameCanvasMouseDown = useCallback((coords) => {
    // Shape drawing mode: edit existing shape if click hits it, else start fresh draw
    if (drawingMode !== 'sam' && !roiMode) {
      if (!serverReady || !imageCacheRef.current) return;
      const x = Math.round(coords.x);
      const y = Math.round(coords.y);
      const existing = shapeGeomRef.current[currentObjectId];
      const hit = hitTestShape(x, y, existing);
      if (hit) {
        shapeEditRef.current = {
          active: true,
          mode: hit.type,
          handle: hit.name || null,
          origGeom: { ...existing },
          startX: x,
          startY: y,
        };
        // Seed shapeDraft from existing geom so the canvas renders the
        // shape via the same path while it's being dragged.
        const b = geomToBBox(existing);
        setShapeDraft({ x: b.x0, y: b.y0, w: b.x1 - b.x0, h: b.y1 - b.y0 });
        return;
      }
      shapeDrag.current = { active: true, startX: x, startY: y };
      setShapeDraft({ x, y, w: 0, h: 0 });
      return;
    }
    // ROI mode
    if (!roiMode || !imageCacheRef.current) return;
    const x = Math.round(coords.x);
    const y = Math.round(coords.y);
    roiDrag.current = { active: true, startX: x, startY: y };
    setRoiDraft({ x, y, w: 0, h: 0 });
  }, [roiMode, drawingMode, serverReady, currentObjectId, hitTestShape, geomToBBox]);

  const handleFrameCanvasMouseMove = useCallback((coords) => {
    // Shape edit (move / resize) — takes precedence over fresh draw
    if (drawingMode !== 'sam' && shapeEditRef.current.active) {
      const x = Math.round(coords.x);
      const y = Math.round(coords.y);
      const ed = shapeEditRef.current;
      const orig = geomToBBox(ed.origGeom);
      let next = { ...orig };
      if (ed.mode === 'body') {
        const dx = x - ed.startX;
        const dy = y - ed.startY;
        next = { x0: orig.x0 + dx, y0: orig.y0 + dy, x1: orig.x1 + dx, y1: orig.y1 + dy };
      } else if (ed.mode === 'handle') {
        const h = ed.handle;
        if (h.includes('n')) next.y0 = y;
        if (h.includes('s')) next.y1 = y;
        if (h.includes('w')) next.x0 = x;
        if (h.includes('e')) next.x1 = x;
      }
      setShapeDraft({
        x: Math.min(next.x0, next.x1),
        y: Math.min(next.y0, next.y1),
        w: Math.abs(next.x1 - next.x0),
        h: Math.abs(next.y1 - next.y0),
      });
      return;
    }
    // Shape drawing drag
    if (drawingMode !== 'sam' && shapeDrag.current.active) {
      const x = Math.round(coords.x);
      const y = Math.round(coords.y);
      const sx = shapeDrag.current.startX;
      const sy = shapeDrag.current.startY;
      setShapeDraft({
        x: Math.min(sx, x),
        y: Math.min(sy, y),
        w: Math.abs(x - sx),
        h: Math.abs(y - sy),
      });
      return;
    }
    // ROI drag
    if (!roiMode || !roiDrag.current.active) return;
    const x = Math.round(coords.x);
    const y = Math.round(coords.y);
    const sx = roiDrag.current.startX;
    const sy = roiDrag.current.startY;
    const rect = {
      x: Math.min(sx, x),
      y: Math.min(sy, y),
      w: Math.abs(x - sx),
      h: Math.abs(y - sy),
    };
    setRoiDraft(clampToImage(rect.x, rect.y, rect.w, rect.h));
  }, [roiMode, clampToImage, drawingMode]);

  const handleFrameCanvasMouseUp = useCallback(() => {
    // Shape edit (move / resize): commit new geometry, rebuild mask
    if (drawingMode !== 'sam' && shapeEditRef.current.active) {
      const ed = shapeEditRef.current;
      shapeEditRef.current = { active: false, mode: null, handle: null, origGeom: null, startX: 0, startY: 0 };
      const imgW = imageCacheRef.current?.width || 1024;
      const imgH = imageCacheRef.current?.height || 1024;
      if (!shapeDraft || shapeDraft.w < 3 || shapeDraft.h < 3) {
        // Treat as cancel — restore the original geometry rendering.
        if (ed.origGeom) {
          shapeGeomRef.current[currentObjectId] = ed.origGeom;
        }
        setShapeDraft(null);
        bumpUi();
        return;
      }
      const bbox = {
        x0: shapeDraft.x,
        y0: shapeDraft.y,
        x1: shapeDraft.x + shapeDraft.w,
        y1: shapeDraft.y + shapeDraft.h,
      };
      const kind = ed.origGeom?.kind || drawingMode;
      const newGeom = bboxToGeom(bbox, kind);
      shapeGeomRef.current[currentObjectId] = newGeom;
      const mask = rasteriseShape(newGeom, imgW, imgH);
      objectMasksRef.current[currentObjectId] = { mask, width: imgW, height: imgH };
      setShapeDraft(null);
      bumpUi();
      return;
    }
    // Shape drawing: finalize shape
    if (drawingMode !== 'sam' && shapeDrag.current.active) {
      shapeDrag.current.active = false;
      if (!shapeDraft || shapeDraft.w < 5 || shapeDraft.h < 5) {
        setShapeDraft(null);
        return;
      }

      const imgW = imageCacheRef.current?.width || 1024;
      const imgH = imageCacheRef.current?.height || 1024;
      const { x, y, w, h } = shapeDraft;

      if (drawingMode === 'rect') {
        // Generate raw rectangular mask locally (no SAM2 inference)
        const mask = new Uint8Array(imgW * imgH);
        const x0 = Math.max(0, Math.round(x));
        const y0 = Math.max(0, Math.round(y));
        const x1 = Math.min(imgW, Math.round(x + w));
        const y1 = Math.min(imgH, Math.round(y + h));
        for (let row = y0; row < y1; row++) {
          for (let col = x0; col < x1; col++) {
            mask[row * imgW + col] = 1;
          }
        }

        objectMasksRef.current[currentObjectId] = { mask, width: imgW, height: imgH };
        // Stash the geometry so the user can adjust it with handles, and so
        // triggerPropagation can rebuild the mask without re-rasterising.
        shapeGeomRef.current[currentObjectId] = { kind: 'rect', x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
        setShapeDraft(null);
        bumpUi();
        // Shape modes are static — no SAM2 registration needed; "Track" will
        // call /freeze_propagation to write this mask to every frame.
        enqueueSnackbar(`Rectangle drawn (${x1 - x0}x${y1 - y0} px). Click Track to apply across frames.`, { variant: 'success' });
        return;
      }

      if (drawingMode === 'ellipse') {
        // Generate ellipse mask locally (no SAM2 inference)
        const cx = x + w / 2;
        const cy = y + h / 2;
        const rx = w / 2;
        const ry = h / 2;

        const mask = new Uint8Array(imgW * imgH);
        for (let row = 0; row < imgH; row++) {
          for (let col = 0; col < imgW; col++) {
            const dx = (col - cx) / rx;
            const dy = (row - cy) / ry;
            if (dx * dx + dy * dy <= 1.0) {
              mask[row * imgW + col] = 1;
            }
          }
        }

        objectMasksRef.current[currentObjectId] = { mask, width: imgW, height: imgH };
        shapeGeomRef.current[currentObjectId] = { kind: 'ellipse', cx, cy, rx, ry };
        setShapeDraft(null);
        bumpUi();
        enqueueSnackbar(`Ellipse drawn (${Math.round(rx)}x${Math.round(ry)} px). Click Track to apply across frames.`, { variant: 'success' });
        return;
      }

      setShapeDraft(null);
      return;
    }
    // ROI mode
    if (!roiMode || !roiDrag.current.active) return;
    roiDrag.current.active = false;
    if (roiDraft && roiDraft.w > 3 && roiDraft.h > 3) {
      setRoi(roiDraft);
    }
    setRoiDraft(null);
  }, [roiMode, roiDraft, drawingMode, shapeDraft, currentObjectId, bumpUi, enqueueSnackbar, bboxToGeom, rasteriseShape]);

  // Poll a backend task until completion (resolves to final progress object)
  const waitForTaskCompletion = async (backendUrl, taskId, sessionId, {
    intervalMs = 5000,
    maxMs = 1200000
  } = {}) => {
    const t0 = Date.now();
    // optional: update overlay text while waiting
    setIsGlobalLoading(true);

    while (Date.now() - t0 < maxMs) {
      const resp = await fetch(`${gpuUrl}/task_progress/${taskId}?sessionId=${encodeURIComponent(sessionId)}`);
      if (!resp.ok) {
        throw new Error(`Progress poll failed (${resp.status})`);
      }
      const prog = await resp.json();

      // You already show an overlay; keep it fresh
      setTaskProgress(prev => ({
        ...prev,
        stage: prog.stage || 'processing',
        progress: typeof prog.progress === 'number' ? prog.progress : (prev.progress || 0),
        message: prog.message || prev.message || 'Processing...'
      }));

      if (prog.stage === 'complete') return prog;
      if (prog.stage === 'error' || prog.stage === 'cancelled') {
        throw new Error(prog.message || `Task ${prog.stage}`);
      }
      await new Promise(r => setTimeout(r, intervalMs));
    }
    throw new Error('Task timed out.');
  };

  const fetchTaskResults = async (backendUrl, taskId, sessionId) => {
    const resp = await fetch(`${gpuUrl}/task_results/${taskId}?sessionId=${encodeURIComponent(sessionId)}`);
    if (!resp.ok) {
      // 202 while still running; caller should have waited already
      const text = await resp.text();
      throw new Error(`Failed to get task results: ${resp.status} ${text}`);
    }
    return resp.json();
  };


  const sendPrompt = useMemo(() => debounce(async () => {
    const sid = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (!sid) return;

    if (!serverReady) {
      enqueueSnackbar('Please wait for setup to complete.', { variant: 'info' });
      return;
    }

    // Ensure inference-ready
    try {
      const readinessResponse = await fetch(`${gpuUrl}/check_inference_ready/${sid}?sessionId=${encodeURIComponent(sid)}`);
      const readinessResult = await readinessResponse.json();
      if (!readinessResult.inference_ready) {
        enqueueSnackbar('Still preparing, please wait...', { variant: 'info' });
        return;
      }
    } catch {
      enqueueSnackbar('Unable to verify readiness.', { variant: 'error' });
      return;
    }

    // Build payload from ref — backend expects flat fields per object
    const entries = Object.entries(promptMapRef.current).filter(
      ([, v]) => v?.points?.length
    );
    if (entries.length === 0) return;

    // Use the current object's prompt (last entry)
    const [objId, promptData] = entries[entries.length - 1];
    const payload = {
      sessionId: sid,
      frame_idx: toBackendIndex(currentIndex),
      obj_id: Number(objId),
      points: promptData.points,
      labels: promptData.labels,
    };

    try {
      setIsGlobalLoading(true);
      const startRes = await fetch(`${gpuUrl}/add_prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const startJson = await startRes.json();
      if (!startRes.ok) throw new Error(startJson?.error || `add_prompt failed (${startRes.status})`);

      // Extract masks from response (either async task or direct 200)
      let masksPayload = null;
      if (startRes.status === 202 && startJson.task_id) {
        const taskId = startJson.task_id;
        await waitForTaskCompletion(gpuUrl, taskId, sid);
        const resultJson = await fetchTaskResults(gpuUrl, taskId, sid);
        masksPayload = resultJson?.masks;
      } else if (startRes.status === 200 && startJson?.masks) {
        masksPayload = startJson.masks;
      } else {
        throw new Error('Unexpected add_prompt response shape');
      }

      if (masksPayload) {
        // Backend returns masks keyed by object ID: {"1": {counts, size}, "2": ...}
        // Decode each mask and store under its object ID.
        const isDict = masksPayload && !Array.isArray(masksPayload) && typeof masksPayload === 'object';
        const entries = isDict
          ? Object.entries(masksPayload)
          : [[String(currentObjectId), Array.isArray(masksPayload) ? masksPayload[0] : masksPayload]];

        for (const [objIdStr, rle] of entries) {
          const objId = parseInt(objIdStr, 10);
          if (rle?.counts && rle?.size) {
            const decoded = decodeCOCORLE(rle);
            if (decoded) {
              objectMasksRef.current[objId] = decoded;
            }
          } else if (rle?.rle) {
            // Legacy format: rle.rle can be a base64+zlib string OR a nested COCO RLE dict
            const inner = rle.rle;
            const decoded = (typeof inner === 'object' && inner?.counts)
              ? decodeCOCORLE(inner)
              : decodeMaskRLE(inner, rle.shape);
            if (decoded) {
              objectMasksRef.current[objId] = decoded;
            }
          }
        }
        // Trigger re-render — the draw useEffect will redraw all masks on the canvas overlay
        bumpUi();
        enqueueSnackbar('Prompt processed.', { variant: 'success' });
      }
    } catch (e) {
      console.error('Error submitting prompt:', e);
      enqueueSnackbar(e.message || 'Error submitting prompt.', { variant: 'error' });
    } finally {
      setIsGlobalLoading(false);
    }
  }, 500), [
    sessionIdQuery, currentIndex, serverReady, gpuUrl, enqueueSnackbar, currentObjectId, bumpUi
  ]);


  useEffect(() => {
    if (!serverReady) return;
    if (!shouldSubmitRef.current) return;

    // Build the minimal payload we actually send
    const payloadPrompt = {};
    Object.entries(promptMap).forEach(([id, v]) => {
      const points = v?.points || [];
      const labels = v?.labels || [];
      if (points.length) payloadPrompt[id] = [points, labels];
    });

    // Nothing to send?
    if (Object.keys(payloadPrompt).length === 0) return;

    const key = JSON.stringify(payloadPrompt);
    if (key === lastPayloadRef.current) return; // identical to last submission → skip

    lastPayloadRef.current = key;
    shouldSubmitRef.current = false; // consume the flag
    sendPrompt(promptMap);           // your debounced/task-aware sender
  }, [uiTick, serverReady, sendPrompt]);


  // Timeout failsafe is now handled by useEC2Session hook.

  const clearPoints = async () => {
    promptMapRef.current = {};
    objectMasksRef.current = {};
    objectMetaRef.current = {};
    setObjectIds([]);
    setCurrentObjectId(1);
    shouldSubmitRef.current = false;
    lastPayloadRef.current = '';
    // Reset pipeline-flow state so the next Colony Count starts fresh —
    // without this, picking a different pipeline keeps the prior dish-shape
    // selection + dialog state, which blocks switching cleanly.
    setCountMode(null);
    setShowCountModeDialog(false);
    setMorphDialogState(null);
    setFindSimilarDialogState(null);
    setSpiralCircle({ cx: 0, cy: 0, radius: 0 });
    setRectROI({ x: 0, y: 0, w: 0, h: 0 });
    setAutoSegProgress({ inProgress: false, progress: 0, message: '' });
    setPropagatedMasks({});
    bumpUi();

    // Clear canvas overlay (video is the background, canvas is transparent)
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');

    // Check if session is ready before clearing
    if (!serverReady) {
      enqueueSnackbar('Setup not complete. Please wait a moment.', { variant: 'warning' });
      return;
    }

    try {
      // Check inference readiness before calling reset_state
      //const readinessResponse = await fetch(`${gpuUrl}/check_inference_ready/${sessionId}`);
      const readinessResponse = await fetch(`${gpuUrl}/check_inference_ready/${sessionId}?sessionId=${encodeURIComponent(sessionId)}`);
      const readinessResult = await readinessResponse.json();

      if (!readinessResult.inference_ready) {
        enqueueSnackbar('Still preparing, please wait...', { variant: 'info' });
        return;
      }

      const res = await fetch(`${gpuUrl}/reset_state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionId })
      });

      if (!res.ok) throw new Error('Reset state failed');
      enqueueSnackbar('Points cleared.', { variant: 'info' });
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Failed to reset state.', { variant: 'error' });
    }
  };


  // Wipe the live session prefix so a "redo + track" cycle starts fresh.
  // Two failure modes this avoids:
  //   1. The new propagation runs at a different stride or window than the
  //      previous one, leaving high-index frames from the previous run in
  //      the live prefix. The next save's snapshot copies BOTH sets and the
  //      colony-map preview (which picks the highest-index frame) ends up
  //      identical to the previous save's preview.
  //   2. Cached growth/pigmentation/rugosity JSON from the previous run
  //      makes the new precompute skip work, or briefly serves the old
  //      curve while the new one is in flight.
  // Saved snapshots under saves/ are NEVER touched here.
  const wipeLivePrefixBeforePropagation = async (sessionId) => {
    if (!sessionId) return;
    const bucket = process.env.REACT_APP_USER_BUCKET;
    const livePrefix = `Analysis/${sessionId}/`;
    try {
      await ensureClients();
      let token = null;
      const toDelete = [];
      do {
        const res = await s3.listObjectsV2({
          Bucket: bucket,
          Prefix: livePrefix,
          ...(token ? { ContinuationToken: token } : {}),
        }).promise();
        (res.Contents || []).forEach((o) => {
          const rel = o.Key.substring(livePrefix.length);
          if (!rel || rel.startsWith('saves/')) return;
          toDelete.push({ Key: o.Key });
        });
        token = res.IsTruncated ? res.NextContinuationToken : null;
      } while (token);
      // deleteObjects caps at 1000 keys per call.
      for (let i = 0; i < toDelete.length; i += 1000) {
        const batch = toDelete.slice(i, i + 1000);
        await s3.deleteObjects({
          Bucket: bucket,
          Delete: { Objects: batch, Quiet: true },
        }).promise();
      }
      if (toDelete.length) {
        // Force the chart panels to drop the previous run's cached state
        // immediately so a fast user does not see the old curve render for
        // one tick after the new propagation finishes.
        setBgAnalysisTasks({ growth: null, pigmentation: null, rugosity: null, spiral: null });
        setGrowthData({ csvUrl: null, timeInterval: 30 });
        setPrecomputeDone(false);
        setPropagatedMasks({});
      }
    } catch (e) {
      console.warn('wipe live prefix failed (continuing):', e?.code || e?.message);
    }
  };

  const triggerPropagation = async () => {
    const sid = sessionIdQuery || sessionStorage.getItem('sessionId');

    // Check if session is ready before propagation
    if (!serverReady) {
      enqueueSnackbar('Setup not complete. Please wait a moment.', { variant: 'warning' });
      return;
    }

    try {
      // Drop any leftover RLE + cached analyses from a previous track in the
      // same session before starting the new one. Without this, the second
      // track + save inherits the previous track's high-index frames and
      // every downstream view (colony map preview, growth chart trailing
      // frames) looks identical to the previous save.
      await wipeLivePrefixBeforePropagation(sid);

      // Double-check inference readiness before calling propagate_in_video
      //const readinessResponse = await fetch(`${gpuUrl}/check_inference_ready/${sid}`);
      const readinessResponse = await fetch(`${gpuUrl}/check_inference_ready/${sid}?sessionId=${encodeURIComponent(sid)}`);
      const readinessResult = await readinessResponse.json();

      if (!readinessResult.inference_ready) {
        enqueueSnackbar('Still preparing, please wait...', { variant: 'info' });
        return;
      }

      const lastIdx = Math.max(0, (frameCount || 1) - 1);
      // propagateStart/End live on the toolbar scrubber in DISPLAY coords
      // (display 0 = newest in time, display N-1 = oldest). The backend SAM2
      // session indexes chronologically (backend 0 = oldest). Convert before
      // sending so the masked range matches what the user picked on the slider.
      const rawStart = propagateStart ?? lastIdx;
      const rawEnd = propagateEnd ?? 0;
      const startBackend = toBackendIndex(rawStart);
      const endBackend = toBackendIndex(rawEnd);
      const startFrame = Math.max(0, Math.min(startBackend, endBackend));
      const endFrame = Math.min(lastIdx, Math.max(startBackend, endBackend));
      const anchorIdx = Math.min(Math.max(toBackendIndex(currentIndex), startFrame), endFrame);

      // Shape modes: skip SAM2. The user has explicitly committed to a fixed
      // geometry, so we ask the backend to write the same RLE to every frame
      // in the window. This is fast (no GPU) and keeps area constant by
      // construction so growth/color/rugosity reflect the same region per frame.
      let pr;
      if (drawingMode !== 'sam') {
        const masksToFreeze = Object.entries(objectMasksRef.current || {})
          .filter(([oid]) => String(oid) !== String(DISH_OBJ_ID))
          .map(([oid, m]) => {
            if (!m || !m.mask || !m.width || !m.height) return null;
            const rle = encodeCOCORLE(m.mask, m.width, m.height);
            return { obj_id: parseInt(oid, 10), rle };
          })
          .filter(Boolean);
        if (masksToFreeze.length === 0) {
          enqueueSnackbar('Draw at least one rectangle or ellipse first.', { variant: 'warning' });
          return;
        }
        pr = await fetch(`${gpuUrl}/freeze_propagation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sid,
            objects: masksToFreeze,
            frame_stride: frameStride,
            start_frame: startFrame,
            end_frame: endFrame,
          }),
        });
      } else {
        // Auto-detect (`/automask`, `/automask_grid`, classical-CV fallback)
        // writes masks to objectMasksRef but does NOT register them with
        // SAM2's predictor_state. /propagate_in_video only tracks objects
        // that ARE in predictor_state (those added via /add_prompt or
        // /add_mask), so without this loop a "Track" right after Auto Detect
        // propagates exactly zero objects and looks like a no-op.
        //
        // Only register objects that have no point prompts — those are the
        // auto-detected ones. Point-prompted objects (promptMap has points)
        // are already in predictor_state and re-registering would clobber
        // their original prompts.
        const toRegister = Object.entries(objectMasksRef.current || {})
          .filter(([oid]) => String(oid) !== String(DISH_OBJ_ID))
          .filter(([oid, m]) => {
            if (!m || !m.mask || !m.width || !m.height) return false;
            const promptPts = promptMapRef.current[oid]?.points || [];
            return promptPts.length === 0;
          });
        if (toRegister.length > 0) {
          enqueueSnackbar(`Registering ${toRegister.length} mask${toRegister.length === 1 ? '' : 's'} with tracker…`, { variant: 'info' });
          // Sequential — backend's register_mask mutates SAM2 inference state,
          // and SAM2 isn't thread-safe across overlapping add_new_mask calls.
          // ~10-50 ms each, so 100 colonies = ~1-5 s.
          for (const [oid, m] of toRegister) {
            await registerMaskWithBackend(parseInt(oid, 10), m.mask, m.width, m.height);
          }
        }

        pr = await fetch(`${gpuUrl}/propagate_in_video`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sid,
            frame_idx: anchorIdx,
            frame_stride: frameStride,
            start_frame: startFrame,
            end_frame: endFrame,
          })
        });
      }
      if (!pr.ok) throw new Error('Propagation failed');

      const { task_id } = await pr.json();
      setPropagationProgress({ inProgress: true, stage: 'initializing', progress: 0, message: '' });

      const iv = setInterval(async () => {
        try {
          const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
          const resp = await fetch(`${gpuUrl}/task_progress/${task_id}?sessionId=${encodeURIComponent(sessionId)}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            },

          });



          const pd = await resp.json();
          setPropagationProgress({
            inProgress: true,
            stage: pd.stage,
            progress: pd.progress,
            message: pd.message
          });
          if (pd.stage === 'complete' || pd.stage === 'error' || pd.stage === 'cancelled') {
            clearInterval(iv);
            setPropagationProgress(prev => ({ ...prev, inProgress: false }));

            if (pd.stage === 'complete') {
              enqueueSnackbar('Processing complete. Updating timeline...', { variant: 'success' });
              setDrawingMasks(true); // Show mask drawing progress
              // Fetch propagated masks for all frames
              await fetchPropagatedMasks(sid, gpuUrl);
              // Switch to timeline view with masks
              setDrawingMasks(false);
              setShowTimelineWithMasks(true);
              setPromptMapCompat({});
            } else {
              enqueueSnackbar(`Processing error: ${pd.message}`, { variant: 'error' });
            }
          }
        } catch {
          clearInterval(iv);
          setPropagationProgress(prev => ({ ...prev, inProgress: false }));
          enqueueSnackbar('Processing interrupted. Please try again.', { variant: 'error' });
        }
      }, 2000);
    } catch (e) {
      enqueueSnackbar('Failed to start processing.', { variant: 'error' });
      console.error(e);
    }
  };

  // ── Post-propagation per-frame correction ──────────────────────────────────
  // Enter edit mode for the currently-scrubbed frame. Snapshots the current
  // per-frame mask state so Cancel can revert. Mode = 'retrack' | 'single'.
  const enterEditFrame = useCallback((nextMode) => {
    const frameIdx = toBackendIndex(currentIndex);
    const snapshot = {
      frameIdx,
      objectMasks: {},
      promptMap: {},
    };
    // Shallow-clone the per-object refs for this frame so we can restore on cancel.
    for (const [oid, m] of Object.entries(objectMasksRef.current || {})) {
      if (m && m.mask && m.width && m.height) {
        // We deliberately reuse the same Uint8Array; revert just swaps the ref entry back.
        snapshot.objectMasks[oid] = { mask: m.mask, width: m.width, height: m.height };
      }
    }
    for (const [oid, p] of Object.entries(promptMapRef.current || {})) {
      if (p) snapshot.promptMap[oid] = { points: [...(p.points || [])], labels: [...(p.labels || [])] };
    }
    editFrameSnapshotRef.current = snapshot;
    setEditFrameMode(nextMode);
    enqueueSnackbar(
      nextMode === 'retrack'
        ? `Edit frame ${frameIdx}: add points or shapes, then Apply to re-track.`
        : `Edit frame ${frameIdx}: this frame's mask only — no re-propagation.`,
      { variant: 'info' }
    );
  }, [currentIndex, toBackendIndex, enqueueSnackbar]);

  const cancelEditFrame = useCallback(() => {
    const snap = editFrameSnapshotRef.current;
    if (snap) {
      // Restore per-object masks for the frame the user was editing.
      objectMasksRef.current = snap.objectMasks;
      promptMapRef.current = snap.promptMap;
      // Clear shape draft + any in-flight shape edit.
      setShapeDraft(null);
      if (shapeEditRef.current) {
        shapeEditRef.current = { active: false, mode: null, handle: null, origGeom: null, startX: 0, startY: 0 };
      }
      bumpUi();
    }
    editFrameSnapshotRef.current = null;
    setEditFrameMode(null);
  }, [bumpUi]);

  // Apply the correction. For 'retrack', stages a new prompt frame and re-runs
  // /propagate_in_video. For 'single', overwrites just this frame's per-frame
  // RLE JSON on S3 with the user's edited masks. In both cases, after success
  // we re-fetch propagated_masks and trigger /cpu/precompute?force=true so
  // growth/color/rugosity reflect the corrected masks.
  const applyEditFrame = useCallback(async () => {
    if (!editFrameMode || applyingCorrection) return;
    const sid = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (!sid) {
      enqueueSnackbar('Session expired. Cannot apply correction.', { variant: 'error' });
      return;
    }
    const frameIdx = toBackendIndex(currentIndex);
    setApplyingCorrection(true);

    try {
      if (editFrameMode === 'single') {
        // Build edits from the current frame's objectMasksRef.
        const edits = Object.entries(objectMasksRef.current || {})
          .filter(([oid]) => String(oid) !== String(DISH_OBJ_ID))
          .map(([oid, m]) => {
            if (!m || !m.mask || !m.width || !m.height) return null;
            const rle = encodeCOCORLE(m.mask, m.width, m.height);
            return { obj_id: parseInt(oid, 10), rle };
          })
          .filter(Boolean);
        if (edits.length === 0) {
          enqueueSnackbar('No object masks to save on this frame.', { variant: 'warning' });
          setApplyingCorrection(false);
          return;
        }
        const r = await fetch(`${gpuUrl}/edit_frame_mask`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: sid, frame_idx: frameIdx, objects: edits }),
        });
        if (!r.ok) throw new Error(`edit_frame_mask returned ${r.status}`);
        // Mirror locally so the timeline view reflects the change without a full re-fetch.
        setPropagatedMasks(prev => {
          const next = { ...prev };
          next[frameIdx] = edits.reduce((acc, e) => {
            acc[e.obj_id] = e.rle;
            return acc;
          }, {});
          return next;
        });
        enqueueSnackbar(`Saved frame ${frameIdx} (no re-tracking).`, { variant: 'success' });
      } else if (editFrameMode === 'retrack') {
        // The user's new prompts have already been pushed to predictor_state by sendPrompt().
        // Re-run propagation from this frame, respecting the toolbar window.
        // Convert display-coord markers to backend-coord window — same logic as triggerPropagation.
        const lastIdx = Math.max(0, (frameCount || 1) - 1);
        const rawStart = propagateStart ?? lastIdx;
        const rawEnd = propagateEnd ?? 0;
        const startBackend = toBackendIndex(rawStart);
        const endBackend = toBackendIndex(rawEnd);
        const startFrame = Math.max(0, Math.min(startBackend, endBackend));
        const endFrame = Math.min(lastIdx, Math.max(startBackend, endBackend));
        const anchorIdx = Math.min(Math.max(frameIdx, startFrame), endFrame);
        const pr = await fetch(`${gpuUrl}/propagate_in_video`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sid,
            frame_idx: anchorIdx,
            frame_stride: frameStride,
            start_frame: startFrame,
            end_frame: endFrame,
          }),
        });
        if (!pr.ok) throw new Error(`propagate_in_video returned ${pr.status}`);
        const { task_id } = await pr.json();
        // Poll until the re-propagation completes, then refresh masks.
        await new Promise((resolve, reject) => {
          const iv = setInterval(async () => {
            try {
              const r = await fetch(`${gpuUrl}/task_progress/${task_id}?sessionId=${encodeURIComponent(sid)}`);
              if (!r.ok) { clearInterval(iv); reject(new Error('progress poll failed')); return; }
              const pd = await r.json();
              setPropagationProgress({
                inProgress: pd.stage !== 'complete' && pd.stage !== 'error' && pd.stage !== 'cancelled',
                stage: pd.stage,
                progress: pd.progress || 0,
                message: pd.message || '',
              });
              if (pd.stage === 'complete') { clearInterval(iv); resolve(); }
              else if (pd.stage === 'error' || pd.stage === 'cancelled') {
                clearInterval(iv);
                reject(new Error(pd.message || pd.stage));
              }
            } catch (e) { clearInterval(iv); reject(e); }
          }, 1500);
        });
        await fetchPropagatedMasks(sid);
        enqueueSnackbar(`Re-tracked from frame ${anchorIdx}.`, { variant: 'success' });
      }

      // Invalidate cached analyses so growth/color/rugosity recompute against the
      // new masks. Three pieces are wiped synchronously so the user can keep
      // editing other frames; the actual S3 recompute fires in the background:
      //   1. bgAnalysisTasks: the in-memory map of {growth, rugosity, pigmentation}
      //      task IDs. Cleared NOW so the next chart open re-POSTs instead of
      //      polling the pre-edit task.
      //   2. growthData.csvUrl: chart state cached on the page; replace it with
      //      null so reopening Growth goes through the API again.
      //   3. /cpu/precompute (force=true) — fire-and-forget. Don't await; the
      //      backend overwrites results.json when done. If the user reopens a
      //      chart before that finishes, the chart handler will see the in-
      //      progress task via precomputeTaskId and either show stale-for-a-
      //      few-seconds data or wait. Either way, the edit toolbar is freed
      //      up immediately.
      setBgAnalysisTasks({ growth: null, pigmentation: null, rugosity: null, spiral: null });
      setGrowthData({ csvUrl: null, timeInterval: 30 });
      setPrecomputeDone(false);
      fetch(`${cpuUrl}/precompute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sid,
          s3_path: initialPath || (multiPaths.length > 0 ? multiPaths[0] : ''),
          force: true,
        }),
      })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d?.task_id) setPrecomputeTaskId(d.task_id);
        })
        .catch(() => { /* non-fatal */ });
      enqueueSnackbar('Recomputing growth, color, texture in the background…', { variant: 'info' });

      editFrameSnapshotRef.current = null;
      setEditFrameMode(null);
    } catch (e) {
      console.error('applyEditFrame failed:', e);
      enqueueSnackbar(`Apply failed: ${e.message}`, { variant: 'error' });
    } finally {
      setApplyingCorrection(false);
    }
  }, [
    editFrameMode, applyingCorrection, sessionIdQuery, currentIndex, toBackendIndex,
    gpuUrl, cpuUrl, encodeCOCORLE, propagateStart, propagateEnd, frameCount, frameStride,
    initialPath, multiPaths, enqueueSnackbar,
  ]);

  // Function to fetch propagated masks for all frames.
  // Stores raw RLE data — masks are decoded on-demand per frame to avoid
  // allocating ~80 GB of decoded Uint8Arrays for 274 frames x 19 objects.
  const fetchPropagatedMasks = async (sessionId) => {
    try {
      const response = await fetch(`${gpuUrl}/get_propagated_masks/${sessionId}?sessionId=${encodeURIComponent(sessionId)}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch propagated masks: ${response.status}`);
      }
      const data = await response.json();
      const masksFromBackend = data.masks;

      if (!masksFromBackend || Object.keys(masksFromBackend).length === 0) {
        enqueueSnackbar('No detection results found.', { variant: 'warning' });
        return;
      }

      // Store raw RLE data keyed by frame index (as int).
      // Format: {frame_idx: {obj_id: {counts, size}}}
      const allFrameRLE = {};
      Object.entries(masksFromBackend).forEach(([frameIdx, frameMasks]) => {
        const validMasks = {};
        Object.entries(frameMasks).forEach(([objId, rle]) => {
          if (rle?.counts && rle?.size) {
            validMasks[objId] = rle;
          }
        });
        if (Object.keys(validMasks).length > 0) {
          allFrameRLE[parseInt(frameIdx)] = validMasks;
        }
      });

      setPropagatedMasks(allFrameRLE);
      enqueueSnackbar(`Loaded results for ${Object.keys(allFrameRLE).length} frames.`, { variant: 'success' });

      // A fresh propagation has just landed, so any precompute results
      // cached on S3 from the previous run are stale by definition. Wipe
      // the in-memory chart state so the Growth/Color/Texture panels
      // cannot keep rendering an older csvUrl and request the new task,
      // then fire /cpu/precompute with force=true so the CPU service
      // overwrites growth/pigmentation/rugosity instead of short-circuiting
      // on the cached files. Without force=true, the backend skipped the
      // recompute and every "redo + analyze" cycle kept showing the first
      // run's chart.
      setBgAnalysisTasks({ growth: null, pigmentation: null, rugosity: null, spiral: null });
      setGrowthData({ csvUrl: null, timeInterval: 30 });
      setPrecomputeDone(false);
      fetch(`${cpuUrl}/precompute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          s3_path: initialPath || (multiPaths.length > 0 ? multiPaths[0] : ''),
          force: true,
        }),
      }).then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.task_id) setPrecomputeTaskId(d.task_id); })
        .catch(() => {});

    } catch (error) {
      console.error('Error fetching propagated masks:', error);
      enqueueSnackbar('Failed to load processed results.', { variant: 'error' });
    }
  };

  const deleteObject = async (objIdToDelete) => {
    const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
    delete promptMapRef.current[objIdToDelete];
    delete objectMasksRef.current[objIdToDelete];
    delete objectMetaRef.current[objIdToDelete];
    setObjectIds(ids => ids.filter(id => id !== objIdToDelete));

    // Optional: reset to another object
    setCurrentObjectId(1);
    bumpUi();

    // Call backend
    try {
      const res = await fetch(`${gpuUrl}/delete_object`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId,
          obj_id: objIdToDelete
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to delete');
      }

      enqueueSnackbar(`Deleted object ${objIdToDelete}`, { variant: 'success' });
    } catch (error) {
      console.error('Delete error:', error);
      enqueueSnackbar('Failed to delete object', { variant: 'error' });
    }
  };


  // Function to decode RLE mask data
  const decodeMaskRLE = (encodedMask, shape) => {
    // Guard: encodedMask must be a base64 string. Anything else (object, null, etc.)
    // indicates a caller is passing the wrong format — return null safely.
    if (typeof encodedMask !== 'string') return null;
    try {

      // Handle 3D shape array - extract height and width
      let height, width;
      if (Array.isArray(shape) && shape.length === 3) {
        // Shape is [channels, height, width] or [1, height, width]
        height = shape[1];
        width = shape[2];
      } else if (Array.isArray(shape) && shape.length === 2) {
        // Shape is [height, width]
        height = shape[0];
        width = shape[1];
      } else {
        console.error('Invalid shape format:', shape);
        return null;
      }


      // Decode base64
      const compressedData = atob(encodedMask);
      const compressedBytes = new Uint8Array(compressedData.length);
      for (let i = 0; i < compressedData.length; i++) {
        compressedBytes[i] = compressedData.charCodeAt(i);
      }

      // Decompress using pako (zlib decompression)
      const decompressedBytes = pako.inflate(compressedBytes);

      // Convert bytes back to uint32 array (RLE data)
      const rleData = new Uint32Array(decompressedBytes.buffer);

      // Decode RLE to flat mask
      const totalPixels = height * width;
      const maskFlat = new Uint8Array(totalPixels);
      let pos = 0;

      for (let i = 0; i < rleData.length; i += 2) {
        const val = rleData[i];
        const count = rleData[i + 1];

        if (pos + count > totalPixels) {
          console.error('RLE data exceeds expected size');
          break;
        }

        for (let j = 0; j < count; j++) {
          maskFlat[pos + j] = val;
        }
        pos += count;
      }


      // Return data in the format expected by drawMaskOnCanvas
      return {
        mask: maskFlat, // Keep as flat array for ImageData
        width: width,
        height: height
      };
    } catch (error) {
      console.error('Error decoding mask:', error);
      return null;
    }
  };

  const drawMaskOnCanvas = (canvas, maskData, objectId, options = {}) => {
    if (!maskData || !canvas) return;

    const { mask, width, height } = maskData;
    const srcW = parseInt(width);
    const srcH = parseInt(height);
    if (!srcW || !srcH || !mask) return;

    const [r, g, b] = generateObjectColor(objectId);
    const { isSelected = false, isHovered = false, isDimmed = false } = options;

    // Raster cache: rasterizing the outline + fill is the dominant cost when
    // many masks share a frame. Identical (mask, color, state, canvas-width)
    // tuples produce identical pixels, so we cache the offscreen canvas.
    // Keyed by maskData (WeakMap) → Map<stateKey, { offscreen, geom }>.
    // Cache lives on the page; cleared on frameSize change (canvas resize)
    // so we don't draw a stale scale.
    const cacheRoot = maskRasterCacheRef.current;
    let perMask = cacheRoot.get(maskData);
    const stateKey = `${objectId}|${isSelected ? 1 : 0}|${isHovered ? 1 : 0}|${isDimmed ? 1 : 0}|${canvas.width}`;
    if (perMask) {
      const hit = perMask.get(stateKey);
      if (hit) {
        const ctxHit = canvas.getContext('2d');
        ctxHit.drawImage(hit.offscreen, hit.destX, hit.destY, hit.destW, hit.destH);
        return;
      }
    }
    // Hover wins over selected for highlighting (lets users preview a different
    // colony without losing the current edit target). Dimmed pulls the rest of
    // the masks back so the hovered one stands out.

    // If this mask is bbox-cropped (has offsetX/offsetY + fullWidth/fullHeight),
    // it only covers a sub-region of the full frame. Render into the equivalent
    // sub-rectangle of the canvas instead of scaling to the full canvas.
    const hasOffset = Number.isFinite(maskData.offsetX) && Number.isFinite(maskData.offsetY)
      && Number.isFinite(maskData.fullWidth) && Number.isFinite(maskData.fullHeight);
    const fullW = hasOffset ? maskData.fullWidth : srcW;
    const fullH = hasOffset ? maskData.fullHeight : srcH;
    const offX = hasOffset ? maskData.offsetX : 0;
    const offY = hasOffset ? maskData.offsetY : 0;

    // Run edge detection at display-scale resolution so the resulting outlines
    // remain a visible ~2px when the canvas is CSS-scaled down.
    const TARGET_W = 1024;
    const fullDw = Math.min(canvas.width, TARGET_W);
    const fullDh = Math.round(fullDw * canvas.height / canvas.width);

    // Display dimensions of just this mask's bbox.
    const dw = Math.max(1, Math.round(fullDw * (srcW / fullW)));
    const dh = Math.max(1, Math.round(fullDh * (srcH / fullH)));

    const scaleX = srcW / dw;
    const scaleY = srcH / dh;

    // Nearest-neighbor downsample mask to display scale
    const scaledMask = new Uint8Array(dw * dh);
    for (let dy = 0; dy < dh; dy++) {
      const sy = Math.min(Math.floor(dy * scaleY), srcH - 1);
      for (let dx = 0; dx < dw; dx++) {
        const sx = Math.min(Math.floor(dx * scaleX), srcW - 1);
        scaledMask[dy * dw + dx] = mask[sy * srcW + sx];
      }
    }

    // Edge detection pass 1: foreground pixels adjacent to background.
    // Do NOT treat the mask buffer's boundary as an edge — for bbox-cropped
    // masks the buffer edge is the bbox edge, not a mask/background transition,
    // so doing so paints visible rectangle outlines around every grid colony.
    const edgeBuf = new Uint8Array(dw * dh);
    for (let y = 1; y < dh - 1; y++) {
      for (let x = 1; x < dw - 1; x++) {
        const i = y * dw + x;
        if (!scaledMask[i]) continue;
        if (!scaledMask[i - 1] || !scaledMask[i + 1] || !scaledMask[i - dw] || !scaledMask[i + dw]) {
          edgeBuf[i] = 1;
        }
      }
    }

    // Edge pass 2: dilate by 1px → ~2px total outline width
    const edgeTmp = new Uint8Array(edgeBuf);
    for (let y = 0; y < dh; y++) {
      for (let x = 0; x < dw; x++) {
        const i = y * dw + x;
        if (!scaledMask[i] || edgeTmp[i]) continue;
        if ((x > 0 && edgeTmp[i - 1]) || (x < dw - 1 && edgeTmp[i + 1])
           || (y > 0 && edgeTmp[i - dw]) || (y < dh - 1 && edgeTmp[i + dw])) {
          edgeBuf[i] = 1;
        }
      }
    }

    // Edge pass 3 (selected OR hovered): extra dilation → ~3-4px outline
    if (isSelected || isHovered) {
      const edgeTmp2 = new Uint8Array(edgeBuf);
      for (let y = 0; y < dh; y++) {
        for (let x = 0; x < dw; x++) {
          const i = y * dw + x;
          if (!scaledMask[i] || edgeTmp2[i]) continue;
          if ((x > 0 && edgeTmp2[i - 1]) || (x < dw - 1 && edgeTmp2[i + 1])
             || (y > 0 && edgeTmp2[i - dw]) || (y < dh - 1 && edgeTmp2[i + dw])) {
            edgeBuf[i] = 1;
          }
        }
      }
    }

    const offscreen = document.createElement('canvas');
    offscreen.width = dw;
    offscreen.height = dh;
    const offCtx = offscreen.getContext('2d');
    const imageData = offCtx.createImageData(dw, dh);
    const data = imageData.data;

    // Semi-transparent fill for selected/hovered colony
    const fillAlpha = isHovered ? 80 : isSelected ? 55 : 0;
    if (fillAlpha > 0) {
      for (let i = 0; i < dw * dh; i++) {
        if (!scaledMask[i]) continue;
        const idx = i * 4;
        data[idx]     = r;
        data[idx + 1] = g;
        data[idx + 2] = b;
        data[idx + 3] = fillAlpha;
      }
    }

    // Outline pixels — brighter on hovered/selected, faded on dimmed
    const boost = isHovered ? 100 : isSelected ? 80 : 0;
    const er = Math.min(255, r + boost);
    const eg = Math.min(255, g + boost);
    const eb = Math.min(255, b + boost);
    const outlineAlpha = isDimmed ? 70 : 255;
    for (let i = 0; i < dw * dh; i++) {
      if (!edgeBuf[i]) continue;
      const idx = i * 4;
      data[idx]     = er;
      data[idx + 1] = eg;
      data[idx + 2] = eb;
      data[idx + 3] = outlineAlpha;
    }

    offCtx.putImageData(imageData, 0, 0);
    const ctx = canvas.getContext('2d');
    // Scale offscreen back up to native canvas dimensions, positioned at the
    // mask's full-frame offset (non-zero only for bbox-cropped grid masks).
    const destX = (offX / fullW) * canvas.width;
    const destY = (offY / fullH) * canvas.height;
    const destW = (srcW / fullW) * canvas.width;
    const destH = (srcH / fullH) * canvas.height;
    ctx.drawImage(offscreen, destX, destY, destW, destH);

    // Store for next draw with the same state — see cache lookup above.
    if (!perMask) {
      perMask = new Map();
      cacheRoot.set(maskData, perMask);
    }
    perMask.set(stateKey, { offscreen, destX, destY, destW, destH });
  };


  // Function to add object ID annotation to canvas masks



  // Function to reset everything and start over
  const handleRedo = async () => {
    try {
      const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');

      // Call backend reset_state to clear model memory
      const response = await fetch(`${gpuUrl}/reset_state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionId })
      });

      if (!response.ok) {
        throw new Error('Failed to reset backend state');
      }

      promptMapRef.current = { 1: { points: [], labels: [] } };
      objectMasksRef.current = {};
      objectMetaRef.current = { 1: { name: 'Object 1' } };
      setObjectIds([1]);
      setCurrentObjectId(1);
      setPropagatedMasks({});
      setShowTimelineWithMasks(false);
      setDrawingMasks(false);
      bumpUi();

      // Clear canvas overlay
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      enqueueSnackbar('Reset complete! You can now add new prompts.', { variant: 'success' });

    } catch (error) {
      console.error('Error during redo:', error);
      enqueueSnackbar('Failed to reset. Please refresh the page.', { variant: 'error' });
    }
  };

  // ─── CPU analysis helpers ────────────────────────────────────────────────

  // Poll a CPU service background task until completion or timeout
  const pollCpuTask = async (taskId) => {
    const deadline = Date.now() + 5 * 60 * 1000; // 5-minute timeout
    while (Date.now() < deadline) {
      let resp;
      try {
        resp = await fetch(`${cpuUrl}/task_progress/${taskId}`);
      } catch {
        throw new Error('Analysis server is unreachable. Please contact your administrator.');
      }
      if (resp.status === 503 || resp.status === 504) {
        throw new Error('Analysis server is currently unavailable. Please contact your administrator and try again later.');
      }
      if (!resp.ok) {
        throw new Error(`Analysis polling error (${resp.status})`);
      }
      const prog = await resp.json();
      if (prog.stage === 'complete') {
        const rr = await fetch(`${cpuUrl}/task_results/${taskId}`);
        if (!rr.ok) throw new Error('Failed to retrieve analysis results');
        return await rr.json();
      }
      if (prog.stage === 'error') {
        throw new Error(prog.message || 'Analysis task failed on server');
      }
      await new Promise(r => setTimeout(r, 2000));
    }
    throw new Error('Analysis timed out after 5 minutes.');
  };

  // Function to call growth API and show chart
  const handleGrowthAnalysis = async () => {
    try {
      const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
      enqueueSnackbar('Starting growth analysis...', { variant: 'info' });

      let taskId = bgAnalysisTasks.growth;
      if (!taskId) {
        let resp;
        try {
          resp = await fetch(`${cpuUrl}/growth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_id: sessionId,
              s3_path: initialPath || (multiPaths.length > 0 ? multiPaths[0] : ''),
            })
          });
        } catch {
          enqueueSnackbar('Growth analysis server is unreachable. Please contact your administrator.', { variant: 'error' });
          return;
        }
        if (resp.status >= 500) {
          enqueueSnackbar('Growth analysis server is currently unavailable. Please contact your administrator.', { variant: 'error' });
          return;
        }
        if (!resp.ok) throw new Error(`Growth analysis request failed (${resp.status})`);
        const d = await resp.json();
        taskId = d.task_id;
      }

      enqueueSnackbar('Growth analysis running...', { variant: 'info' });
      const result = await pollCpuTask(taskId);
      if (result.file_url) {
        setGrowthData({ csvUrl: result.file_url, timeInterval: 30 });
        setShowGrowthChart(true);
      } else {
        throw new Error('No file URL in growth analysis result');
      }
    } catch (error) {
      const msg = error.message || '';
      if (msg.includes('unavailable') || msg.includes('unreachable') || msg.includes('administrator')) {
        enqueueSnackbar(msg, { variant: 'error' });
      } else {
        enqueueSnackbar('Growth analysis failed. Please try again.', { variant: 'error' });
      }
    }
  };

  // Function to call rugosity API for a specific frame
  const handleRugosityApiCall = async (frameIdx) => {
    try {
      const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');

      let taskId = bgAnalysisTasks.rugosity;
      if (!taskId) {
        let resp;
        try {
          resp = await fetch(`${cpuUrl}/rugosity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              session_id: sessionId,
              frame_indices: [frameIdx],
              s3_path: initialPath || (multiPaths.length > 0 ? multiPaths[0] : ''),
            })
          });
        } catch {
          throw new Error(`Network connection failed. Please check your internet connection and try again. (Frame ${frameIdx})`);
        }
        if (resp.status >= 500) {
          throw new Error('Analysis server is currently unavailable. Please contact your administrator.');
        }
        if (!resp.ok) {
          const err = await resp.text();
          throw new Error(`Rugosity analysis failed for frame ${frameIdx}: ${resp.status} ${err}`);
        }
        const d = await resp.json();
        taskId = d.task_id;
      }

      const result = await pollCpuTask(taskId);
      if (!result.data_url) throw new Error('No data URL in rugosity result');

      const dataResponse = await fetch(result.data_url);
      if (!dataResponse.ok) throw new Error(`Failed to fetch rugosity data: ${dataResponse.status}`);
      const rugosityRaw = await dataResponse.json();

      let objects;
      if (Array.isArray(rugosityRaw)) {
        objects = {};
        rugosityRaw.forEach(obj => {
          const frameData = obj.frames?.find(f => f.frame_idx === frameIdx) || obj.frames?.[0];
          if (frameData) {
            const { frame_idx: _fi, ...metrics } = frameData;
            objects[String(obj.obj_id)] = metrics;
          }
        });
      } else if (rugosityRaw?.objects) {
        objects = rugosityRaw.objects;
      } else {
        throw new Error('Invalid rugosity data format from S3');
      }

      if (!Object.keys(objects).length) {
        throw new Error(`No rugosity data found for frame ${frameIdx}`);
      }

      return { data: { objects } };

    } catch (error) {
      const msg = error.message || '';
      if (msg.includes('Failed to fetch') || msg.includes('ERR_NETWORK')) {
        throw new Error(`Network connection failed. Please check your internet connection. (Frame ${frameIdx})`);
      }
      throw error;
    }
  };

  // (handleNewObject was moved up near the keydown handler so the "+" hotkey
  // can reference it without a TDZ on first render.)

  // Returns true if the colony mask's centroid falls within the dish mask.
  // Used to filter out spurious detections outside the petri dish boundary.
  const isColonyInDish = (decoded, dishMask) => {
    if (!dishMask) return true;
    const { mask: colonyMask, width, height } = decoded;
    const { mask: dishMaskPixels, width: dw, height: dh } = dishMask;
    const w = parseInt(width);
    const h = parseInt(height);
    // Strict overlap check: count colony pixels that fall on a dish-mask
    // pixel; require >=70% overlap. The previous centroid-only check
    // misclassified colonies that straddled the dish boundary (and broke
    // entirely when coordinate spaces differed slightly across runs).
    const sx = dw / w;
    const sy = dh / h;
    let total = 0;
    let inside = 0;
    for (let row = 0; row < h; row++) {
      for (let col = 0; col < w; col++) {
        if (!colonyMask[row * w + col]) continue;
        total++;
        const drow = Math.min(dh - 1, Math.max(0, Math.floor(row * sy)));
        const dcol = Math.min(dw - 1, Math.max(0, Math.floor(col * sx)));
        if (dishMaskPixels[drow * dw + dcol]) inside++;
      }
    }
    if (total === 0) return false;
    return (inside / total) >= 0.7;
  };

  const handleAutoSegment = async () => {
    if (!dishMaskData) {
      setDishSelectMode(true);
      enqueueSnackbar('Click on the dish in the image to select it.', { variant: 'info', autoHideDuration: 4000 });
      return;
    }

    // Phase 2: compute ROI from dish mask bounding box and proceed
    const { mask, width, height } = dishMaskData;
    const w = parseInt(width), h = parseInt(height);
    let minX = w, minY = h, maxX = 0, maxY = 0;
    for (let row = 0; row < h; row++) {
      for (let col = 0; col < w; col++) {
        if (mask[row * w + col]) {
          if (col < minX) minX = col;
          if (col > maxX) maxX = col;
          if (row < minY) minY = row;
          if (row > maxY) maxY = row;
        }
      }
    }
    const computedRoi = minX <= maxX && minY <= maxY
      ? { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 }
      : null;

    // Save dish mask for filtering out-of-dish detections before clearing state
    const savedDishMask = { mask: dishMaskData.mask, width: dishMaskData.width, height: dishMaskData.height };

    // Clear dish selection state
    setDishMaskData(null);
    setDishSelectMode(false);
    // Remove dish object from backend (best-effort)
    const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (sessionId) {
      fetch(`${gpuUrl}/delete_object`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, obj_id: DISH_OBJ_ID })
      }).catch(() => {});
    }
    // delete from refs
    delete promptMapRef.current[DISH_OBJ_ID];
    delete objectMasksRef.current[DISH_OBJ_ID];
    bumpUi();

    if (!sessionId) {
      enqueueSnackbar('Analysis not found. Please reload and try again.', { variant: 'error' });
      return;
    }

    if (!await checkSessionReadiness(sessionId)) return;

    try {
      setIsGlobalLoading(true);
      setAutoSegProgress({ inProgress: true, progress: 0, message: 'Starting…' });
      const body = { sessionId: sessionId, frame_idx: toBackendIndex(currentIndex) };
      if (colonyPipeline) body.pipeline = colonyPipeline;
      if (computedRoi && computedRoi.w > 2 && computedRoi.h > 2) {
        body.roi = { x: Math.round(computedRoi.x), y: Math.round(computedRoi.y), w: Math.round(computedRoi.w), h: Math.round(computedRoi.h) };
      }

      const startResponse = await fetch(`${gpuUrl}/automask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const startResult = await startResponse.json();
      if (!startResponse.ok) {
        throw new Error(startResult.error || 'Failed to start automask task.');
      }

      const { task_id } = startResult;
      enqueueSnackbar('Auto-detection started. Waiting for results...', { variant: 'info' });

      // Poll for completion
      const maxAttempts = 60; // e.g. wait up to ~2 minutes
      const pollInterval = 30000;

      let attempts = 0;
      let status = null;

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        const statusResponse = await fetch(`${gpuUrl}/task_progress/${task_id}?session_id=${sessionId}`);
        const statusData = await statusResponse.json();
        status = statusData;
        const pct = typeof statusData.progress === 'number' ? statusData.progress : 0;
        setAutoSegProgress({
          inProgress: statusData.stage !== 'complete' && statusData.stage !== 'error',
          progress: pct,
          message: statusData.message || statusData.stage || 'Processing…'
        });

        if (status.stage === 'complete') {
          break;
        } else if (status.stage === 'error') {
          throw new Error(status.message || 'Auto-segmentation failed.');
        }

        attempts++;
      }

      if (status.stage !== 'complete') {
        throw new Error('Auto-segmentation timed out.');
      }

      // Fetch the result
      const resultResponse = await fetch(`${gpuUrl}/task_results/${task_id}?session_id=${sessionId}`);
      const result = await resultResponse.json();

      // GPU automask returns: { task_id, result: { masks: [{counts, size, score, area}, ...] } }
      const gpuMasks = result?.result?.masks;
      if (!gpuMasks || gpuMasks.length === 0) {
        enqueueSnackbar('No regions detected. Try adjusting your selection.', { variant: 'warning' });
        return;
      }

      // Decode & draw masks
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);

      const existingIds = Object.keys(objectMasksRef.current).map(Number).filter(n => !isNaN(n));
      let nextId = existingIds.length ? Math.max(...existingIds) + 1 : 1;
      const updatedMasks = {};
      for (const maskData of gpuMasks) {
        const decoded = decodeCOCORLE({ counts: maskData.counts, size: maskData.size });
        if (!decoded || !isColonyInDish(decoded, savedDishMask)) continue;
        const objId = String(nextId++);
        objectMasksRef.current[objId] = decoded;
        if (!objectMetaRef.current[objId]) objectMetaRef.current[objId] = { name: `Object ${objId}` };
        if (!promptMapRef.current[objId]) promptMapRef.current[objId] = { points: [], labels: [] };
        if (!objectIds.includes(+objId)) setObjectIds(ids => [...ids, +objId]);
        bumpUi();
        drawMaskOnCanvas(canvas, decoded, parseInt(objId));
        updatedMasks[objId] = decoded;
      }

      if (Object.keys(updatedMasks).length === 0) {
        enqueueSnackbar('No colonies detected inside the dish area.', { variant: 'warning' });
        return;
      }

      // Update prompt map and metadata
      const newPromptMap = {};
      const newMeta = {};
      for (const objId of Object.keys(updatedMasks)) {
        newPromptMap[objId] = { points: [], labels: [] };
        newMeta[objId] = { name: `Object ${objId}` };
      }

      setPromptMapCompat(prev => ({ ...prev, ...newPromptMap }));
      setObjectMeta(prev => ({ ...prev, ...newMeta }));

      enqueueSnackbar(`Detected ${Object.keys(updatedMasks).length} colonies.`, { variant: 'success' });

    } catch (error) {
      const isCudaOOM = error.message && (
        error.message.toLowerCase().includes('cuda') ||
        error.message.toLowerCase().includes('out of memory') ||
        error.message.toLowerCase().includes('oom at all scales')
      );

      if (isCudaOOM) {
        enqueueSnackbar('Switching to alternative detection method...', { variant: 'warning' });
        try {
          const cpuResponse = await fetch(`${cpuUrl}/phenotypic/detect_colonies`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, frame_idx: toBackendIndex(currentIndex) }),
          });
          if (!cpuResponse.ok) {
            const err = await cpuResponse.json().catch(() => ({}));
            throw new Error(err.detail || 'CPU detection failed');
          }
          const cpuResult = await cpuResponse.json();
          const cpuMasks = cpuResult.masks || [];
          if (cpuMasks.length === 0) {
            enqueueSnackbar('No colonies detected. Try adjusting the dish selection.', { variant: 'warning' });
            return;
          }
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d');
          if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
          const existingIds = Object.keys(objectMasksRef.current).map(Number).filter(n => !isNaN(n));
          let nextId = existingIds.length ? Math.max(...existingIds) + 1 : 1;
          let added = 0;
          for (const maskData of cpuMasks) {
            const decoded = decodeCOCORLE({ counts: maskData.counts, size: maskData.size });
            if (!decoded || !isColonyInDish(decoded, savedDishMask)) continue;
            const objId = String(nextId++);
            objectMasksRef.current[objId] = decoded;
            if (!objectMetaRef.current[objId]) objectMetaRef.current[objId] = { name: `Object ${objId}` };
            if (!promptMapRef.current[objId]) promptMapRef.current[objId] = { points: [], labels: [] };
            if (!objectIds.includes(+objId)) setObjectIds(ids => [...ids, +objId]);
            bumpUi();
            drawMaskOnCanvas(canvas, decoded, parseInt(objId));
            added++;
          }
          if (added === 0) {
            enqueueSnackbar('No colonies detected inside the dish area.', { variant: 'warning' });
            return;
          }
          enqueueSnackbar(`Detected ${added} colony candidates.`, { variant: 'success' });
          return;
        } catch (cpuError) {
          console.error('CPU fallback also failed:', cpuError);
          enqueueSnackbar('Auto-detection failed. Please try again or select colonies manually.', { variant: 'error' });
          return;
        }
      }

      console.error('Autosegment error:', error);
      enqueueSnackbar(error.message || 'Auto-detection failed. Please try again.', { variant: 'error' });
    } finally {
      setIsGlobalLoading(false);
      setAutoSegProgress({ inProgress: false, progress: 0, message: '' });
    }
  };


  // Function to show rugosity analysis chart
  const handleRugosityAnalysis = async () => {
    try {
      // Use the last frame index (length - 1)
      const lastFrameIdx = (frameCount || localImages.length) - 1;

      if (lastFrameIdx < 0) {
        enqueueSnackbar('No images available for texture analysis.', { variant: 'error' });
        return;
      }

      enqueueSnackbar('Opening texture analysis for frame ' + lastFrameIdx + '...', { variant: 'info' });

      // Open the chart dialog - it will handle the API call internally
      setShowRugosityChart(true);

    } catch (error) {
      console.error('Error starting rugosity analysis:', error);
      enqueueSnackbar('Failed to start texture analysis.', { variant: 'error' });
    }
  };

  // ── Colony Count unified flow (90mm / Spiral / Rectangular) ──

  const handleColonyCountStart = () => {
    setShowCountModeDialog(true);
  };

  const handleCountModeSelect = (mode) => {
    setShowCountModeDialog(false);
    const canvas = frameCanvasRef.current?.frameCanvas;
    const cw = canvas?.width || frameSize?.width || 4656;
    const ch = canvas?.height || frameSize?.height || 3496;
    if (mode === 'spiral' || mode === '90mm') {
      setSpiralCircle({
        cx: Math.round(cw / 2),
        cy: Math.round(ch / 2),
        radius: Math.round(Math.min(cw, ch) * 0.4),
      });
    } else if (mode === 'rectangular') {
      const rw = Math.round(cw * 0.7);
      const rh = Math.round(ch * 0.6);
      const rx = Math.round((cw - rw) / 2);
      const ry = Math.round((ch - rh) / 2);
      setRectROI({ x: rx, y: ry, w: rw, h: rh });
      const cols = 4, rows = 4;
      const verticals = [];
      for (let i = 1; i < cols; i++) verticals.push(Math.round(rx + (rw * i) / cols));
      const horizontals = [];
      for (let i = 1; i < rows; i++) horizontals.push(Math.round(ry + (rh * i) / rows));
      setRectGrid({ rows, cols, horizontals, verticals });
    }
    setCountMode(mode);
  };

  // Spiral-specific: circle confirmed -> open config dialog (volume/sectors/quadrants)
  const handleSpiralAlignConfirm = () => {
    setSpiralPlateConfig((prev) => ({
      ...prev,
      center_x: spiralCircle.cx,
      center_y: spiralCircle.cy,
      radius: spiralCircle.radius,
    }));
    setCountMode(null);
    setShowSpiralConfigDialog(true);
  };

  // Shared cancel for any alignment mode
  const handleAlignCancel = () => {
    setCountMode(null);
    frameCanvasRef.current?.clearOverlay();
  };

  const [spiralAutoColonies, setSpiralAutoColonies] = useState(null);

  const handleSpiralConfigConfirm = async () => {
    setShowSpiralConfigDialog(false);

    // Auto-detect: use automask with spiral circle as ROI
    const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (!sessionId) {
      enqueueSnackbar('No session ID available.', { variant: 'error' });
      return;
    }

    const cfg = spiralPlateConfig;
    if (!cfg.center_x || !cfg.radius) {
      enqueueSnackbar('Plate boundary not set. Please align the spiral mask first.', { variant: 'error' });
      return;
    }

    // Compute bounding box ROI from the spiral circle
    const roiX = Math.max(0, Math.round(cfg.center_x - cfg.radius));
    const roiY = Math.max(0, Math.round(cfg.center_y - cfg.radius));
    const roiW = Math.round(cfg.radius * 2);
    const roiH = Math.round(cfg.radius * 2);

    try {
      setIsGlobalLoading(true);
      setAutoSegProgress({ inProgress: true, progress: 0, message: 'Starting colony auto-detection...' });

      if (!await checkSessionReadiness(sessionId)) {
        setIsGlobalLoading(false);
        setAutoSegProgress({ inProgress: false, progress: 0, message: '' });
        return;
      }

      const body = {
        sessionId,
        frame_idx: toBackendIndex(currentIndex),
        roi: { x: roiX, y: roiY, w: roiW, h: roiH },
      };
      if (colonyPipeline) body.pipeline = colonyPipeline;

      const startResponse = await fetch(`${gpuUrl}/automask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const startResult = await startResponse.json();
      if (!startResponse.ok) {
        throw new Error(startResult.error || 'Failed to start automask task.');
      }

      const { task_id } = startResult;
      enqueueSnackbar('Auto-detection started. Waiting for results...', { variant: 'info' });

      // Poll for completion
      const maxAttempts = 60;
      const pollInterval = 30000;
      let attempts = 0;
      let status = null;

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        const statusResponse = await fetch(`${gpuUrl}/task_progress/${task_id}?session_id=${sessionId}`);
        const statusData = await statusResponse.json();
        status = statusData;
        const pct = typeof statusData.progress === 'number' ? statusData.progress : 0;
        setAutoSegProgress({
          inProgress: statusData.stage !== 'complete' && statusData.stage !== 'error',
          progress: pct,
          message: statusData.message || statusData.stage || 'Processing...',
        });
        if (status.stage === 'complete') break;
        if (status.stage === 'error') throw new Error(status.message || 'Auto-detection failed.');
        attempts++;
      }

      if (status.stage !== 'complete') throw new Error('Auto-detection timed out.');

      const resultResponse = await fetch(`${gpuUrl}/task_results/${task_id}?session_id=${sessionId}`);
      const result = await resultResponse.json();
      const gpuMasks = result?.result?.masks;

      if (!gpuMasks || gpuMasks.length === 0) {
        enqueueSnackbar('No colonies detected. You can add colonies manually on the plate view.', { variant: 'warning' });
        setSpiralAutoColonies([]);
        setShowSpiralChart(true);
        return;
      }

      // Convert masks to colony entries with centroids
      const detectedColonies = [];
      let colonyIdx = 1;
      for (const maskData of gpuMasks) {
        const decoded = decodeCOCORLE({ counts: maskData.counts, size: maskData.size });
        if (!decoded) continue;
        const { mask, width, height } = decoded;
        const w = parseInt(width), h = parseInt(height);

        // Compute centroid from mask pixels
        let sumX = 0, sumY = 0, count = 0;
        for (let row = 0; row < h; row++) {
          for (let col = 0; col < w; col++) {
            if (mask[row * w + col]) {
              sumX += col;
              sumY += row;
              count++;
            }
          }
        }
        if (count === 0) continue;
        const cx = sumX / count;
        const cy = sumY / count;

        // Filter: only keep colonies inside the spiral circle
        const dx = cx - cfg.center_x;
        const dy = cy - cfg.center_y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > cfg.radius) continue;

        // Assign sector
        const numQ = cfg.num_quadrants || 4;
        const numS = cfg.num_sectors || 6;
        const angle = ((Math.atan2(-dy, dx) * 180) / Math.PI + 360) % 360;
        const qIdx = Math.floor(angle / (360 / numQ));
        const r0 = cfg.radius * 0.08;
        const rUsable = cfg.radius - r0;
        let sIdx;
        if (dist < r0) {
          sIdx = numS;
        } else {
          const normalized = (dist - r0) / rUsable;
          const ring = Math.min(Math.floor(normalized * numS), numS - 1);
          sIdx = numS - ring;
        }
        const qLabel = numQ === 4 ? ((qIdx === 0 || qIdx === 2) ? 'A' : 'B') : String.fromCharCode(65 + qIdx);

        detectedColonies.push({
          obj_id: `sam2_${colonyIdx}`,
          centroid_x: Math.round(cx * 10) / 10,
          centroid_y: Math.round(cy * 10) / 10,
          area_pixels: count,
          r: Math.round(dist * 10) / 10,
          theta: Math.round(angle * 10) / 10,
          quadrant: qLabel,
          quadrant_idx: qIdx,
          sector: sIdx,
          score: maskData.score || 0,
          _added: true,
        });
        colonyIdx++;
      }

      enqueueSnackbar(`Detected ${detectedColonies.length} colonies. You can edit the results.`, { variant: 'success' });
      setSpiralAutoColonies(detectedColonies);
      setShowSpiralChart(true);

    } catch (error) {
      console.error('Spiral auto-detect error:', error);
      enqueueSnackbar(`Auto-detection failed: ${error.message}. You can add colonies manually.`, { variant: 'warning' });
      setSpiralAutoColonies([]);
      setShowSpiralChart(true);
    } finally {
      setIsGlobalLoading(false);
      setAutoSegProgress({ inProgress: false, progress: 0, message: '' });
    }
  };

  // Poll a GPU async task until complete; returns the parsed result payload.
  const pollGpuTask = async (taskId, sessionId, onProgress) => {
    const maxAttempts = 60;
    const pollInterval = 30000;
    let attempts = 0;
    let status = null;
    while (attempts < maxAttempts) {
      await new Promise((r) => setTimeout(r, pollInterval));
      const statusResp = await fetch(`${gpuUrl}/task_progress/${taskId}?session_id=${sessionId}`);
      status = await statusResp.json();
      if (onProgress) onProgress(status);
      if (status.stage === 'complete') break;
      if (status.stage === 'error') throw new Error(status.message || 'Task failed.');
      attempts++;
    }
    if (!status || status.stage !== 'complete') throw new Error('Task timed out.');
    const resultResp = await fetch(`${gpuUrl}/task_results/${taskId}?session_id=${sessionId}`);
    return await resultResp.json();
  };

  // Shared: decoded masks -> ObjectManagerPanel objects (populates refs + state, draws on overlay).
  // circleFilter: { cx, cy, r } - if set, drops colonies whose centroid lies outside the circle.
  // Pull species + pipeline + similarity fields out of one raw backend mask
  // payload so importMasksAsObjects can stash them on objectMeta. Returns
  // null when there's nothing useful to record (legacy pipelines).
  const extractMaskMeta = (m) => {
    if (!m || typeof m !== 'object') return null;
    const out = {};
    if (m.species_top1) out.species_top1 = m.species_top1;
    if (typeof m.species_top1_score === 'number') out.species_top1_score = m.species_top1_score;
    if (m.species_scores && typeof m.species_scores === 'object') out.species_scores = m.species_scores;
    if (m.species_top1_v2) out.species_top1_v2 = m.species_top1_v2;
    if (typeof m.species_top1_score_v2 === 'number') out.species_top1_score_v2 = m.species_top1_score_v2;
    if (m.species_scores_v2 && typeof m.species_scores_v2 === 'object') out.species_scores_v2 = m.species_scores_v2;
    if (typeof m.similarity === 'number') out.similarity = m.similarity;
    if (m.pipeline) out.pipeline = m.pipeline;
    return Object.keys(out).length ? out : null;
  };

  // maskMeta (optional, parallel to decodedList): per-mask fields to merge into
  // objectMetaRef so the ObjectManagerPanel can surface them (species_top1,
  // species_top1_score, species_scores, pipeline). Existing callers pass two
  // args and skip species — only ensemble pipelines populate this.
  const importMasksAsObjects = (decodedList, circleFilter = null, maskMeta = null) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);

    const existingIds = Object.keys(objectMasksRef.current).map(Number).filter((n) => !isNaN(n));
    let nextId = existingIds.length ? Math.max(...existingIds) + 1 : 1;
    const newPromptMap = {};
    const newMeta = {};
    let kept = 0;

    for (let mi = 0; mi < decodedList.length; mi++) {
      const decoded = decodedList[mi];
      const extraMeta = maskMeta && maskMeta[mi] ? maskMeta[mi] : null;
      if (!decoded) continue;
      if (circleFilter) {
        // Reject any colony with significant area outside the 90mm circle.
        // Previously a centroid-only check let through colonies whose body
        // extended past the boundary, and broke entirely when the colony
        // mask's coordinate space didn't match the circle's (user-confirmed
        // bug: detections falling completely outside the overlay).
        //
        // We assume both the mask and the circle live in the source image's
        // pixel space — which is how SAM2's `automask` returns its RLE here
        // and how the alignment circle is dragged. If that ever stops being
        // true the rescue is to pass through clampToImage(...).
        const { mask, width, height } = decoded;
        const w = parseInt(width), h = parseInt(height);
        const cxF = circleFilter.cx;
        const cyF = circleFilter.cy;
        const r2 = circleFilter.r * circleFilter.r;
        // Bbox-cropped RLEs (e.g. from /automask with return_bbox_rle=True,
        // which find_similar uses) store decoded coords as 0..bbox_w. Shift
        // by the recorded offset so the dish-circle test runs in the same
        // pixel space as cx/cy/r. Falsy offset == full-frame mask.
        const ox = parseInt(decoded.offsetX) || 0;
        const oy = parseInt(decoded.offsetY) || 0;
        let totalPx = 0;
        let insidePx = 0;
        let sumX = 0, sumY = 0;
        const stride = 1;
        for (let row = 0; row < h; row += stride) {
          for (let col = 0; col < w; col += stride) {
            if (!mask[row * w + col]) continue;
            totalPx++;
            const px = col + ox;
            const py = row + oy;
            sumX += px; sumY += py;
            const dx = px - cxF;
            const dy = py - cyF;
            if (dx * dx + dy * dy <= r2) insidePx++;
          }
        }
        if (totalPx === 0) continue;
        const insideRatio = insidePx / totalPx;
        // Require >=70% of the mask area to lie inside the dish circle.
        // Centroid also has to be inside as a sanity check — catches pathological
        // masks that wrap around the circle edge with only ~half their pixels in.
        if (insideRatio < 0.7) continue;
        const cxMask = sumX / totalPx;
        const cyMask = sumY / totalPx;
        const cdx = cxMask - cxF, cdy = cyMask - cyF;
        if (cdx * cdx + cdy * cdy > r2) continue;
      }
      const objId = String(nextId++);
      objectMasksRef.current[objId] = decoded;
      const baseMeta = { name: `Object ${objId}`, ...(extraMeta || {}) };
      if (!objectMetaRef.current[objId]) {
        objectMetaRef.current[objId] = baseMeta;
      } else if (extraMeta) {
        objectMetaRef.current[objId] = { ...objectMetaRef.current[objId], ...extraMeta };
      }
      if (!promptMapRef.current[objId]) promptMapRef.current[objId] = { points: [], labels: [] };
      if (!objectIds.includes(+objId)) setObjectIds((ids) => [...ids, +objId]);
      if (canvas) drawMaskOnCanvas(canvas, decoded, parseInt(objId));
      newPromptMap[objId] = { points: [], labels: [] };
      newMeta[objId] = baseMeta;
      kept++;
    }

    if (kept > 0) {
      setPromptMapCompat((prev) => ({ ...prev, ...newPromptMap }));
      setObjectMeta((prev) => ({ ...prev, ...newMeta }));
      bumpUi();
    }
    return kept;
  };

  // ── 90mm flow: circular ROI -> automask -> ObjectManagerPanel ──
  const handle90mmConfirm = async () => {
    const cx = spiralCircle.cx, cy = spiralCircle.cy, r = spiralCircle.radius;
    setCountMode(null);
    if (!r) {
      enqueueSnackbar('Please align the circle over the dish first.', { variant: 'error' });
      return;
    }
    const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (!sessionId) {
      enqueueSnackbar('No session ID available.', { variant: 'error' });
      return;
    }
    if (!await checkSessionReadiness(sessionId)) return;

    const roi = {
      x: Math.max(0, Math.round(cx - r)),
      y: Math.max(0, Math.round(cy - r)),
      w: Math.round(r * 2),
      h: Math.round(r * 2),
    };

    // Morphological pipeline: open the live-preview sliders dialog instead
    // of immediately submitting an automask task. The dialog's Run handler
    // will dispatch the automask call with the user's chosen params.
    if (colonyPipeline === 'morphological') {
      setMorphDialogState({ mode: '90mm', roi, dishMeta: { cx, cy, r } });
      return;
    }
    // Find-All-Similar: prompt the user to pick an exemplar before dispatch.
    if (colonyPipeline === 'find_similar') {
      setFindSimilarDialogState({ mode: '90mm', roi, dishMeta: { cx, cy, r } });
      return;
    }

    try {
      setIsGlobalLoading(true);
      setAutoSegProgress({ inProgress: true, progress: 0, message: 'Detecting colonies...' });
      const startResp = await fetch(`${gpuUrl}/automask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          frame_idx: toBackendIndex(currentIndex),
          roi,
          ...(colonyPipeline ? { pipeline: colonyPipeline } : {}),
        }),
      });
      const startJson = await startResp.json();
      if (!startResp.ok) throw new Error(startJson.error || 'Failed to start automask.');
      const result = await pollGpuTask(startJson.task_id, sessionId, (s) => {
        setAutoSegProgress({
          inProgress: s.stage !== 'complete' && s.stage !== 'error',
          progress: typeof s.progress === 'number' ? s.progress : 0,
          message: s.message || s.stage || 'Processing...',
        });
      });
      const masks = result?.result?.masks || [];
      if (masks.length === 0) {
        enqueueSnackbar('No colonies detected. You can click to add colonies manually.', { variant: 'warning' });
        return;
      }
      const decodedList = masks.map((m) => decodeCOCORLE({ counts: m.counts, size: m.size }));
      const maskMeta = masks.map((m) => extractMaskMeta(m));
      const kept = importMasksAsObjects(decodedList, { cx, cy, r }, maskMeta);
      enqueueSnackbar(`Detected ${kept} colonies. Use the panel to edit.`, { variant: 'success' });
    } catch (err) {
      console.error('90mm auto-detect error:', err);
      enqueueSnackbar(`Auto-detection failed: ${err.message}.`, { variant: 'error' });
    } finally {
      setIsGlobalLoading(false);
      setAutoSegProgress({ inProgress: false, progress: 0, message: '' });
    }
  };

  // ── Rectangular flow: grid-tiled automask -> ObjectManagerPanel ──
  const handleRectangularConfirm = async () => {
    const { x, y, w, h } = rectROI;
    const { horizontals, verticals } = rectGrid;
    setCountMode(null);
    if (w <= 0 || h <= 0) {
      enqueueSnackbar('Please align the rectangle over the dish first.', { variant: 'error' });
      return;
    }
    const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (!sessionId) {
      enqueueSnackbar('No session ID available.', { variant: 'error' });
      return;
    }
    if (!await checkSessionReadiness(sessionId)) return;

    // Build cells from gridlines. Column x-edges = [x, ...verticals (sorted), x+w];
    // row y-edges similarly. Clip to ROI bounds and skip degenerate cells.
    const xs = [x, ...verticals.slice().sort((a, b) => a - b), x + w]
      .map((v) => Math.max(x, Math.min(x + w, Math.round(v))));
    const ys = [y, ...horizontals.slice().sort((a, b) => a - b), y + h]
      .map((v) => Math.max(y, Math.min(y + h, Math.round(v))));
    const cells = [];
    for (let i = 0; i < ys.length - 1; i++) {
      for (let j = 0; j < xs.length - 1; j++) {
        const cw = xs[j + 1] - xs[j];
        const ch = ys[i + 1] - ys[i];
        if (cw > 2 && ch > 2) cells.push({ x: xs[j], y: ys[i], w: cw, h: ch });
      }
    }
    if (cells.length === 0) {
      enqueueSnackbar('No valid grid cells. Check gridline positions.', { variant: 'error' });
      return;
    }

    // Morphological pipeline: open sliders dialog before running. We pass the
    // rect ROI (not the per-cell grid) since the dialog operates on the dish
    // as a whole; the run handler decides grid vs single based on stored mode.
    if (colonyPipeline === 'morphological') {
      setMorphDialogState({ mode: 'rectangular', cells, roi: { x, y, w, h } });
      return;
    }
    if (colonyPipeline === 'find_similar') {
      setFindSimilarDialogState({ mode: 'rectangular', cells, roi: { x, y, w, h } });
      return;
    }

    try {
      setIsGlobalLoading(true);
      setAutoSegProgress({ inProgress: true, progress: 0, message: `Detecting colonies across ${cells.length} cells...` });
      const startResp = await fetch(`${gpuUrl}/automask_grid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          frame_idx: toBackendIndex(currentIndex),
          cells,
          ...(colonyPipeline ? { pipeline: colonyPipeline } : {}),
        }),
      });
      const startJson = await startResp.json();
      if (!startResp.ok) throw new Error(startJson.error || 'Failed to start grid automask.');
      const result = await pollGpuTask(startJson.task_id, sessionId, (s) => {
        setAutoSegProgress({
          inProgress: s.stage !== 'complete' && s.stage !== 'error',
          progress: typeof s.progress === 'number' ? s.progress : 0,
          message: s.message || s.stage || 'Processing...',
        });
      });
      const masks = result?.result?.masks || [];
      if (masks.length === 0) {
        enqueueSnackbar('No colonies detected. You can click to add colonies manually.', { variant: 'warning' });
        return;
      }
      // Backend returns bbox-cropped RLEs with {bbox: [x, y, w, h], full_size: [H, W]}.
      // Decode into small bbox-sized masks and carry the offset through so
      // drawMaskOnCanvas / pickObjectAtCoords can locate them on the full canvas.
      const decodedList = [];
      const maskMeta = [];
      for (const m of masks) {
        const d = decodeCOCORLE({ counts: m.counts, size: m.size });
        if (!d) continue;
        if (Array.isArray(m.bbox) && Array.isArray(m.full_size)) {
          d.offsetX = m.bbox[0];
          d.offsetY = m.bbox[1];
          d.fullWidth = m.full_size[1];
          d.fullHeight = m.full_size[0];
        }
        decodedList.push(d);
        maskMeta.push(extractMaskMeta(m));
      }
      const kept = importMasksAsObjects(decodedList, null, maskMeta);
      enqueueSnackbar(`Detected ${kept} colonies across ${cells.length} cells.`, { variant: 'success' });
    } catch (err) {
      console.error('Rectangular auto-detect error:', err);
      enqueueSnackbar(`Auto-detection failed: ${err.message}.`, { variant: 'error' });
    } finally {
      setIsGlobalLoading(false);
      setAutoSegProgress({ inProgress: false, progress: 0, message: '' });
    }
  };

  // Unified confirm handler dispatches on current countMode
  const handleAlignConfirm = () => {
    if (countMode === 'spiral') handleSpiralAlignConfirm();
    else if (countMode === '90mm') handle90mmConfirm();
    else if (countMode === 'rectangular') handleRectangularConfirm();
  };

  // Draw spiral alignment overlay on the canvas
  const drawSpiralOverlay = useCallback(() => {
    if (!spiralAlignMode) return;
    const overlayCtx = frameCanvasRef.current?.getOverlayContext();
    if (!overlayCtx) return;
    const canvas = frameCanvasRef.current?.overlayCanvas;
    if (!canvas) return;

    overlayCtx.clearRect(0, 0, canvas.width, canvas.height);
    const { cx, cy, radius } = spiralCircle;
    const isSpiral = countMode === 'spiral';
    const numSectors = spiralPlateConfig.num_sectors || 6;
    const numQuadrants = spiralPlateConfig.num_quadrants || 4;
    const deadZone = radius * 0.08;

    // Semi-transparent fill outside plate circle
    overlayCtx.save();
    overlayCtx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    overlayCtx.fillRect(0, 0, canvas.width, canvas.height);
    overlayCtx.globalCompositeOperation = 'destination-out';
    overlayCtx.beginPath();
    overlayCtx.arc(cx, cy, radius, 0, 2 * Math.PI);
    overlayCtx.fill();
    overlayCtx.restore();

    // Plate circle outline
    overlayCtx.beginPath();
    overlayCtx.arc(cx, cy, radius, 0, 2 * Math.PI);
    overlayCtx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    overlayCtx.lineWidth = 3;
    overlayCtx.stroke();

    if (isSpiral) {
      // Concentric sector rings
      for (let s = 1; s < numSectors; s++) {
        const ringR = deadZone + (radius - deadZone) * (s / numSectors);
        overlayCtx.beginPath();
        overlayCtx.arc(cx, cy, ringR, 0, 2 * Math.PI);
        overlayCtx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
        overlayCtx.lineWidth = 1;
        overlayCtx.stroke();
      }

      // Dead zone circle
      overlayCtx.beginPath();
      overlayCtx.arc(cx, cy, deadZone, 0, 2 * Math.PI);
      overlayCtx.strokeStyle = 'rgba(255, 200, 0, 0.5)';
      overlayCtx.lineWidth = 1;
      overlayCtx.setLineDash([6, 4]);
      overlayCtx.stroke();
      overlayCtx.setLineDash([]);

      // Quadrant divider lines
      for (let q = 0; q < numQuadrants; q++) {
        const angle = (2 * Math.PI * q) / numQuadrants;
        overlayCtx.beginPath();
        overlayCtx.moveTo(cx, cy);
        overlayCtx.lineTo(cx + radius * Math.cos(angle), cy - radius * Math.sin(angle));
        overlayCtx.strokeStyle = 'rgba(255, 220, 50, 0.7)';
        overlayCtx.lineWidth = 2;
        overlayCtx.stroke();
      }
    }

    // Center crosshair
    const chSize = Math.max(12, radius * 0.02);
    overlayCtx.beginPath();
    overlayCtx.moveTo(cx - chSize, cy);
    overlayCtx.lineTo(cx + chSize, cy);
    overlayCtx.moveTo(cx, cy - chSize);
    overlayCtx.lineTo(cx, cy + chSize);
    overlayCtx.strokeStyle = 'rgba(255, 80, 80, 0.9)';
    overlayCtx.lineWidth = 2;
    overlayCtx.stroke();
  }, [spiralAlignMode, countMode, spiralCircle, spiralPlateConfig.num_sectors, spiralPlateConfig.num_quadrants]);

  useEffect(() => {
    drawSpiralOverlay();
  }, [drawSpiralOverlay]);

  // ── Rectangular alignment overlay ──
  const drawRectangularOverlay = useCallback(() => {
    if (!rectAlignMode) return;
    const overlayCtx = frameCanvasRef.current?.getOverlayContext();
    if (!overlayCtx) return;
    const canvas = frameCanvasRef.current?.overlayCanvas;
    if (!canvas) return;

    overlayCtx.clearRect(0, 0, canvas.width, canvas.height);
    const { x, y, w, h } = rectROI;

    // Dim outside the ROI
    overlayCtx.save();
    overlayCtx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    overlayCtx.fillRect(0, 0, canvas.width, canvas.height);
    overlayCtx.clearRect(x, y, w, h);
    overlayCtx.restore();

    // ROI border
    overlayCtx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    overlayCtx.lineWidth = 3;
    overlayCtx.strokeRect(x, y, w, h);

    // Gridlines
    overlayCtx.strokeStyle = 'rgba(255, 220, 50, 0.75)';
    overlayCtx.lineWidth = 2;
    for (const gy of rectGrid.horizontals) {
      overlayCtx.beginPath();
      overlayCtx.moveTo(x, gy);
      overlayCtx.lineTo(x + w, gy);
      overlayCtx.stroke();
    }
    for (const gx of rectGrid.verticals) {
      overlayCtx.beginPath();
      overlayCtx.moveTo(gx, y);
      overlayCtx.lineTo(gx, y + h);
      overlayCtx.stroke();
    }

    // Corner handles
    const hs = 12;
    overlayCtx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    const corners = [
      [x, y], [x + w, y], [x, y + h], [x + w, y + h],
    ];
    for (const [cx, cy] of corners) {
      overlayCtx.fillRect(cx - hs / 2, cy - hs / 2, hs, hs);
    }
  }, [rectAlignMode, rectROI, rectGrid]);

  useEffect(() => {
    drawRectangularOverlay();
  }, [drawRectangularOverlay]);

  // Rectangular mouse handlers: drag gridlines, corners (resize), or interior (move).
  const handleRectMouseDown = useCallback((coords) => {
    if (!rectAlignMode) return;
    const { x, y, w, h } = rectROI;
    const { horizontals, verticals } = rectGrid;
    const threshold = 12;

    // Check corners first
    const corners = [
      { name: 'nw', cx: x, cy: y },
      { name: 'ne', cx: x + w, cy: y },
      { name: 'sw', cx: x, cy: y + h },
      { name: 'se', cx: x + w, cy: y + h },
    ];
    for (const c of corners) {
      if (Math.abs(coords.x - c.cx) < threshold && Math.abs(coords.y - c.cy) < threshold) {
        rectDragRef.current = {
          active: true, mode: `resize-${c.name}`,
          startX: coords.x, startY: coords.y,
          orig: { x, y, w, h },
        };
        return;
      }
    }

    // Check gridlines (only within ROI bounds)
    if (coords.x >= x && coords.x <= x + w && coords.y >= y && coords.y <= y + h) {
      for (let i = 0; i < horizontals.length; i++) {
        if (Math.abs(coords.y - horizontals[i]) < threshold) {
          rectDragRef.current = { active: true, mode: 'line-h', lineIndex: i, startX: coords.x, startY: coords.y, orig: horizontals[i] };
          return;
        }
      }
      for (let i = 0; i < verticals.length; i++) {
        if (Math.abs(coords.x - verticals[i]) < threshold) {
          rectDragRef.current = { active: true, mode: 'line-v', lineIndex: i, startX: coords.x, startY: coords.y, orig: verticals[i] };
          return;
        }
      }
      // Interior -> move rect (and gridlines follow)
      rectDragRef.current = {
        active: true, mode: 'move',
        startX: coords.x, startY: coords.y,
        orig: { x, y, w, h, horizontals: [...horizontals], verticals: [...verticals] },
      };
    }
  }, [rectAlignMode, rectROI, rectGrid]);

  const handleRectMouseMove = useCallback((coords) => {
    if (!rectAlignMode || !rectDragRef.current.active) return;
    const { mode, startX, startY, orig, lineIndex } = rectDragRef.current;
    const dx = coords.x - startX;
    const dy = coords.y - startY;

    if (mode === 'line-h') {
      const { y, h } = rectROI;
      const newY = Math.max(y + 2, Math.min(y + h - 2, Math.round(orig + dy)));
      setRectGrid((g) => {
        const hs = [...g.horizontals];
        hs[lineIndex] = newY;
        return { ...g, horizontals: hs };
      });
    } else if (mode === 'line-v') {
      const { x, w } = rectROI;
      const newX = Math.max(x + 2, Math.min(x + w - 2, Math.round(orig + dx)));
      setRectGrid((g) => {
        const vs = [...g.verticals];
        vs[lineIndex] = newX;
        return { ...g, verticals: vs };
      });
    } else if (mode === 'move') {
      const nx = Math.round(orig.x + dx);
      const ny = Math.round(orig.y + dy);
      setRectROI({ x: nx, y: ny, w: orig.w, h: orig.h });
      setRectGrid((g) => ({
        ...g,
        horizontals: orig.horizontals.map((v) => v + (ny - orig.y)),
        verticals: orig.verticals.map((v) => v + (nx - orig.x)),
      }));
    } else if (mode?.startsWith('resize-')) {
      let { x, y, w, h } = orig;
      const corner = mode.slice(7);
      if (corner === 'nw') { x = orig.x + dx; y = orig.y + dy; w = orig.w - dx; h = orig.h - dy; }
      else if (corner === 'ne') { y = orig.y + dy; w = orig.w + dx; h = orig.h - dy; }
      else if (corner === 'sw') { x = orig.x + dx; w = orig.w - dx; h = orig.h + dy; }
      else if (corner === 'se') { w = orig.w + dx; h = orig.h + dy; }
      w = Math.max(40, w); h = Math.max(40, h);
      setRectROI({ x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) });
      // Re-distribute gridlines evenly within the new rect
      setRectGrid((g) => {
        const verticals = [];
        for (let i = 1; i < g.cols; i++) verticals.push(Math.round(x + (w * i) / g.cols));
        const horizontals = [];
        for (let i = 1; i < g.rows; i++) horizontals.push(Math.round(y + (h * i) / g.rows));
        return { ...g, horizontals, verticals };
      });
    }
  }, [rectAlignMode, rectROI]);

  const handleRectMouseUp = useCallback(() => {
    rectDragRef.current.active = false;
  }, []);

  // Change grid size: redistribute lines evenly within current ROI.
  const setGridSize = (rows, cols) => {
    const { x, y, w, h } = rectROI;
    const verticals = [];
    for (let i = 1; i < cols; i++) verticals.push(Math.round(x + (w * i) / cols));
    const horizontals = [];
    for (let i = 1; i < rows; i++) horizontals.push(Math.round(y + (h * i) / rows));
    setRectGrid({ rows, cols, horizontals, verticals });
  };

  const addHorizontalLine = () => {
    setRectGrid((g) => {
      const hs = [...g.horizontals, rectROI.y + Math.round(rectROI.h / 2)].sort((a, b) => a - b);
      return { ...g, horizontals: hs, rows: hs.length + 1 };
    });
  };
  const addVerticalLine = () => {
    setRectGrid((g) => {
      const vs = [...g.verticals, rectROI.x + Math.round(rectROI.w / 2)].sort((a, b) => a - b);
      return { ...g, verticals: vs, cols: vs.length + 1 };
    });
  };

  // Spiral alignment mouse handlers
  const handleSpiralMouseDown = useCallback((coords) => {
    if (!spiralAlignMode) return;
    const { cx, cy, radius } = spiralCircle;
    const dx = coords.x - cx;
    const dy = coords.y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const edgeThreshold = Math.max(30, radius * 0.04);

    if (Math.abs(dist - radius) < edgeThreshold) {
      spiralDragRef.current = { active: true, mode: 'resize', startX: coords.x, startY: coords.y, origCx: cx, origCy: cy, origR: radius };
    } else if (dist < radius) {
      spiralDragRef.current = { active: true, mode: 'move', startX: coords.x, startY: coords.y, origCx: cx, origCy: cy, origR: radius };
    }
  }, [spiralAlignMode, spiralCircle]);

  const handleSpiralMouseMove = useCallback((coords) => {
    if (!spiralAlignMode || !spiralDragRef.current.active) return;
    const { mode, startX, startY, origCx, origCy, origR } = spiralDragRef.current;
    if (mode === 'move') {
      setSpiralCircle({
        cx: Math.round(origCx + (coords.x - startX)),
        cy: Math.round(origCy + (coords.y - startY)),
        radius: origR,
      });
    } else if (mode === 'resize') {
      const dx = coords.x - origCx;
      const dy = coords.y - origCy;
      const newR = Math.max(50, Math.round(Math.sqrt(dx * dx + dy * dy)));
      setSpiralCircle({ cx: origCx, cy: origCy, radius: newR });
    }
  }, [spiralAlignMode]);

  const handleSpiralMouseUp = useCallback(() => {
    spiralDragRef.current.active = false;
  }, []);

  const handleSpiralWheel = useCallback((coords, e) => {
    if (!spiralAlignMode) return;
    const delta = e.deltaY > 0 ? -15 : 15;
    setSpiralCircle((prev) => ({
      ...prev,
      radius: Math.max(50, prev.radius + delta),
    }));
  }, [spiralAlignMode]);

  const handleSpiralApiCall = async (frameIdx) => {
    try {
      const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
      if (!sessionId) throw new Error('No session ID available');

      let taskId = bgAnalysisTasks.spiral;
      if (!taskId) {
        let resp;
        try {
          resp = await fetch(`${cpuUrl}/spiral_analysis`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              session_id: sessionId,
              frame_indices: [frameIdx],
              s3_path: initialPath || (multiPaths.length > 0 ? multiPaths[0] : ''),
              plate_config: {
                ...spiralPlateConfig,
                use_image_detection: true,
                detection_params: {
                  sensitivity: spiralPlateConfig.sensitivity || 'medium',
                  min_area: spiralPlateConfig.sensitivity === 'high' ? 5 : spiralPlateConfig.sensitivity === 'low' ? 50 : 10,
                },
              },
            }),
          });
        } catch {
          throw new Error('Spiral analysis server is unreachable. Please contact your administrator.');
        }
        if (resp.status >= 500) {
          throw new Error('Analysis server is currently unavailable. Please contact your administrator.');
        }
        if (!resp.ok) {
          const err = await resp.text();
          throw new Error(`Spiral analysis failed: ${resp.status} ${err}`);
        }
        const d = await resp.json();
        taskId = d.task_id;
      }

      const result = await pollCpuTask(taskId);
      if (!result.data_url) throw new Error('No data URL in spiral analysis result');

      const dataResponse = await fetch(result.data_url);
      if (!dataResponse.ok) throw new Error(`Failed to fetch spiral data: ${dataResponse.status}`);
      return await dataResponse.json();

    } catch (error) {
      const msg = error.message || '';
      if (msg.includes('Failed to fetch') || msg.includes('ERR_NETWORK')) {
        throw new Error('Network connection failed. Please check your internet connection.');
      }
      throw error;
    }
  };

  // Function to call pigmentation API
  const handlePigmentationApiCall = async () => {
    const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (!sessionId) throw new Error('No session ID available');

    let taskId = bgAnalysisTasks.pigmentation;
    if (!taskId) {
      let resp;
      try {
        resp = await fetch(`${cpuUrl}/pigmentation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            s3_path: initialPath || (multiPaths.length > 0 ? multiPaths[0] : ''),
          })
        });
      } catch {
        throw new Error('Color analysis server is unreachable. Please contact your administrator.');
      }
      if (resp.status >= 500) {
        throw new Error('Color analysis server is currently unavailable. Please contact your administrator.');
      }
      if (!resp.ok) throw new Error(`Color analysis request failed (${resp.status})`);
      const d = await resp.json();
      taskId = d.task_id;
    }

    const result = await pollCpuTask(taskId);
    if (!result.data_url) throw new Error('No data URL in color analysis result');
    return result;
  };

  // Function to show pigmentation analysis chart
  const handlePigmentationAnalysis = async () => {
    try {
      setShowPigmentationChart(true);
    } catch (error) {
      console.error('Error starting pigmentation analysis:', error);
      enqueueSnackbar('Failed to start pigmentation analysis.', { variant: 'error' });
    }
  };

  // Function to show HSV visualization explorer
  const handleHSVVisualization = async () => {
    try {
      setShowHSVVisualization(true);
    } catch (error) {
      console.error('Error starting HSV visualization:', error);
      enqueueSnackbar('Failed to start color visualization.', { variant: 'error' });
    }
  };

  // ─── Masked Video: check S3 → generate if missing → download ───
  const fetchOrGenerateMaskedVideo = useCallback(async (sessionId, bucketName) => {
    if (!sessionId) {
      enqueueSnackbar('No session found for this analysis.', { variant: 'warning' });
      return;
    }
    await ensureClients();

    const videoKey = `Analysis/${sessionId}/videos/masked_overlay.mp4`;

    // Step 1: Try to download from S3
    setMaskedVideoState({ status: 'checking' });
    try {
      await s3.headObject({ Bucket: bucketName || 'petriboothproject', Key: videoKey }).promise();
      // Video exists — download it
      setMaskedVideoState({ status: 'downloading' });
      const data = await s3.getObject({ Bucket: bucketName || 'petriboothproject', Key: videoKey }).promise();
      const blob = new Blob([data.Body], { type: 'video/mp4' });
      const blobUrl = URL.createObjectURL(blob);
      setMaskedVideoUrl(blobUrl);
      setMaskedVideoState({ status: 'ready' });
      return blobUrl;
    } catch {
      // Video doesn't exist — generate it
    }

    // Step 2: Trigger generation via CPU service
    setMaskedVideoState({ status: 'generating', progress: 0, message: 'Starting video generation...' });
    try {
      const genResp = await fetch(`${cpuUrl}/video/generate_video?session_id=${sessionId}`, { method: 'POST' });
      if (!genResp.ok) throw new Error(`Generate video failed: ${genResp.status}`);
      const genData = await genResp.json();
      const taskId = genData.task_id;
      if (!taskId) throw new Error('No task_id returned');

      // Step 3: Poll for progress
      let complete = false;
      while (!complete) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const progResp = await fetch(`${cpuUrl}/video/task_progress/${taskId}`);
          const prog = await progResp.json();
          if (prog.stage === 'complete') {
            complete = true;
          } else if (prog.stage === 'error') {
            throw new Error(prog.message || 'Video generation failed');
          } else {
            setMaskedVideoState({
              status: 'generating',
              progress: prog.progress || 0,
              message: prog.message || 'Generating video...',
            });
          }
        } catch (pollErr) {
          if (pollErr.message?.includes('Video generation failed')) throw pollErr;
          // network blip — retry
        }
      }

      // Step 4: Download the generated video
      setMaskedVideoState({ status: 'downloading' });
      const data = await s3.getObject({ Bucket: bucketName || 'petriboothproject', Key: videoKey }).promise();
      const blob = new Blob([data.Body], { type: 'video/mp4' });
      const blobUrl = URL.createObjectURL(blob);
      setMaskedVideoUrl(blobUrl);
      setMaskedVideoState({ status: 'ready' });
      return blobUrl;
    } catch (err) {
      console.error('Masked video generation failed:', err);
      enqueueSnackbar('Failed to generate masked video. Please try again later.', { variant: 'error' });
      setMaskedVideoState({ status: 'idle' });
      return null;
    }
  }, [cpuUrl, enqueueSnackbar]);

  const handleDownloadMaskedVideoUrl = useCallback((blobUrl, filename) => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'masked_video.mp4';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  // Slugify the analysis name into something safe to embed in an S3 key.
  // Spaces collapse to "_"; anything outside [A-Za-z0-9._-] gets stripped.
  // Names that collapse to empty fall back to a session-time-based slug so
  // the save still succeeds with a deterministic key.
  const slugifyAnalysisId = (raw) => {
    const cleaned = String(raw || '')
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^A-Za-z0-9._-]/g, '');
    return cleaned || `save_${Date.now()}`;
  };

  // Copy every object under the live session prefix into a per-save snapshot
  // prefix so two saves inside one session do not overwrite each other. The
  // live prefix continues to receive new propagation + analysis writes as the
  // user redoes masks; each save freezes a snapshot keyed by the analysis id.
  //
  // Excludes the existing saves/ subtree so back-to-back saves do not copy
  // earlier snapshots into the current one. Returns the snapshot prefix that
  // callers should record in DynamoDB s3_location.
  const snapshotSessionToAnalysisPrefix = async (bucket, sessionId, analysisIdSlug) => {
    const livePrefix = `Analysis/${sessionId}/`;
    const snapPrefix = `Analysis/${sessionId}/saves/${analysisIdSlug}/`;
    let token = null;
    const liveKeys = [];
    do {
      const res = await s3.listObjectsV2({
        Bucket: bucket,
        Prefix: livePrefix,
        ...(token ? { ContinuationToken: token } : {}),
      }).promise();
      (res.Contents || []).forEach((o) => {
        // Skip anything under saves/ — previous snapshots stay in place.
        const rel = o.Key.substring(livePrefix.length);
        if (!rel || rel.startsWith('saves/')) return;
        liveKeys.push({ key: o.Key, rel });
      });
      token = res.IsTruncated ? res.NextContinuationToken : null;
    } while (token);

    // S3 copyObject limit is 5 GB; everything Mira writes per-frame is small,
    // so a plain copy is fine. Run them in small batches so a session with
    // hundreds of frames does not spawn hundreds of in-flight requests.
    const BATCH = 16;
    for (let i = 0; i < liveKeys.length; i += BATCH) {
      const batch = liveKeys.slice(i, i + BATCH);
      await Promise.all(batch.map(({ key, rel }) =>
        s3.copyObject({
          Bucket: bucket,
          // CopySource format is "<bucket>/<key>" with the key encoded but
          // the slashes inside the key preserved. encodeURIComponent on the
          // whole string converts "/" into "%2F" which AWS interprets as a
          // single flat key under the wrong bucket and the copy silently
          // resolves to a missing source — the bug the first snapshot
          // rollout shipped with. Encode only "+" and "?" which are the
          // characters S3 keys can contain that break the header.
          CopySource: `${bucket}/${key}`
            .replace(/\+/g, '%2B')
            .replace(/\?/g, '%3F'),
          Key: `${snapPrefix}${rel}`,
        }).promise().catch((e) => {
          // One bad object should not torpedo the whole snapshot.
          console.warn(`snapshot copy failed for ${key}:`, e?.code || e?.message);
        })
      ));
    }
    return snapPrefix;
  };

  // ─── Save Analysis (register in DynamoDB – backend already saved data to S3) ───
  const handleSaveAnalysis = async () => {
    // Validate before we do any async work so the dialog never closes on a no-op.
    if (!saveAnalysisName.trim()) {
      enqueueSnackbar('Please enter a name for this analysis.', { variant: 'warning' });
      return;
    }
    const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
    if (!sessionId) {
      enqueueSnackbar('Unable to save. Please reload and try again.', { variant: 'error' });
      return;
    }
    const pathParts = (path || '').split('/');
    const devBucket = deviceBucketParam || sessionStorage.getItem('device_bucket') || pathParts[0] || '';
    if (!devBucket) {
      enqueueSnackbar('Cannot determine device. Please try again.', { variant: 'error' });
      return;
    }

    // Lock the UI immediately: close the dialog and disable the toolbar button
    // so the user gets unambiguous feedback that the click registered. Anything
    // that fails below now reports via snackbar instead of leaving a stuck dialog.
    setIsSaving(true);
    setShowSaveDialog(false);

    // The live working prefix every backend write lands in. Snapshot is built
    // below and overrides s3Location for DynamoDB so each save is independent.
    const livePrefix = `Analysis/${sessionId}/`;
    let s3Location = livePrefix;

    // ensureClients() can hang if Cognito refresh stalls — bound it.
    try {
      await Promise.race([
        ensureClients(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('AWS client init timed out')), 15000),
        ),
      ]);
    } catch (e) {
      setIsSaving(false);
      enqueueSnackbar(`Failed to save analysis: ${e.message}`, { variant: 'error' });
      return;
    }

    // Wait (briefly) for precompute to finish if still running. Bounded so a
    // wedged CPU task can never block save indefinitely; precompute results
    // are cached in S3 and re-derivable on demand, so saving the metadata
    // record without them is safe.
    if (precomputeTaskId && !precomputeDone) {
      enqueueSnackbar('Finishing background analyses before saving...', { variant: 'info' });
      const deadline = Date.now() + 60 * 1000;
      while (Date.now() < deadline) {
        try {
          const r = await fetch(`${cpuUrl}/task_progress/${precomputeTaskId}`);
          if (!r.ok) { setPrecomputeDone(true); break; }
          const prog = await r.json();
          if (prog.stage === 'complete' || prog.stage === 'error') {
            setPrecomputeDone(true);
            break;
          }
        } catch { break; }
        await new Promise(r => setTimeout(r, 3000));
      }
    }

    try {
      const now = new Date().toISOString();
      const storedDevice = JSON.parse(localStorage.getItem('selectedDevice') || '{}');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const groupId = userData['custom:bucket']
        ? `${userData['custom:bucket']}_users`
        : storedDevice.MIRA_group_id || '';
      const bucket = process.env.REACT_APP_USER_BUCKET;
      const analysisIdSlug = slugifyAnalysisId(saveAnalysisName);

      // Snapshot the current state of the live session prefix into a
      // dedicated per-save subtree so a redo + save in the same session
      // does not clobber the previous save's data. Best-effort: if the
      // snapshot fails, fall back to the live prefix so the row is at
      // least restorable from the live state.
      try {
        s3Location = await snapshotSessionToAnalysisPrefix(
          bucket, sessionId, analysisIdSlug,
        );
      } catch (snapErr) {
        console.warn(
          'Snapshot to per-save prefix failed; falling back to live prefix:',
          snapErr?.code || snapErr?.message,
        );
        s3Location = livePrefix;
      }

      // Ground frame_count + object_count on the snapshot's S3 reality, not
      // on in-memory React state. propagatedMasks / objectIds can be sparse
      // at click time (e.g. user clicked Save before fetchPropagatedMasks
      // repopulated state after a propagation run). Listing the snapshot is
      // a single fast S3 call and removes the timing dependency that caused
      // analyses to record frame_count=1 / object_count=0.
      let snapshotFrameCount = Object.keys(propagatedMasks).length;
      let snapshotObjectCount = objectIds.length;
      try {
        let token = null;
        const rleKeys = [];
        do {
          const res = await s3.listObjectsV2({
            Bucket: bucket,
            Prefix: `${s3Location}rle_data/`,
            ...(token ? { ContinuationToken: token } : {}),
          }).promise();
          (res.Contents || []).forEach((o) => {
            if (o.Key.endsWith('.json')) rleKeys.push(o.Key);
          });
          token = res.IsTruncated ? res.NextContinuationToken : null;
        } while (token);
        if (rleKeys.length > 0) snapshotFrameCount = rleKeys.length;
        // Distinct obj_ids across the saved frames. Probe up to 4 frames so
        // we capture objects that appear later than frame 0 without paying
        // the cost of every frame on long timelapses.
        const sample = rleKeys
          .sort()
          .filter((_, i, a) => i === 0 || i === Math.floor(a.length / 2) || i === a.length - 1 || i === Math.floor(a.length / 4))
          .slice(0, 4);
        const objIds = new Set();
        await Promise.all(sample.map(async (k) => {
          try {
            const obj = await s3.getObject({ Bucket: bucket, Key: k }).promise();
            const body = JSON.parse(
              typeof obj.Body === 'string' ? obj.Body : obj.Body.toString('utf8'),
            );
            (body?.objects || []).forEach((o) => objIds.add(o.obj_id));
          } catch { /* skip unreadable */ }
        }));
        if (objIds.size > 0) snapshotObjectCount = objIds.size;
      } catch (countErr) {
        console.warn(
          'Falling back to in-memory frame/object counts:',
          countErr?.code || countErr?.message,
        );
      }

      await Promise.race([
        dynamoDB.put({
          TableName: 'Analysis_folders',
          Item: {
            device_bucket: devBucket,
            analysis_id: saveAnalysisName.trim(),
            MIRA_group_id: groupId,
            device_name: storedDevice.device_name || sessionStorage.getItem('device_name') || '',
            analysis_name: saveAnalysisName.trim(),
            s3_location: s3Location,
            s3_bucket: bucket,
            source_path: path || '',
            created_at: now,
            updated_at: now,
            frame_count: snapshotFrameCount,
            measurement_count: 0,
            object_count: snapshotObjectCount,
            thumbnail_key: '',
            status: 'active',
            mode: 'beta',
            session_id: sessionId
          }
        }).promise(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('DynamoDB write timed out')), 20000),
        ),
      ]);

      // Persist the per-object metadata (user-given names + custom colors +
      // species labels) into the snapshot prefix so restore + compare read
      // exactly the meta that was current at save time. Live-prefix writes
      // would get overwritten by the next save inside the same session.
      try {
        const objectMetaSerializable = Object.fromEntries(
          Object.entries(objectMetaRef.current || {}).map(([k, v]) => [
            String(k),
            {
              name: v?.name || `Object ${k}`,
              customColor: v?.customColor || null,
              species_top1: v?.species_top1 || null,
              species_top1_score: typeof v?.species_top1_score === 'number' ? v.species_top1_score : null,
              species_scores: v?.species_scores || null,
              species_top1_v2: v?.species_top1_v2 || null,
              species_top1_score_v2: typeof v?.species_top1_score_v2 === 'number' ? v.species_top1_score_v2 : null,
              species_scores_v2: v?.species_scores_v2 || null,
              pipeline: v?.pipeline || null,
            },
          ]),
        );
        await s3.putObject({
          Bucket: bucket,
          Key: `${s3Location}object_meta.json`,
          Body: JSON.stringify(objectMetaSerializable),
          ContentType: 'application/json',
        }).promise();
      } catch (metaErr) {
        console.error('Failed to write object_meta.json (analysis still saved):', metaErr);
      }

      enqueueSnackbar(`Analysis "${saveAnalysisName.trim()}" saved successfully!`, {
        variant: 'success',
        autoHideDuration: 4000
      });
    } catch (error) {
      console.error('Failed to save analysis:', error);
      enqueueSnackbar(`Failed to save analysis: ${error.message}`, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // isEnding and handleEndSession are provided by useEC2Session hook
  const isEnding = isEndingSession;
  const handleEndSession = endSession;

  // Helper: pick an icon label based on file extension
  const getFileIcon = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) return 'VID';
    if (['csv', 'tsv', 'xls', 'xlsx'].includes(ext)) return 'CSV';
    if (['json'].includes(ext)) return 'JSON';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff'].includes(ext)) return 'IMG';
    if (['pdf'].includes(ext)) return 'PDF';
    return 'FILE';
  };


  if (!framesReady && !analysisRestoreMode) {
    const serverFailed = !settingUpResources && !serverReady;
    const loadingFrames = !settingUpResources && serverReady && !framesReady;
    // A content error (real backend reason, e.g. "No images found for this
    // sample") is distinct from a connectivity failure. Show the real reason
    // and an accurate title instead of a misleading "Connection Failed".
    const contentError = framesError || sessionError;
    const errorMsg = contentError || (serverFailed
      ? 'Could not connect to the analysis server. Please reload the page. If the problem persists, the server may be starting up — try again in a few minutes.'
      : null);
    const errorTitle = errorMsg ? (contentError ? 'Cannot Open Analysis' : 'Connection Failed') : 'Setting Up Analysis';
    const errorSubtitle = errorMsg
      ? (contentError ? 'This experiment or sample could not be opened' : 'The analysis server could not be reached')
      : 'Preparing your experiment for analysis';

    const stageMessage = (() => {
      if (errorMsg) return errorMsg;
      if (loadingFrames) return 'Preparing experiment frames for display...';
      if (sessionProgress.stage === 'connecting_to_server')
        return sessionProgress.message || 'Connecting to analysis server...';
      if (sessionProgress.message) return sessionProgress.message;
      if (sessionProgress.stage === 'running') return 'Preparing experiment...';
      if (sessionProgress.stage === 'initializing_inference') return 'Finalizing session...';
      return 'Preparing your analysis environment...';
    })();

    const progress = loadingFrames ? 99 : (sessionProgress.progress || 0);
    const stages = [
      { label: 'Connecting', done: progress >= 5 || loadingFrames },
      { label: 'Loading Images', done: progress >= 55 || loadingFrames },
      { label: 'Initializing', done: progress >= 97 || loadingFrames },
      { label: 'Loading Frames', done: framesReady },
    ];
    const activeStage = stages.filter(s => !s.done)[0] || stages[stages.length - 1];

    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', bgcolor: '#0a0a0a', px: 3,
      }}>
        <Box sx={{ width: '100%', maxWidth: 520 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, color: errorMsg ? '#ef5350' : '#fff', textAlign: 'center' }}>
            {errorTitle}
          </Typography>
          <Typography variant="body2" sx={{ mb: 4, color: '#666', textAlign: 'center' }}>
            {errorSubtitle}
          </Typography>

          {errorMsg ? (
            <Box sx={{ p: 2, bgcolor: '#1a0000', borderRadius: 2, border: '1px solid #5a1a1a', mb: 2 }}>
              <Typography variant="body2" sx={{ color: '#ef9a9a', fontSize: '0.85rem' }}>
                {errorMsg}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Stage pills */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                {stages.map((s) => (
                  <Box key={s.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: s.done ? '#1976d2' : (s.label === activeStage.label ? '#333' : '#1a1a1a'),
                      border: `2px solid ${s.done ? '#1976d2' : (s.label === activeStage.label ? '#555' : '#2a2a2a')}`,
                      transition: 'all 0.4s',
                    }}>
                      {s.done
                        ? <Typography sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 700 }}>&#x2713;</Typography>
                        : (s.label === activeStage.label
                            ? <CircularProgress size={14} sx={{ color: '#90caf9' }} />
                            : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#333' }} />)}
                    </Box>
                    <Typography variant="caption" sx={{ color: s.done ? '#90caf9' : (s.label === activeStage.label ? '#ccc' : '#444'), whiteSpace: 'nowrap', fontSize: '0.65rem' }}>
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Progress bar */}
              <LinearProgress
                variant={progress > 0 ? 'determinate' : 'indeterminate'}
                value={progress}
                sx={{ height: 8, borderRadius: 4, bgcolor: '#1a1a1a', '& .MuiLinearProgress-bar': { bgcolor: '#1976d2' } }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                <Typography variant="caption" sx={{ color: '#555', fontSize: '0.7rem' }}>
                  {stageMessage}
                </Typography>
                <Typography variant="caption" sx={{ color: '#555', fontSize: '0.7rem' }}>
                  {Math.round(progress)}%
                </Typography>
              </Box>

              {progress > 0 && progress < 99 && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#111', borderRadius: 2, border: '1px solid #1e1e1e' }}>
                  <Typography variant="body2" sx={{ color: '#555', fontSize: '0.75rem' }}>
                    Large experiments with many frames can take 5&#8211;15 minutes to initialize.
                    This is a one-time setup &#8212; subsequent sessions for the same experiment load faster.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  }

  // ─── Restore / Browse Mode ───
  if (analysisRestoreMode) {
    if (!restoreData) {
      return (
        <Box sx={{ mx: 'auto', mt: 10, textAlign: 'center', width: '60%' }}>
          <CircularProgress size={48} />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading saved analysis...</Typography>
        </Box>
      );
    }

    const { analysisName, folderTree, metadata, bucket } = restoreData;

    // Category definitions mapping to S3 folder structure
    // For colony_count saves, surface the Colony Summary tab first. For
    // legacy/timelapse saves it stays hidden unless a phenotypic/results.json
    // happens to exist.
    const isColonyCountSave = metadata.mode === 'colony_count';
    // Single-image colony-count saves have a very different navigation —
    // there's only one frame, time-series doesn't apply, and the user spec
    // (2026-05-28) is: just Area + Color tabs with side-by-side chart +
    // image+masks. Everything else (Colony Summary, Experiment, Objects,
    // Spiral, Texture) is hidden.
    //
    // Don't rely on the URL ?mode=single param alone — the FileExplorer link
    // (older versions) doesn't include it, and we still want this UI for any
    // saved analysis whose source is exactly one image. Infer from metadata.
    const sourceIsSingleImage = (() => {
      const sp = (metadata.source_path || '').toLowerCase();
      const fc = Number(metadata.frame_count);
      const looksLikeImage = /\.(jpg|jpeg|png|webp)$/.test(sp);
      return singleImageMode || looksLikeImage || (Number.isFinite(fc) && fc <= 1);
    })();
    const isSingleImageColonyRestore = isColonyCountSave && sourceIsSingleImage;
    const baseCategories = isSingleImageColonyRestore
      ? [
          { key: 'growth', label: 'Area', folder: 'growth', matchExt: 'json', matchExtFallback: 'csv', description: 'Area distribution + image overlay' },
          { key: 'color', label: 'Color', folder: 'pigmentation', matchExt: 'json', description: 'HSV / CIELAB scatter + image overlay' },
        ]
      : [
          { key: 'experiment', label: 'Experiment', folder: '', matchExt: '', description: 'Experiment details' },
          { key: 'growth', label: 'Growth', folder: 'growth', matchExt: 'json', matchExtFallback: 'csv', description: 'Colony area(s) over time' },
          { key: 'color', label: 'Color', folder: 'pigmentation', matchExt: 'json', description: 'Color analysis' },
          { key: 'texture', label: 'Texture', folder: 'rugosity', matchExt: 'json', description: 'Surface texture analysis' },
          { key: 'shape', label: 'Shape', folder: 'morphology', matchExt: 'json', description: 'Circularity, irregularity and edge metrics over time' },
          { key: 'spiral', label: 'Spiral', folder: 'spiral', matchExt: 'json', description: 'Spiral plate colony counting' },
          { key: 'objects', label: 'Objects', folder: 'rle_data', matchExt: 'json', description: 'Colony detection results' },
        ];
    // Colony Summary was removed from the analysis page: its per-colony HSV /
    // colour / area numbers duplicate what Color, Growth and Shape already plot
    // over time, and it read as a second, disagreeing source of truth for the
    // same measurements. The phenotypic endpoint and its panel component are
    // untouched -- only this page stops surfacing it.
    const CATEGORIES = baseCategories;

    // Resolve the target file for a given category from the folder tree.
    // Checks the primary folder/ext first, then falls back to legacy locations.
    const getCategoryFile = (cat) => {
      if (cat.key === 'experiment') return { _virtual: true };
      // Try primary folder + primary extension
      const primary = (folderTree[cat.folder] || [])
        .find(f => (f.fileName || f.name || '').toLowerCase().endsWith(`.${cat.matchExt}`));
      if (primary) return primary;
      // Try primary folder + fallback extension (e.g. .csv for growth)
      if (cat.matchExtFallback) {
        const primaryFallbackExt = (folderTree[cat.folder] || [])
          .find(f => (f.fileName || f.name || '').toLowerCase().endsWith(`.${cat.matchExtFallback}`));
        if (primaryFallbackExt) return primaryFallbackExt;
      }
      // Try fallback folder + primary extension (e.g. rle_data for texture)
      if (cat.folderFallback) {
        const fallbackFolder = (folderTree[cat.folderFallback] || [])
          .find(f => (f.fileName || f.name || '').toLowerCase().endsWith(`.${cat.matchExt}`));
        if (fallbackFolder) return fallbackFolder;
      }
      return null;
    };

    // Find the video file across all folders for the Download Video button
    const getVideoFile = () => {
      const allFolderKeys = Object.keys(folderTree);
      for (const fk of allFolderKeys) {
        const match = (folderTree[fk] || []).find(f =>
          (f.fileName || f.name || '').toLowerCase().endsWith('.mp4')
        );
        if (match) return match;
      }
      return null;
    };

    // Recompute a specific analysis category for this saved analysis
    const recomputeCategory = async (catKey) => {
      const sessionId = metadata.session_id;
      const s3Path = metadata.source_path || '';
      if (!sessionId) {
        enqueueSnackbar('Unable to recompute. Please reload and try again.', { variant: 'error' });
        return;
      }

      const endpointMap = {
        growth: `${cpuUrl}/growth`,
        color: `${cpuUrl}/pigmentation`,
        texture: `${cpuUrl}/rugosity`,
      };
      const endpoint = endpointMap[catKey];
      if (!endpoint) return;

      setComputingCategory(catKey);
      enqueueSnackbar(`Computing ${catKey} analysis...`, { variant: 'info' });

      try {
        const body = { session_id: sessionId, s3_path: s3Path };
        if (catKey === 'texture') body.frame_indices = [0];

        const resp = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!resp.ok) throw new Error(`Failed to start ${catKey} analysis (${resp.status})`);
        const { task_id } = await resp.json();

        // Poll for completion
        const deadline = Date.now() + 5 * 60 * 1000;
        while (Date.now() < deadline) {
          await new Promise(r => setTimeout(r, 3000));
          const pollResp = await fetch(`${cpuUrl}/task_progress/${task_id}`);
          if (!pollResp.ok) throw new Error(`Analysis polling error (${pollResp.status})`);
          const prog = await pollResp.json();
          if (prog.stage === 'complete') break;
          if (prog.stage === 'error') throw new Error(prog.message || `${catKey} analysis failed`);
        }

        enqueueSnackbar(`${catKey} analysis complete. Reloading...`, { variant: 'success' });
        // Reload restore data to pick up new S3 files
        setRestoreData(null);
        setRestoreSelectedFile(null);
        setRestoreFileContent(null);
      } catch (err) {
        console.error(err);
        enqueueSnackbar(err.message || `Failed to compute ${catKey} analysis.`, { variant: 'error' });
      } finally {
        setComputingCategory(null);
      }
    };

    // Auto-load the selected category's file
    const handleCategorySelect = async (catKey) => {
      await ensureClients();
      setRestoreCategory(catKey);
      setRestoreSelectedFile(null);
      setRestoreFileContent(null);
      // Reset mask frame slider when switching categories
      if (catKey !== 'texture') setRestoreMaskFrame(0);
      // Experiment category needs no file loading -- data is already in state
      if (catKey === 'experiment') {
        setRestoreSelectedFile({ _virtual: true, ext: 'experiment' });
        return;
      }

      // Objects category: load ALL per-frame JSON files from rle_data/ and consolidate
      if (catKey === 'objects') {
        const rleFiles = (folderTree['rle_data'] || [])
          .filter(f => (f.fileName || f.name || '').toLowerCase().endsWith('.json'))
          .sort((a, b) => (a.fileName || a.name || '').localeCompare(b.fileName || b.name || ''));
        if (!rleFiles.length) return;
        setRestoreSelectedFile({ _virtual: true, ext: 'json', fileName: 'propagated_masks (consolidated)' });
        try {
          // Fetch all per-frame JSON files in parallel (limit concurrency to 10)
          const consolidated = { frames: {} };
          const batchSize = 10;
          for (let i = 0; i < rleFiles.length; i += batchSize) {
            const batch = rleFiles.slice(i, i + batchSize);
            const results = await Promise.all(batch.map(async (file) => {
              const url = s3.getSignedUrl('getObject', { Bucket: bucket, Key: file.key, Expires: 3600 });
              const resp = await fetch(url);
              return resp.json();
            }));
            results.forEach((data) => {
              if (!data || data.frame_idx === undefined) return;
              const frameKey = String(data.frame_idx);
              consolidated.frames[frameKey] = {};
              (data.objects || []).forEach((obj) => {
                consolidated.frames[frameKey][String(obj.obj_id)] = {
                  rle: obj.rle, // COCO RLE: {counts: [...], size: [H, W]}
                  shape: obj.rle?.size || [0, 0],
                  _cocoRLE: true, // flag so drawRestoreMasks uses decodeCOCORLE
                };
              });
            });
          }
          setRestoreFileContent(JSON.stringify(consolidated));
        } catch (err) {
          console.error('Failed to load rle_data files:', err);
        }
        return;
      }

      const cat = CATEGORIES.find(c => c.key === catKey);
      if (!cat) return;
      const file = getCategoryFile(cat);
      if (!file) return;
      const ext = (file.fileName || file.name || '').split('.').pop().toLowerCase();
      const signParams = {
        Bucket: bucket,
        Key: file.key,
        Expires: 3600
      };
      const presignedUrl = s3.getSignedUrl('getObject', signParams);
      const selected = { ...file, url: presignedUrl, ext };
      setRestoreSelectedFile(selected);
      // For CSV and JSON, fetch content for inline viewing
      if (ext === 'csv' || ext === 'json') {
        try {
          const resp = await fetch(presignedUrl);
          const text = await resp.text();
          setRestoreFileContent(text);
        } catch (err) {
          console.error('Failed to fetch file content:', err);
        }
      }
    };

    // Download video via S3 SDK (bypasses CORS issues with presigned URLs)
    // --- Symbiomics "resume file": every characteristic for this well, one export
    // Replaces hunting through the per-characteristic XLSX buttons. Sheet layout
    // and file split live in utils/wellExport.js; this only gathers the inputs.
    const handleDownloadWellData = async () => {
      await ensureClients();
      const meta = metadata || {};
      const identity = identityFromName(analysisName);

      const readJson = async (folder, file) => {
        const f = (folderTree[folder] || [])
          .find(x => (x.fileName || x.name || '').toLowerCase().endsWith('.json'));
        if (!f) return null;
        try {
          const obj = await s3.getObject({ Bucket: bucket, Key: f.key }).promise();
          return JSON.parse(obj.Body.toString('utf-8'));
        } catch (e) {
          console.error(`well export: could not read ${folder}`, e);
          return null;
        }
      };

      try {
        const [growth, pigmentation, morphology] = await Promise.all([
          readJson('growth'), readJson('pigmentation'), readJson('morphology'),
        ]);
        if (!growth && !pigmentation && !morphology) {
          enqueueSnackbar('No analysis results to export yet for this well.', { variant: 'warning' });
          return;
        }
        const ids = wellObjectIds({ growth, pigmentation, morphology });
        if (!ids.length) {
          enqueueSnackbar('No objects found in this analysis.', { variant: 'warning' });
          return;
        }

        const time = timeResolver(restoreFrameTimestamps, restoreFrameTimeIso);
        // This analysis is EITHER the colony or the medium; the other side is a
        // separate analysis. Whichever is absent ships as header-only sheets, so
        // the reader can tell "not measured" from "not exported".
        const isMedia = identity?.kind === 'media';
        const components = ids.map((id, i) => {
          const bundle = bundleFor(id, { growth, pigmentation, morphology });
          const side = identity?.side || (ids.length > 1 ? (i === 0 ? 'left' : 'right') : null);
          return {
            side,
            // The side named in the analysis decides which component this is, so
            // an "A2_R" analysis lands in Component2 even though it is the only
            // object in its own file. Falling back to the loop index would file
            // every side-analysis as Component1 and collide on disk.
            colonyIndex: side === 'right' ? 2 : side === 'left' ? 1 : i + 1,
            colony: isMedia ? undefined : bundle,
            media: isMedia ? bundle : undefined,
          };
        });

        // An _L/_R suffix is itself proof of a barrier plate: the operator only
        // splits a well into sides when a barrier divides it. That beats the
        // object-count heuristic, which sees one colony per side-analysis and
        // would call a barrier well plain.
        const type = inferExperimentType({
          declaredType: meta.experiment_type      // set at setup on newer firmware
            || (identity?.side ? EXPERIMENT_I : undefined),
          colonyCount: ids.length,
        });
        const books = buildWellExport({
          plate: identity?.plate || meta.plate || '1',
          well: identity?.well || analysisName || 'well',
          experimentType: type,
          components,
          time,
        });

        books.forEach((book) => {
          const wb = XLSX.utils.book_new();
          book.sheets.forEach(sh =>
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sh.rows), sh.name));
          XLSX.writeFile(wb, book.filename);
        });
        enqueueSnackbar(
          books.length > 1
            ? `Exported ${books.length} files (barrier plate: one per component).`
            : `Exported ${books[0].filename}`,
          { variant: 'success' });
      } catch (e) {
        console.error('well export failed', e);
        enqueueSnackbar(`Could not build the well export: ${e.message}`, { variant: 'error' });
      }
    };

    const handleDownloadVideo = async () => {
      await ensureClients();
      const videoFile = getVideoFile();
      if (!videoFile) {
        enqueueSnackbar('No video file found in this analysis.', { variant: 'warning' });
        return;
      }
      try {
        const data = await s3.getObject({ Bucket: bucket, Key: videoFile.key }).promise();
        const ext = (videoFile.fileName || videoFile.name || '').split('.').pop().toLowerCase();
        const mimeType = ext === 'mov' ? 'video/quicktime' : `video/${ext}`;
        const blob = new Blob([data.Body], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = videoFile.fileName || videoFile.name || 'masked_video.mp4';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error('Video download failed:', err);
        enqueueSnackbar('Failed to download video.', { variant: 'error' });
      }
    };

    // Download or generate masked overlay MP4 via shared handler
    const handleDownloadMaskedVideo = async () => {
      const sessionId = metadata?.session_id;
      if (maskedVideoState.status === 'ready' && maskedVideoUrl) {
        handleDownloadMaskedVideoUrl(maskedVideoUrl, `masked_video_${metadata?.analysis_id || sessionId}.mp4`);
        return;
      }
      const url = await fetchOrGenerateMaskedVideo(sessionId, bucket);
      if (url) handleDownloadMaskedVideoUrl(url, `masked_video_${metadata?.analysis_id || sessionId}.mp4`);
    };

    const handleDownloadFile = async (url, filename) => {
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        // Fallback: open in new tab
        console.error('Download failed, opening in new tab:', err);
        window.open(url, '_blank');
      }
    };

    // Download growth JSON data as XLSX
    const handleDownloadGrowthXlsx = (parsedGrowth) => {
      if (!Array.isArray(parsedGrowth)) return;
      const elapsedH = (fi) => {
        const s = restoreFrameTimestamps?.[fi] ?? restoreFrameTimestamps?.[String(fi)];
        return typeof s === 'number' ? +(s / 3600).toFixed(4) : '';
      };
      const isoOf = (fi) => restoreFrameTimeIso?.[fi] ?? restoreFrameTimeIso?.[String(fi)] ?? '';
      const rows = [['Frame', 'Time_Elapsed_h', 'Timestamp_UTC', 'Object_ID', 'Area_px2', 'Growth_Rate']];
      parsedGrowth.forEach(obj => {
        (obj.frames || []).forEach(f => {
          rows.push([f.frame_idx, elapsedH(f.frame_idx), isoOf(f.frame_idx), obj.obj_id, f.area_pixels || 0, f.growth_rate || 0]);
        });
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Growth Data');
      XLSX.writeFile(wb, 'growth_data.xlsx');
    };

    // Download rugosity/texture JSON data as XLSX (multi-sheet: metrics + spectrum)
    const handleDownloadRugosityXlsx = (parsedData) => {
      if (!parsedData) return;
      let objectsMap = {};
      if (Array.isArray(parsedData)) {
        parsedData.forEach(obj => { objectsMap[String(obj.obj_id)] = obj.frames || []; });
      } else if (parsedData.objects && typeof parsedData.objects === 'object') {
        Object.entries(parsedData.objects).forEach(([objId, data]) => {
          objectsMap[objId] = [{ frame_idx: parsedData.frame_idx ?? 0, ...data }];
        });
      }
      const elapsedH = (fi) => {
        const s = restoreFrameTimestamps?.[fi] ?? restoreFrameTimestamps?.[String(fi)];
        return typeof s === 'number' ? +(s / 3600).toFixed(4) : '';
      };
      const isoOf = (fi) => restoreFrameTimeIso?.[fi] ?? restoreFrameTimeIso?.[String(fi)] ?? '';
      const metricRows = [['Frame', 'Time_Elapsed_h', 'Timestamp_UTC', 'Colony', 'Contrast', 'Homogeneity', 'Energy', 'Correlation', 'Edge_Density']];
      const spectrumRows = [['Colony', 'Frame', 'log_Frequency', 'log_Power']];
      Object.entries(objectsMap).forEach(([objId, frames]) => {
        frames.forEach(f => {
          metricRows.push([f.frame_idx, elapsedH(f.frame_idx), isoOf(f.frame_idx), objId, f.contrast ?? '', f.homogeneity ?? '', f.energy ?? '', f.correlation ?? '', f.edge_density ?? '']);
          if (f.frequency_log && f.radial_profile_log) {
            f.frequency_log.forEach((freq, i) => {
              spectrumRows.push([objId, f.frame_idx, freq, f.radial_profile_log[i] ?? '']);
            });
          }
        });
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(metricRows), 'Texture Metrics');
      if (spectrumRows.length > 1) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(spectrumRows), 'Power Spectrum');
      }
      XLSX.writeFile(wb, 'texture_data.xlsx');
    };

    // --- Color space conversion utilities ---
    // HSV (H: 0-180, S: 0-255, V: 0-255) -> RGB (0-255)
    const hsvToRgb = (h, s, v) => {
      const hNorm = (h / 180) * 360; // OpenCV hue 0-180 -> 0-360
      const sNorm = s / 255;
      const vNorm = v / 255;
      const c = vNorm * sNorm;
      const x = c * (1 - Math.abs(((hNorm / 60) % 2) - 1));
      const m = vNorm - c;
      let r1, g1, b1;
      if (hNorm < 60)       { r1 = c; g1 = x; b1 = 0; }
      else if (hNorm < 120) { r1 = x; g1 = c; b1 = 0; }
      else if (hNorm < 180) { r1 = 0; g1 = c; b1 = x; }
      else if (hNorm < 240) { r1 = 0; g1 = x; b1 = c; }
      else if (hNorm < 300) { r1 = x; g1 = 0; b1 = c; }
      else                  { r1 = c; g1 = 0; b1 = x; }
      return [
        Math.round((r1 + m) * 255),
        Math.round((g1 + m) * 255),
        Math.round((b1 + m) * 255)
      ];
    };

    // RGB (0-255) -> CIELAB (L: 0-100, a: ~-128..127, b: ~-128..127)
    const rgbToLab = (r, g, b) => {
      // sRGB -> linear
      let rl = r / 255, gl = g / 255, bl = b / 255;
      rl = rl > 0.04045 ? Math.pow((rl + 0.055) / 1.055, 2.4) : rl / 12.92;
      gl = gl > 0.04045 ? Math.pow((gl + 0.055) / 1.055, 2.4) : gl / 12.92;
      bl = bl > 0.04045 ? Math.pow((bl + 0.055) / 1.055, 2.4) : bl / 12.92;
      // Linear RGB -> XYZ (D65 illuminant)
      let x = (rl * 0.4124564 + gl * 0.3575761 + bl * 0.1804375) / 0.95047;
      let y = (rl * 0.2126729 + gl * 0.7151522 + bl * 0.0721750) / 1.00000;
      let z = (rl * 0.0193339 + gl * 0.1191920 + bl * 0.9503041) / 1.08883;
      // XYZ -> Lab
      const f = (t) => t > 0.008856 ? Math.cbrt(t) : (7.787 * t) + (16 / 116);
      const fx = f(x), fy = f(y), fz = f(z);
      const L = (116 * fy) - 16;
      const a = 500 * (fx - fy);
      const bVal = 200 * (fy - fz);
      return [parseFloat(L.toFixed(2)), parseFloat(a.toFixed(2)), parseFloat(bVal.toFixed(2))];
    };

    // Download pigmentation data as XLSX in the specified color space
    const handleColorXlsxDownload = (parsedJson, colorSpace) => {
      if (!parsedJson || !parsedJson.frames) return;
      const frames = parsedJson.frames;
      const frameKeys = Object.keys(frames).map(Number).sort((a, b) => a - b);
      const allObjIds = [...new Set(frameKeys.flatMap(fk => Object.keys(frames[String(fk)])))].sort((a, b) => parseInt(a) - parseInt(b));

      const elapsedH = (fi) => {
        const s = restoreFrameTimestamps?.[fi] ?? restoreFrameTimestamps?.[String(fi)];
        return typeof s === 'number' ? +(s / 3600).toFixed(4) : '';
      };
      const isoOf = (fi) => restoreFrameTimeIso?.[fi] ?? restoreFrameTimeIso?.[String(fi)] ?? '';
      let header, getValues;
      if (colorSpace === 'hsv') {
        header = ['Frame', 'Time_Elapsed_h', 'Timestamp_UTC', 'Object', 'Hue', 'Saturation', 'Value'];
        getValues = (fd) => [fd.hue, fd.saturation, fd.value].map(v => v != null ? parseFloat(parseFloat(v).toFixed(2)) : '');
      } else if (colorSpace === 'rgb') {
        header = ['Frame', 'Time_Elapsed_h', 'Timestamp_UTC', 'Object', 'R', 'G', 'B'];
        getValues = (fd) => {
          if (fd.hue == null || fd.saturation == null || fd.value == null) return ['', '', ''];
          const [r, g, b] = hsvToRgb(fd.hue, fd.saturation, fd.value);
          return [r, g, b];
        };
      } else if (colorSpace === 'cielab') {
        header = ['Frame', 'Time_Elapsed_h', 'Timestamp_UTC', 'Object', 'L', 'a', 'b'];
        getValues = (fd) => {
          if (fd.hue == null || fd.saturation == null || fd.value == null) return ['', '', ''];
          const [r, g, b] = hsvToRgb(fd.hue, fd.saturation, fd.value);
          const [L, a, bVal] = rgbToLab(r, g, b);
          return [L, a, bVal];
        };
      } else {
        return;
      }

      const rows = [header];
      for (const fk of frameKeys) {
        for (const objId of allObjIds) {
          const fd = frames[String(fk)]?.[objId];
          if (!fd) continue;
          const vals = getValues(fd);
          rows.push([fk, elapsedH(fk), isoOf(fk), parseInt(objId), ...vals]);
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), `Color ${colorSpace.toUpperCase()}`);
      XLSX.writeFile(wb, `color_analysis_${colorSpace.toUpperCase()}.xlsx`);
      setColorDownloadAnchor(null);
    };

    // Render the center preview area based on file type
    const renderPreview = () => {
      // Single-image colony-count restore: the Area + Color tabs go through
      // a dedicated panel that pairs the chart with the source image and
      // per-object visibility. Drop the legacy time-series rendering for
      // these tabs since there's only one frame.
      if (
        isSingleImageColonyRestore &&
        (restoreCategory === 'growth' || restoreCategory === 'color')
      ) {
        if (restoredColonies == null) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="caption" sx={{ color: '#aaa' }}>Loading saved colony data…</Typography>
            </Box>
          );
        }
        if (restoredColonies.length === 0) {
          return (
            <Box sx={{ p: 3, color: '#888' }}>
              <Typography variant="body2">
                No saved colony data in this analysis (phenotypic/results.json missing).
              </Typography>
            </Box>
          );
        }
        return (
          <SingleImageRestoreVizPanel
            view={restoreCategory === 'growth' ? 'area' : 'color'}
            colonies={restoredColonies}
            rleData={restoredSingleFrameRle}
            frameImageUrl={restoreFrameImages[0]}
            analysisName={analysisName || metadata.analysis_name || ''}
            objectMeta={restoredObjectMeta || {}}
          />
        );
      }
      // Colony Summary tab in restore mode: render ColonySummaryPanel directly
      // from the pre-loaded phenotypic JSON. Bypass the file-renderer pipeline
      // entirely since the panel owns its own visualizations.
      if (restoreCategory === 'phenotypic') {
        if (restoredColonies == null) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="caption" sx={{ color: '#aaa' }}>Loading saved colony data…</Typography>
            </Box>
          );
        }
        if (restoredColonies.length === 0) {
          return (
            <Box sx={{ p: 3, color: '#888' }}>
              <Typography variant="body2">
                No saved colony summary in this analysis (phenotypic/results.json missing).
              </Typography>
            </Box>
          );
        }
        return (
          <Box sx={{ height: '100%', minHeight: 0 }}>
            <ColonySummaryPanel
              cpuUrl={cpuUrl}
              sessionId={metadata.session_id || ''}
              frameIdx={0}
              s3Path={metadata.source_path || ''}
              staticColonies={restoredColonies}
              onHoverObject={() => { /* no canvas in this view */ }}
              colonyCount={restoredColonies.length}
              refreshTick={0}
            />
          </Box>
        );
      }
      if (!restoreSelectedFile) {
        const activeCat = CATEGORIES.find(c => c.key === restoreCategory);
        const catFile = activeCat ? getCategoryFile(activeCat) : null;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary', gap: 1 }}>
            {catFile === null && activeCat ? (
              activeCat.key !== 'experiment' && activeCat.key !== 'objects' && activeCat.key !== 'spiral' ? (
                <>
                  <Typography variant="h6">{activeCat.label} data not yet computed</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {activeCat.label} analysis has not been run for this session.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!!computingCategory}
                    onClick={() => recomputeCategory(activeCat.key)}
                    sx={{ textTransform: 'none' }}
                  >
                    {computingCategory === activeCat.key ? 'Computing...' : `Compute ${activeCat.label}`}
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="h6">
                    {activeCat.key === 'spiral' ? 'Spiral plate analysis not available' : `${activeCat.label} data not available`}
                  </Typography>
                  <Typography variant="body2">
                    {activeCat.key === 'spiral'
                      ? 'Spiral plate analysis was not performed for this session.'
                      : `This analysis does not contain ${activeCat.label.toLowerCase()} data.`}
                  </Typography>
                </>
              )
            ) : (
              <>
                <CircularProgress size={32} sx={{ mb: 1 }} />
                <Typography variant="body2">Loading...</Typography>
              </>
            )}
          </Box>
        );
      }

      const { url, ext, fileName, name: fullName } = restoreSelectedFile;
      const displayName = fileName || fullName;

      // ─── Experiment Section ───
      if (ext === 'experiment') {
        // Build temperature/humidity chart data
        const getExperimentGraphData = () => {
          if (!experimentTempData || experimentTempData.length === 0) return null;
          const firstTimestamp = new Date(experimentTempData[0].Time).getTime();
          const tempPoints = [];
          const humidityPoints = [];

          for (let i = 0; i < experimentTempData.length; i++) {
            const row = experimentTempData[i];
            const timestamp = new Date(row.Time).getTime();
            const hoursElapsed = (timestamp - firstTimestamp) / (1000 * 60 * 60);
            const tempVal = parseFloat(row['Temperature-1']);
            const humVal = parseFloat(row['Humidity-1']);
            if (!isNaN(tempVal)) tempPoints.push({ x: hoursElapsed, y: tempVal });
            if (!isNaN(humVal)) humidityPoints.push({ x: hoursElapsed, y: humVal });
          }

          if (experimentTempGraph === 'Temperature') {
            return {
              datasets: [{
                label: 'Temperature (C)',
                data: tempPoints,
                borderColor: 'rgba(75,192,192,1)',
                backgroundColor: 'rgba(75,192,192,0.1)',
                fill: false,
                pointRadius: 0,
                borderWidth: 2,
              }]
            };
          } else {
            return {
              datasets: [{
                label: 'Humidity (%)',
                data: humidityPoints,
                borderColor: 'rgba(255,99,132,1)',
                backgroundColor: 'rgba(255,99,132,0.1)',
                fill: false,
                pointRadius: 0,
                borderWidth: 2,
              }]
            };
          }
        };

        const graphData = getExperimentGraphData();

        // Filter out internal/non-useful keys from experiment params
        const HIDDEN_PARAM_KEYS = ['iterations', 'task', 'experiment_running', 'exp_file', 'sample_names', 'advanced'];

        // Friendly display name: remove timestamp prefix if present (e.g. "2025-02-15T12:00:00:MyExperiment" -> "MyExperiment")
        const expDisplayName = experimentName.includes(':') ? experimentName.split(':').slice(1).join(':') : experimentName;

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, overflow: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                {expDisplayName || 'Experiment'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>
                {experimentName}
              </Typography>
            </Box>

            {/* Temperature / Humidity Graph */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ccc' }}>
                  {experimentTempGraph} Graph
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setExperimentTempGraph(experimentTempGraph === 'Temperature' ? 'Humidity' : 'Temperature')}
                  sx={{ ml: 'auto', fontSize: '0.7rem' }}
                >
                  Show {experimentTempGraph === 'Temperature' ? 'Humidity' : 'Temperature'}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => experimentChartRef.current?.resetZoom()}
                  sx={{ fontSize: '0.7rem', color: '#888' }}
                >
                  Reset Zoom
                </Button>
              </Box>
              {graphData ? (
                <Box sx={{ bgcolor: '#111', borderRadius: 2, p: 2, height: 300 }}>
                  <Line
                    ref={experimentChartRef}
                    data={graphData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: { mode: 'index', intersect: false },
                      scales: {
                        x: {
                          type: 'linear',
                          ticks: { color: '#999' },
                          title: { display: true, text: 'Time (hours)', color: '#999' },
                          grid: { color: 'rgba(255,255,255,0.05)' }
                        },
                        y: {
                          type: 'linear',
                          ticks: { color: '#999' },
                          title: {
                            display: true,
                            text: experimentTempGraph === 'Temperature' ? 'Temperature (C)' : 'Humidity (%)',
                            color: '#999'
                          },
                          grid: { color: 'rgba(255,255,255,0.05)' }
                        }
                      },
                      plugins: {
                        legend: { labels: { color: '#ccc' } },
                        zoom: {
                          pan: { enabled: true, mode: 'xy' },
                          zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' }
                        }
                      }
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ bgcolor: '#111', borderRadius: 2, p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    No temperature data available for this experiment.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Experiment Parameters */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ccc', mb: 1 }}>
                Experiment Parameters
              </Typography>
              {experimentParams ? (
                <Box sx={{ bgcolor: '#111', borderRadius: 2, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <tbody>
                      {Object.entries(experimentParams)
                        .filter(([key]) => !HIDDEN_PARAM_KEYS.includes(key))
                        .map(([key, value]) => (
                          <tr key={key} style={{ borderBottom: '1px solid #222' }}>
                            <td style={{ padding: '8px 12px', color: '#aaa', fontWeight: 600, width: '40%', verticalAlign: 'top' }}>{key}</td>
                            <td style={{ padding: '8px 12px', color: '#ddd' }}>{String(value)}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </Box>
              ) : (
                <Box sx={{ bgcolor: '#111', borderRadius: 2, p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    No experiment parameters available.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      }

      // ─── NEW FORMAT: JSON growth data ([{obj_id, frames:[{frame_idx, area_pixels, growth_rate}]}]) ───
      if (ext === 'json' && restoreFileContent) {
        let parsedGrowth;
        try { parsedGrowth = JSON.parse(restoreFileContent); } catch { parsedGrowth = null; }
        // Morphology uses the SAME envelope as growth ([{obj_id, frames:[...]}]),
        // so without excluding 'shape' this branch claims the morphology file and
        // draws it as a growth chart -- reading area_pixels, which morphology
        // calls area_px, so every point plots as zero.
        const isGrowthJSON = Array.isArray(parsedGrowth) && parsedGrowth.length > 0 && parsedGrowth[0]?.frames !== undefined && parsedGrowth[0]?.obj_id !== undefined
          && restoreCategory !== 'texture' && restoreCategory !== 'color' && restoreCategory !== 'shape';
        if (isGrowthJSON) {
          const GROWTH_COLORS = ['#FF6384','#36A2EB','#FFCE56','#4BC0C0','#9966FF','#FF9F40','#8E44AD','#1ABC9C','#2ECC71','#3498DB'];
          // Flatten to dataPoints
          const dataPoints = [];
          parsedGrowth.forEach(obj => {
            (obj.frames || []).forEach(f => {
              dataPoints.push({
                frame: f.frame_idx,
                displayDateTime: `Frame ${f.frame_idx}`,
                objectId: String(obj.obj_id),
                area: f.area_pixels || 0,
                growthRate: f.growth_rate || 0,
              });
            });
          });
          dataPoints.sort((a, b) => a.frame - b.frame);
          const objectIds = [...new Set(dataPoints.map(d => d.objectId))].sort((a, b) => parseInt(a) - parseInt(b));

          // Time-aware x-axis: real elapsed time when a frame->timestamp mapping
          // exists (with a break across the merged-experiment outage), else
          // "Frame N". axis.frames carries null at each inserted break slot.
          const axis = buildTimeAxis(dataPoints.map(d => d.frame), restoreFrameTimestamps);
          const labels = axis.labels;

          const makeDatasets = (valueKey) => objectIds.map((objId, idx) => {
            const color = GROWTH_COLORS[idx % GROWTH_COLORS.length];
            const byFrame = {};
            dataPoints.filter(d => d.objectId === objId).forEach(d => { byFrame[d.frame] = d[valueKey]; });
            return {
              label: `Object ${objId}`,
              // null at break slots + spanGaps:false => the line breaks across
              // the outage (the lost time reads as a gap, not interpolation).
              data: axis.frames.map(f => (f == null ? null : (byFrame[f] ?? null))),
              borderColor: color,
              backgroundColor: color + '33',
              tension: 0.4,
              pointRadius: 3,
              pointHoverRadius: 6,
              borderWidth: 2,
              fill: false,
              spanGaps: false,
            };
          });

          const baseOpts = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { position: 'top', labels: { color: '#ccc', usePointStyle: true, pointStyle: 'circle' } },
              tooltip: { backgroundColor: '#222', titleColor: '#fff', bodyColor: '#ccc' },
              zoom: { pan: { enabled: true, mode: 'xy' }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' } },
            },
            scales: {
              x: { type: 'category', title: { display: true, text: axis.axisTitle, color: '#aaa' }, ticks: { color: '#888', maxTicksLimit: 15 }, grid: { color: '#222' } },
              y: { title: { display: true, text: 'Area (px\u00b2)', color: '#aaa' }, ticks: { color: '#888' }, grid: { color: '#222' }, beginAtZero: true },
            },
          };
          const rateOpts = { ...baseOpts, scales: { ...baseOpts.scales, y: { ...baseOpts.scales.y, title: { display: true, text: 'Growth Rate (relative)', color: '#aaa' }, beginAtZero: false } } };

          const areaChartData = { labels, datasets: makeDatasets('area') };
          const rateChartData = { labels, datasets: makeDatasets('growthRate') };

          // Time of Appearance bar chart
          const jsonAppearanceData = objectIds.map(objId => {
            const objPoints = dataPoints.filter(d => d.objectId === objId && d.area > 0);
            if (objPoints.length === 0) return null;
            const first = objPoints.reduce((earliest, d) => d.frame < earliest.frame ? d : earliest, objPoints[0]);
            return { objectId: objId, frame: first.frame };
          }).filter(Boolean).sort((a, b) => a.frame - b.frame);

          const jsonAppearanceChartData = {
            labels: jsonAppearanceData.map(d => `Object ${d.objectId}`),
            datasets: [{
              label: 'First Appearance (frame)',
              data: jsonAppearanceData.map(d => d.frame),
              backgroundColor: jsonAppearanceData.map((_, i) => GROWTH_COLORS[i % GROWTH_COLORS.length] + 'AA'),
              borderColor: jsonAppearanceData.map((_, i) => GROWTH_COLORS[i % GROWTH_COLORS.length]),
              borderWidth: 1,
            }]
          };
          const jsonAppearanceOpts = {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { backgroundColor: '#222', titleColor: '#fff', bodyColor: '#ccc',
                callbacks: { label: (ctx) => `Frame ${ctx.parsed.x}` }
              },
              zoom: { pan: { enabled: true, mode: 'xy' }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' } }
            },
            scales: {
              x: { title: { display: true, text: 'Frame Number', color: '#aaa' }, ticks: { color: '#888' }, grid: { color: '#222' }, beginAtZero: true },
              y: { ticks: { color: '#ccc' }, grid: { color: '#222' } }
            }
          };

          const viewButtons = [{ key: 'area', label: 'Area' }, { key: 'rate', label: 'Growth Rate' }, { key: 'appearance', label: 'Time of Appearance' }];

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Growth</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="text" onClick={() => growthChartRef.current?.resetZoom()} sx={{ fontSize: '0.7rem', color: '#888' }}>Reset Zoom</Button>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadGrowthXlsx(parsedGrowth)}>Download (XLSX)</Button>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                {viewButtons.map(vb => (
                  <Button key={vb.key} size="small"
                    variant={restoreGrowthView === vb.key ? 'contained' : 'outlined'}
                    onClick={() => setRestoreGrowthView(vb.key)}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', px: 1.5, py: 0.3,
                      ...(restoreGrowthView === vb.key ? { bgcolor: '#1976d2', color: '#fff' } : { color: '#aaa', borderColor: '#555' }) }}>
                    {vb.label}
                  </Button>
                ))}
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, bgcolor: '#000', borderRadius: 2, p: 2 }}>
                {restoreGrowthView === 'area' && <Line ref={growthChartRef} data={areaChartData} options={baseOpts} />}
                {restoreGrowthView === 'rate' && <Line ref={growthChartRef} data={rateChartData} options={rateOpts} />}
                {restoreGrowthView === 'appearance' && <Bar ref={growthChartRef} data={jsonAppearanceChartData} options={jsonAppearanceOpts} />}
              </Box>
            </Box>
          );
        }

      }
      // ─────────────────────────────────────────────────────────────────────────────

      if (ext === 'csv' && restoreFileContent) {
        // Growth-style Area Chart
        const GROWTH_COLORS = ['#FF6384','#36A2EB','#FFCE56','#4BC0C0','#9966FF','#FF9F40','#8E44AD','#1ABC9C','#2ECC71','#3498DB'];
        const lines = restoreFileContent.trim().split('\n');
        const headerLine = lines[0].toLowerCase();
        const isAreaCSV = headerLine.includes('frame') && headerLine.includes('object') && headerLine.includes('area');

        if (isAreaCSV) {
          // Parse CSV
          const headers = lines[0].split(',').map(h => h.trim());
          const frameColIdx = headers.findIndex(h => h.toLowerCase() === 'frame');
          const timeIdx = headers.findIndex(h => h.toLowerCase() === 'time');
          const objIdx = headers.findIndex(h => h.toLowerCase() === 'object');
          const areaIdx = headers.findIndex(h => h.toLowerCase() === 'area');

          const dataPoints = [];
          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split(',').map(c => c.trim());
            if (cols.length < 4) continue;
            const timestamp = cols[timeIdx] || '';
            let displayDateTime = timestamp;
            if (timestamp.length >= 16) {
              const d = new Date(timestamp);
              if (!isNaN(d.getTime())) {
                displayDateTime = `${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
              }
            }
            dataPoints.push({
              frame: parseInt(cols[frameColIdx]),
              timestamp,
              displayDateTime,
              objectId: cols[objIdx],
              area: parseFloat(cols[areaIdx])
            });
          }

          const seen = new Set();
          const labels = [];
          dataPoints.forEach(d => { if (!seen.has(d.displayDateTime)) { seen.add(d.displayDateTime); labels.push(d.displayDateTime); }});
          const objectIds = [...new Set(dataPoints.map(d => d.objectId))].sort((a,b) => parseInt(a)-parseInt(b));

          const datasets = objectIds.map((objId, idx) => {
            const color = GROWTH_COLORS[idx % GROWTH_COLORS.length];
            const objPoints = dataPoints.filter(d => d.objectId === objId);
            const dataMap = {};
            objPoints.forEach(d => { dataMap[d.displayDateTime] = d.area; });
            return {
              label: `Object ${objId}`,
              data: labels.map(l => dataMap[l] ?? null),
              borderColor: color,
              backgroundColor: color + '33',
              tension: 0.4,
              pointRadius: 3,
              pointHoverRadius: 6,
              borderWidth: 2,
              fill: false,
              spanGaps: true
            };
          });

          // --- Area chart data ---
          const areaChartData = { labels, datasets };
          const areaChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { position: 'top', labels: { color: '#ccc', usePointStyle: true, pointStyle: 'circle' } },
              tooltip: {
                backgroundColor: '#222',
                titleColor: '#fff',
                bodyColor: '#ccc',
                callbacks: {
                  title: (items) => {
                    const i = items[0]?.dataIndex;
                    const pt = dataPoints.find(d => d.displayDateTime === labels[i]);
                    return pt ? `Time: ${pt.timestamp}` : labels[i];
                  },
                  label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1)} px\u00b2`
                }
              },
              zoom: {
                pan: { enabled: true, mode: 'xy', modifierKey: null },
                zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' }
              }
            },
            scales: {
              x: {
                title: { display: true, text: 'Date & Time (MM-DD HH:MM)', color: '#aaa' },
                ticks: { color: '#888', maxRotation: 45, autoSkip: true, maxTicksLimit: 20 },
                grid: { color: '#222' }
              },
              y: {
                title: { display: true, text: 'Area (px\u00b2)', color: '#aaa' },
                ticks: { color: '#888' },
                grid: { color: '#222' },
                beginAtZero: true
              }
            }
          };

          // --- Growth Rate (derivative) chart data ---
          const rateDatasets = objectIds.map((objId, idx) => {
            const color = GROWTH_COLORS[idx % GROWTH_COLORS.length];
            const objPoints = dataPoints.filter(d => d.objectId === objId);
            const dataMap = {};
            objPoints.forEach(d => { dataMap[d.displayDateTime] = d.area; });
            const areaVals = labels.map(l => dataMap[l] ?? null);
            const rateVals = areaVals.map((v, i) => {
              if (i === 0 || v === null || areaVals[i - 1] === null) return null;
              return v - areaVals[i - 1];
            });
            return {
              label: `Object ${objId}`,
              data: rateVals,
              borderColor: color,
              backgroundColor: color + '33',
              tension: 0.4,
              pointRadius: 3,
              pointHoverRadius: 6,
              borderWidth: 2,
              fill: false,
              spanGaps: true
            };
          });
          const rateChartData = { labels, datasets: rateDatasets };
          const rateChartOptions = {
            ...areaChartOptions,
            plugins: {
              ...areaChartOptions.plugins,
              tooltip: {
                ...areaChartOptions.plugins.tooltip,
                callbacks: {
                  title: areaChartOptions.plugins.tooltip.callbacks.title,
                  label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2)} px\u00b2/step`
                }
              }
            },
            scales: {
              ...areaChartOptions.scales,
              y: {
                ...areaChartOptions.scales.y,
                title: { display: true, text: 'Growth Rate (px\u00b2/step)', color: '#aaa' },
                beginAtZero: false
              }
            }
          };

          // --- Time of Appearance bar chart data ---
          const appearanceData = objectIds.map(objId => {
            const objPoints = dataPoints.filter(d => d.objectId === objId && d.area > 0);
            if (objPoints.length === 0) return null;
            const first = objPoints.reduce((earliest, d) => d.frame < earliest.frame ? d : earliest, objPoints[0]);
            return { objectId: objId, frame: first.frame, displayDateTime: first.displayDateTime, timestamp: first.timestamp };
          }).filter(Boolean).sort((a, b) => a.frame - b.frame);

          const appearanceChartData = {
            labels: appearanceData.map(d => `Object ${d.objectId}`),
            datasets: [{
              label: 'First Appearance (frame)',
              data: appearanceData.map(d => d.frame),
              backgroundColor: appearanceData.map((_, idx) => GROWTH_COLORS[objectIds.indexOf(appearanceData[idx].objectId) % GROWTH_COLORS.length] + 'AA'),
              borderColor: appearanceData.map((_, idx) => GROWTH_COLORS[objectIds.indexOf(appearanceData[idx].objectId) % GROWTH_COLORS.length]),
              borderWidth: 1
            }]
          };
          const appearanceChartOptions = {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: '#222',
                titleColor: '#fff',
                bodyColor: '#ccc',
                callbacks: {
                  label: (ctx) => {
                    const d = appearanceData[ctx.dataIndex];
                    return d ? `Frame ${d.frame} | ${d.timestamp}` : '';
                  }
                }
              },
              zoom: {
                pan: { enabled: true, mode: 'xy', modifierKey: null },
                zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' }
              }
            },
            scales: {
              x: {
                title: { display: true, text: 'Frame Number', color: '#aaa' },
                ticks: { color: '#888' },
                grid: { color: '#222' },
                beginAtZero: true
              },
              y: {
                ticks: { color: '#ccc' },
                grid: { color: '#222' }
              }
            }
          };

          const viewButtons = [
            { key: 'area', label: 'Area' },
            { key: 'rate', label: 'Growth Rate' },
            { key: 'appearance', label: 'Time of Appearance' }
          ];

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Growth</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="text" onClick={() => growthChartRef.current?.resetZoom()} sx={{ fontSize: '0.7rem', color: '#888' }}>
                    Reset Zoom
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => {
                    const csvLines = restoreFileContent.trim().split('\n');
                    const rows = csvLines.map(line => line.split(',').map(c => c.trim()));
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Growth Data');
                    XLSX.writeFile(wb, 'growth_data.xlsx');
                  }}>
                    Download (XLSX)
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                {viewButtons.map(vb => (
                  <Button key={vb.key} size="small"
                    variant={restoreGrowthView === vb.key ? 'contained' : 'outlined'}
                    onClick={() => setRestoreGrowthView(vb.key)}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', px: 1.5, py: 0.3,
                      ...(restoreGrowthView === vb.key ? { bgcolor: '#1976d2', color: '#fff' } : { color: '#aaa', borderColor: '#555' })
                    }}>
                    {vb.label}
                  </Button>
                ))}
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, bgcolor: '#000', borderRadius: 2, p: 2 }}>
                {restoreGrowthView === 'area' && <Line ref={growthChartRef} data={areaChartData} options={areaChartOptions} />}
                {restoreGrowthView === 'rate' && <Line ref={growthChartRef} data={rateChartData} options={rateChartOptions} />}
                {restoreGrowthView === 'appearance' && <Bar ref={growthChartRef} data={appearanceChartData} options={appearanceChartOptions} />}
              </Box>
              <Typography variant="caption" sx={{ color: '#666', mt: 1 }}>
                {restoreGrowthView === 'area' && `Scroll to zoom, drag to pan`}
                {restoreGrowthView === 'rate' && ``}
                {restoreGrowthView === 'appearance' && ``}
              </Typography>
            </Box>
          );
        }

        // Fallback: generic CSV as table
        const csvHeaders = lines[0].split(',');
        const csvRows = lines.slice(1).map(line => line.split(','));
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{displayName}</Typography>
              <Button size="small" variant="outlined" onClick={() => {
                const allRows = lines.map(line => line.split(',').map(c => c.trim()));
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(allRows), 'Data');
                const xlsxName = displayName.replace(/\.csv$/i, '.xlsx');
                XLSX.writeFile(wb, xlsxName);
              }}>
                Download (XLSX)
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', borderRadius: 2, border: '1px solid #333' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    {csvHeaders.map((h, i) => (
                      <th key={i} style={{ padding: '8px 12px', borderBottom: '2px solid #444', textAlign: 'left', position: 'sticky', top: 0, backgroundColor: '#1a1a1a', color: '#90caf9' }}>
                        {h.trim()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvRows.map((row, ri) => (
                    <tr key={ri} style={{ borderBottom: '1px solid #222' }}>
                      {row.map((cell, ci) => (
                        <td key={ci} style={{ padding: '6px 12px', color: '#ccc' }}>{cell.trim()}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        );
      }

      if (ext === 'json' && restoreFileContent) {
        let parsedJson;
        try { parsedJson = JSON.parse(restoreFileContent); } catch { parsedJson = null; }

        // Shape: shape/edge metrics over time from morphology/results.json.
        // Handled before the generic renderers, which would otherwise show it
        // as a raw JSON blob.
        if (restoreCategory === 'shape' && Array.isArray(parsedJson)) {
          return (
            <ShapeMetricsPanel
              parsedJson={parsedJson}
              frameTimeIso={restoreFrameTimeIso || {}}
              frameTimestamps={restoreFrameTimestamps || {}}
            />
          );
        }

        // Pigmentation data: Tabbed Color Analysis Charts
        const isPigmentation = parsedJson && parsedJson.frames && (restoreCategory === 'color' || displayName.toLowerCase().includes('pigmentation') || parsedJson.summary);
        if (isPigmentation) {
          const PIGMENT_COLORS = [
            {r:255,g:99,b:132}, {r:54,g:162,b:235}, {r:255,g:206,b:86},
            {r:75,g:192,b:192}, {r:153,g:102,b:255}, {r:255,g:159,b:64},
            {r:142,g:68,b:173}, {r:26,g:188,b:156}, {r:46,g:204,b:113}, {r:52,g:152,b:219}
          ];
          const frames = parsedJson.frames;
          const frameKeys = Object.keys(frames).map(Number).sort((a,b) => a-b);
          const allObjIds = [...new Set(frameKeys.flatMap(fk => Object.keys(frames[String(fk)])))].sort((a,b) => parseInt(a)-parseInt(b));
          // Time-aware x-axis (real elapsed time + outage break) when a mapping
          // exists, else "Frame N". pigAxis.frames is null at each break slot.
          const pigAxis = buildTimeAxis(frameKeys, restoreFrameTimestamps);

          const buildDistData = (property) => {
            return {
              labels: pigAxis.labels,
              datasets: allObjIds.map((objId, idx) => {
                const c = PIGMENT_COLORS[idx % PIGMENT_COLORS.length];
                const values = pigAxis.frames.map(fk => {
                  if (fk == null) return null; // outage break slot
                  const fd = frames[String(fk)]?.[objId];
                  return fd ? fd[property] : null;
                });
                return {
                  label: `Object ${objId}`,
                  data: values,
                  borderColor: `rgb(${c.r},${c.g},${c.b})`,
                  backgroundColor: `rgba(${c.r},${c.g},${c.b},0.15)`,
                  tension: 0.4,
                  pointRadius: 3,
                  pointHoverRadius: 6,
                  borderWidth: 2,
                  fill: false,
                  spanGaps: false
                };
              })
            };
          };

          const makeDistOptions = (title, yLabel, yMax) => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { position: 'top', labels: { color: '#ccc', usePointStyle: true, pointStyle: 'circle' } },
              tooltip: { backgroundColor: '#222', titleColor: '#fff', bodyColor: '#ccc' },
              zoom: {
                pan: { enabled: true, mode: 'x', modifierKey: null },
                zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' }
              }
            },
            scales: {
              x: { title: { display: true, text: pigAxis.axisTitle, color: '#aaa' }, ticks: { color: '#888' }, grid: { color: '#222' } },
              y: {
                title: { display: true, text: yLabel, color: '#aaa' },
                ticks: { color: '#888' },
                grid: { color: '#222' },
                beginAtZero: true,
                ...(yMax ? { max: yMax } : {})
              }
            }
          });

          const hueData = buildDistData('hue');
          const satData = buildDistData('saturation');
          const valData = buildDistData('value');
          const hueOpts = makeDistOptions('Hue Distribution', 'Hue (deg)', 180);
          const satOpts = makeDistOptions('Saturation Distribution', 'Saturation (0-255)', 255);
          const valOpts = makeDistOptions('Brightness Distribution', 'Value (0-255)', 255);

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Color Analysis</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="small" variant="text" onClick={() => colorChartRef.current?.resetZoom()} sx={{ fontSize: '0.7rem', color: '#888' }}>
                      Reset Zoom
                    </Button>
                    <Button size="small" variant="outlined" onClick={(e) => setColorDownloadAnchor(e.currentTarget)}>
                      Download
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ backgroundColor: '#00695c', '&:hover': { backgroundColor: '#004d40' }, color: '#fff' }}
                      onClick={() => { setRestorePigmentationData(parsedJson); setShowHSVVisualization(true); }}
                    >
                      Explore
                    </Button>
                    <Menu
                      anchorEl={colorDownloadAnchor}
                      open={Boolean(colorDownloadAnchor)}
                      onClose={() => setColorDownloadAnchor(null)}
                      PaperProps={{ sx: { bgcolor: '#1a1a1a', border: '1px solid #333', minWidth: 160 } }}
                    >
                      <MenuItem onClick={() => handleColorXlsxDownload(parsedJson, 'hsv')}
                        sx={{ color: '#ccc', fontSize: '0.85rem', '&:hover': { bgcolor: '#2a2a2a' } }}>
                        HSV
                      </MenuItem>
                      <MenuItem onClick={() => handleColorXlsxDownload(parsedJson, 'rgb')}
                        sx={{ color: '#ccc', fontSize: '0.85rem', '&:hover': { bgcolor: '#2a2a2a' } }}>
                        RGB
                      </MenuItem>
                      <MenuItem onClick={() => handleColorXlsxDownload(parsedJson, 'cielab')}
                        sx={{ color: '#ccc', fontSize: '0.85rem', '&:hover': { bgcolor: '#2a2a2a' } }}>
                        CIELAB
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>
                <Tabs value={restorePigTab} onChange={(_, v) => setRestorePigTab(v)} textColor="inherit" indicatorColor="primary"
                  sx={{ mb: 1, '& .MuiTab-root': { color: '#888', minHeight: 36, textTransform: 'none', fontSize: 13 }, '& .Mui-selected': { color: '#fff' } }}>
                  <Tab label="Hue" />
                  <Tab label="Saturation" />
                  <Tab label="Brightness" />
                </Tabs>
                <Box sx={{ flex: 1, minHeight: 0, bgcolor: '#000', borderRadius: 2, p: 2 }}>
                  {restorePigTab === 0 && <Line ref={colorChartRef} data={hueData} options={hueOpts} />}
                  {restorePigTab === 1 && <Line ref={colorChartRef} data={satData} options={satOpts} />}
                  {restorePigTab === 2 && <Line ref={colorChartRef} data={valData} options={valOpts} />}
                </Box>
                <Typography variant="caption" sx={{ color: '#666', mt: 1 }}>
                  Scroll to zoom, drag to pan | {allObjIds.length} object(s) | {frameKeys.length} frame(s)
                </Typography>
              </Box>
            </Box>
          );
        }

        // Rugosity / Texture data: Power Spectral Density + GLCM metrics
        // Distinguish from growth data: rugosity frames contain 'contrast' or 'frequency_log', growth has 'area_pixels'
        const hasRugosityFields = (data) => {
          if (Array.isArray(data) && data[0]?.frames?.[0]) {
            const f = data[0].frames[0];
            return f.contrast !== undefined || f.frequency_log !== undefined || f.homogeneity !== undefined;
          }
          return true; // legacy format, let the existing parser handle
        };
        const isRugosity = parsedJson && (restoreCategory === 'texture' || displayName.toLowerCase().includes('rugosity')) && hasRugosityFields(parsedJson);
        if (isRugosity && !isPigmentation) {
          // Supports both formats:
          //  New (array): [{obj_id, frames: [{frame_idx, frequency_log, radial_profile_log, contrast,...}]}]
          //  Legacy (object): {frame_idx, objects: {objId: {frequency_log, radial_profile_log}}}
          const TEXTURE_COLORS = ['#FF6384','#36A2EB','#FFCE56','#4BC0C0','#9966FF','#FF9F40','#8BC34A','#FF5722','#00BCD4','#E91E63'];
          let objectsMap = {}; // {objId: [{frame_idx, frequency_log, radial_profile_log, ...metrics}]}
          if (Array.isArray(parsedJson)) {
            parsedJson.forEach(obj => {
              objectsMap[String(obj.obj_id)] = obj.frames || [];
            });
          } else if (parsedJson.objects && typeof parsedJson.objects === 'object') {
            Object.entries(parsedJson.objects).forEach(([objId, data]) => {
              objectsMap[objId] = [{ frame_idx: parsedJson.frame_idx ?? 0, ...data }];
            });
          } else if (parsedJson.frames) {
            Object.entries(parsedJson.frames).forEach(([fi, objs]) => {
              Object.entries(objs).forEach(([objId, data]) => {
                if (!objectsMap[objId]) objectsMap[objId] = [];
                objectsMap[objId].push({ frame_idx: parseInt(fi), ...data });
              });
            });
          }

          const objIds = Object.keys(objectsMap).sort((a, b) => parseInt(a) - parseInt(b));
          // Use the last available frame for each object for the spectrum chart
          const spectrumDatasets = objIds.map((objId, idx) => {
            const frames = objectsMap[objId].sort((a, b) => a.frame_idx - b.frame_idx);
            const last = frames[frames.length - 1];
            if (!last?.frequency_log || !last?.radial_profile_log) return null;
            return {
              label: `Colony ${objId}`,
              data: last.frequency_log.map((f, i) => ({ x: f, y: last.radial_profile_log[i] })),
              borderColor: TEXTURE_COLORS[idx % TEXTURE_COLORS.length],
              backgroundColor: TEXTURE_COLORS[idx % TEXTURE_COLORS.length] + '33',
              showLine: true, borderWidth: 2, pointRadius: 0, tension: 0.3,
            };
          }).filter(Boolean);

          const spectrumOpts = {
            responsive: true, maintainAspectRatio: false,
            plugins: {
              legend: { position: 'top', labels: { color: '#ccc', usePointStyle: true } },
              tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: freq=${ctx.parsed.x.toFixed(2)}, power=${ctx.parsed.y.toFixed(2)}` } },
              zoom: { pan: { enabled: true, mode: 'xy' }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' } },
            },
            scales: {
              x: { type: 'linear', title: { display: true, text: 'log(Spatial Frequency)', color: '#aaa' }, ticks: { color: '#888' }, grid: { color: '#222' } },
              y: { type: 'linear', title: { display: true, text: 'log(Power)', color: '#aaa' }, ticks: { color: '#888' }, grid: { color: '#222' } },
            },
          };

          // GLCM metrics table (last frame per object)
          const GLCM_KEYS = ['contrast', 'homogeneity', 'energy', 'correlation', 'edge_density'];
          const GLCM_LABELS = ['Contrast', 'Homogeneity', 'Energy', 'Correlation', 'Edge Density'];

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Texture Analysis</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="text" onClick={() => textureChartRef.current?.resetZoom()} sx={{ fontSize: '0.7rem', color: '#888' }}>
                    Reset Zoom
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadRugosityXlsx(parsedJson)}>Download (XLSX)</Button>
                </Box>
              </Box>

              {/* Power Spectrum Chart */}
              {spectrumDatasets.length > 0 ? (
                <Box sx={{ flex: 1, minHeight: 300, bgcolor: '#000', borderRadius: 2, p: 2, mb: 2 }}>
                  <Scatter ref={textureChartRef} data={{ datasets: spectrumDatasets }} options={spectrumOpts} />
                </Box>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                  <Typography>No power spectrum data available for this analysis.</Typography>
                </Box>
              )}

              {/* GLCM Metrics Table */}
              {objIds.length > 0 && (
                <Box sx={{ bgcolor: '#111', borderRadius: 2, p: 2, overflow: 'auto' }}>
                  <Typography variant="subtitle2" sx={{ color: '#ccc', mb: 1 }}>GLCM Texture Metrics (Last Frame)</Typography>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { py: 0.8, px: 1, borderBottom: '1px solid #333', fontSize: '0.85rem' } }}>
                    <thead>
                      <tr>
                        <th style={{ color: '#aaa', textAlign: 'left' }}>Colony</th>
                        {GLCM_LABELS.map(l => <th key={l} style={{ color: '#aaa', textAlign: 'right' }}>{l}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {objIds.map((objId, idx) => {
                        const frames = objectsMap[objId].sort((a, b) => a.frame_idx - b.frame_idx);
                        const last = frames[frames.length - 1];
                        return (
                          <tr key={objId}>
                            <td>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TEXTURE_COLORS[idx % TEXTURE_COLORS.length], flexShrink: 0 }} />
                                <span style={{ color: '#ccc' }}>Colony {objId}</span>
                              </Box>
                            </td>
                            {GLCM_KEYS.map(k => (
                              <td key={k} style={{ color: '#eee', textAlign: 'right', fontFamily: 'monospace' }}>
                                {last?.[k] != null ? last[k].toFixed(4) : '-'}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Box>
                </Box>
              )}

              <Typography variant="caption" sx={{ color: '#666', mt: 1 }}>
                Scroll to zoom, drag to pan | {objIds.length} object(s)
              </Typography>
            </Box>
          );
        }

        // Spiral plate analysis results
        const isSpiral = parsedJson && restoreCategory === 'spiral' && parsedJson.plate_config && parsedJson.frames;
        if (isSpiral) {
          const spiralFrames = parsedJson.frames;
          const frameKeys = Object.keys(spiralFrames).sort((a, b) => Number(a) - Number(b));
          if (!frameKeys.length) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary">Spiral plate analysis not available</Typography>
              </Box>
            );
          }
          const spiralCfg = parsedJson.plate_config || {};
          const numSectors = spiralCfg.num_sectors || 6;
          const numQuadrants = spiralCfg.num_quadrants || 4;
          const SECTOR_COLORS = ['#FF6384','#36A2EB','#FFCE56','#4BC0C0','#9966FF','#FF9F40'];

          // Aggregate across all frames
          const allSectors = [];
          let totalColonies = 0;
          frameKeys.forEach((fk) => {
            const fd = spiralFrames[fk];
            (fd.sectors || []).forEach((s) => allSectors.push({ ...s, frame: fk }));
            totalColonies += fd.summary?.total_colonies || 0;
          });

          // Build aggregated sector table (across all quadrants, first frame)
          const firstFrameData = spiralFrames[frameKeys[0]];
          const sectors = firstFrameData.sectors || [];
          const aggBySector = {};
          sectors.forEach((s) => {
            if (!aggBySector[s.sector]) aggBySector[s.sector] = { sector: s.sector, totalCount: 0, totalVolume: 0 };
            aggBySector[s.sector].totalCount += s.colony_count;
            aggBySector[s.sector].totalVolume += s.volume_uL || 0;
          });
          const aggregated = Object.values(aggBySector).sort((a, b) => a.sector - b.sector);

          // Bar chart data
          const barData = aggregated.length ? {
            labels: aggregated.map((a) => `Sector ${a.sector}`),
            datasets: [
              {
                label: 'Colonies',
                data: aggregated.map((a) => a.totalCount),
                backgroundColor: aggregated.map((a) => SECTOR_COLORS[(a.sector - 1) % SECTOR_COLORS.length] + '99'),
                borderColor: aggregated.map((a) => SECTOR_COLORS[(a.sector - 1) % SECTOR_COLORS.length]),
                borderWidth: 1,
              },
              {
                label: 'CFU/mL',
                data: aggregated.map((a) => {
                  const volML = a.totalVolume / 1000;
                  return volML > 0 ? Math.round(a.totalCount / volML) : 0;
                }),
                backgroundColor: 'rgba(156, 39, 176, 0.3)',
                borderColor: '#9c27b0',
                borderWidth: 1,
                yAxisID: 'y1',
              },
            ],
          } : null;

          const barOpts = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { position: 'top', labels: { color: '#ccc' } },
              tooltip: { backgroundColor: '#222', titleColor: '#fff', bodyColor: '#ccc' },
            },
            scales: {
              x: { ticks: { color: '#888' }, grid: { color: '#222' } },
              y: { title: { display: true, text: 'Colony Count', color: '#aaa' }, ticks: { color: '#888' }, grid: { color: '#222' }, beginAtZero: true },
              y1: { position: 'right', title: { display: true, text: 'CFU/mL', color: '#aaa' }, ticks: { color: '#888' }, grid: { display: false }, beginAtZero: true },
            },
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflow: 'auto', p: 2 }}>
              {/* Summary header */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip label={`Total: ${totalColonies} colonies`} color="primary" variant="outlined" />
                <Chip label={`${numQuadrants} quadrants x ${numSectors} sectors`} variant="outlined" size="small" />
                <Chip label={`${frameKeys.length} frame(s)`} variant="outlined" size="small" />
                <Chip label={`Detection: ${parsedJson.detection_method || 'unknown'}`} variant="outlined" size="small" />
              </Box>

              {/* Aggregated sector table */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Aggregated (All Quadrants)</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'transparent' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Sector</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Colonies</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Volume (uL)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>CFU/mL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aggregated.map((a) => {
                      const volML = a.totalVolume / 1000;
                      const cfu = volML > 0 ? Math.round(a.totalCount / volML) : null;
                      return (
                        <TableRow key={a.sector} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SECTOR_COLORS[(a.sector - 1) % SECTOR_COLORS.length] }} />
                              Sector {a.sector}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{a.totalCount}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{a.totalVolume.toFixed(3)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{cfu != null ? cfu.toLocaleString() : '-'}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Bar chart */}
              {barData && (
                <Box sx={{ minHeight: 300, flex: 1 }}>
                  <Bar data={barData} options={barOpts} />
                </Box>
              )}

              {/* Per-quadrant details */}
              {frameKeys.length === 1 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Per-Quadrant Breakdown</Typography>
                  {(() => {
                    const quadrantMap = {};
                    sectors.forEach((s) => {
                      const key = `${s.quadrant}${s.quadrant_idx}`;
                      if (!quadrantMap[key]) quadrantMap[key] = { label: s.quadrant, quadrant_idx: s.quadrant_idx, sectors: [] };
                      quadrantMap[key].sectors.push(s);
                    });
                    return Object.values(quadrantMap).sort((a, b) => a.quadrant_idx - b.quadrant_idx).map((qGroup) => (
                      <Box key={qGroup.quadrant_idx} sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#aaa' }}>
                          Quadrant {qGroup.label} (#{qGroup.quadrant_idx})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'transparent' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Sector</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Colonies</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Volume (uL)</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>CFU/mL</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {qGroup.sectors.sort((a, b) => a.sector - b.sector).map((s) => (
                                <TableRow key={s.sector} hover>
                                  <TableCell>Sector {s.sector}</TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{s.colony_count}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{(s.volume_uL || 0).toFixed(3)}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                      {s.cfu_per_mL != null ? Math.round(s.cfu_per_mL).toLocaleString() : '-'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ));
                  })()}
                </>
              )}
            </Box>
          );
        }

        // Propagated Masks: Synthetic image stack with RLE decoding
        const isMasks = parsedJson && parsedJson.frames && (restoreCategory === 'objects' || displayName.toLowerCase().includes('propagated_masks') || displayName.toLowerCase().includes('mask'));
        if (isMasks && !isPigmentation) {
          const frames = parsedJson.frames;
          const frameKeys = Object.keys(frames).map(Number).sort((a,b) => a-b);
          const currentFrameKey = String(frameKeys[restoreMaskFrame] ?? frameKeys[0]);
          const frameData = frames[currentFrameKey] || {};
          const objectIds = Object.keys(frameData).sort((a,b) => parseInt(a)-parseInt(b));

          let imgW = 800, imgH = 600;
          if (objectIds.length > 0 && frameData[objectIds[0]]?.shape) {
            const s = frameData[objectIds[0]].shape;
            if (s.length === 3) { imgH = s[1]; imgW = s[2]; }
            else if (s.length === 2) { imgH = s[0]; imgW = s[1]; }
          }

          const maxW = 900, maxH = 600;
          const scale = Math.min(maxW / imgW, maxH / imgH, 1);
          const canvasW = Math.round(imgW * scale);
          const canvasH = Math.round(imgH * scale);

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, overflow: 'hidden' }}>
              {/* Header: title + buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexShrink: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Masks - Frame {currentFrameKey} ({restoreMaskFrame + 1}/{frameKeys.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadFile(url, displayName)}>
                    Download Raw Masks
                  </Button>
                  {getVideoFile() && (
                    <Button size="small" variant="outlined" onClick={handleDownloadVideo}>
                      Download Video
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleDownloadMaskedVideo}
                    disabled={maskedVideoState.status !== 'idle' && maskedVideoState.status !== 'ready'}
                    startIcon={
                      (maskedVideoState.status === 'generating' || maskedVideoState.status === 'checking' || maskedVideoState.status === 'downloading')
                        ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null
                    }
                    sx={{ bgcolor: '#7c4dff', '&:hover': { bgcolor: '#651fff' } }}
                  >
                    {maskedVideoState.status === 'idle' ? 'Masked Video'
                      : maskedVideoState.status === 'checking' ? 'Checking...'
                      : maskedVideoState.status === 'generating' ? `Generating ${Math.round(maskedVideoState.progress || 0)}%`
                      : maskedVideoState.status === 'downloading' ? 'Downloading...'
                      : 'Download Video'}
                  </Button>
                </Box>
              </Box>

              {/* Legend row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, flexShrink: 0 }}>
                {objectIds.map((objId) => {
                  const [cr, cg, cb] = generateObjectColor(parseInt(objId));
                  return (
                    <Box key={objId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: `rgb(${cr},${cg},${cb})` }} />
                      <Typography variant="caption" sx={{ color: '#ccc', fontSize: '0.7rem' }}>Obj {objId}</Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Video player (when ready) or static canvas */}
              {maskedVideoUrl ? (
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#000',
                  borderRadius: 2,
                  minHeight: 0,
                  overflow: 'hidden',
                }}>
                  <video
                    src={maskedVideoUrl}
                    controls
                    autoPlay
                    loop
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      borderRadius: 8,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              ) : (
                <>
                  {/* Canvas area - constrained to fill remaining space without overflow */}
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#000',
                    borderRadius: 2,
                    minHeight: 0,
                    overflow: 'hidden',
                    position: 'relative',
                  }}>
                    <canvas
                      ref={(node) => {
                        maskCanvasRef.current = node;
                        if (node && !maskCanvasMounted) {
                          setMaskCanvasMounted(true);
                        }
                      }}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: canvasW,
                        height: canvasH,
                        borderRadius: 8,
                        imageRendering: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </Box>

                  {/* Slider area - always below canvas */}
                  <Box sx={{ px: 1, pt: 1.5, flexShrink: 0 }}>
                    <Slider
                      value={restoreMaskFrame}
                      min={0}
                      max={frameKeys.length - 1}
                      step={1}
                      onChange={(_, v) => setRestoreMaskFrame(v)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `Frame ${frameKeys[v]}`}
                      sx={{ color: '#7c4dff' }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Frame {frameKeys[0]}</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {objectIds.length} object(s) | {frameKeys.length} frame(s)
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Frame {frameKeys[frameKeys.length-1]}</Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          );
        }

        // Fallback: generic JSON pretty-print
        let formatted;
        try { formatted = JSON.stringify(parsedJson, null, 2); } catch { formatted = restoreFileContent; }

        const handleDownloadGenericJsonXlsx = () => {
          const wb = XLSX.utils.book_new();
          if (Array.isArray(parsedJson) && parsedJson.length > 0 && typeof parsedJson[0] === 'object') {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(parsedJson), 'Data');
          } else {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['data'], [JSON.stringify(parsedJson)]]), 'Data');
          }
          const xlsxName = displayName.replace(/\.json$/i, '.xlsx');
          XLSX.writeFile(wb, xlsxName);
        };

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{displayName}</Typography>
              <Button size="small" variant="outlined" onClick={handleDownloadGenericJsonXlsx}>
                Download (XLSX)
              </Button>
            </Box>
            <Box sx={{
              flex: 1,
              overflow: 'auto',
              borderRadius: 2,
              border: '1px solid #333',
              bgcolor: '#0d1117',
              p: 2,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              color: '#8b949e',
              whiteSpace: 'pre'
            }}>
              {formatted}
            </Box>
          </Box>
        );
      }

      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{displayName}</Typography>
              <Button size="small" variant="outlined" onClick={() => handleDownloadFile(url, displayName)}>
                Download
              </Button>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000', borderRadius: 2 }}>
              <img src={url} alt={displayName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </Box>
          </Box>
        );
      }

      // Default: just offer download
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
          <Typography variant="h6">{displayName}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {restoreSelectedFile.size > 1048576
              ? `${(restoreSelectedFile.size / 1048576).toFixed(1)} MB`
              : `${(restoreSelectedFile.size / 1024).toFixed(1)} KB`}
          </Typography>
          <Button variant="contained" onClick={() => handleDownloadFile(url, displayName)}>
            Download File
          </Button>
        </Box>
      );
    };

    return (
      <div className='dashboard-container'>
        <Navbar />
        {/* Analysis info bar */}
        <Box sx={{
          px: 3, py: 1, display: 'flex', alignItems: 'center', gap: 2,
          borderBottom: '1px solid #333', bgcolor: '#1a1a1a'
        }}>
          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
            {analysisName}
          </Typography>
          <Typography variant="body2" sx={{ color: '#888' }}>
            {metadata.created_at ? new Date(metadata.created_at).toLocaleString() : ''}
          </Typography>
          {/* One export carrying every characteristic for this well, rather than
              a separate XLSX per graph. Lives on the info bar because it is
              about the WELL, not about whichever category is being viewed. */}
          <Box sx={{ ml: 'auto' }}>
            <MuiTooltip title="All characteristics for this well in one .xlsx (barrier plates export one file per component)">
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadWellData}
                sx={{ textTransform: 'none' }}
              >
                Download well data
              </Button>
            </MuiTooltip>
          </Box>
        </Box>

        <div className='main-container'>
          {/* Left panel - 4 fixed categories */}
          <div className='file-display'>
            <Typography variant="body2" sx={{ color: '#888', fontWeight: 600, mb: 1.5, px: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
              Results
            </Typography>
            {CATEGORIES.map((cat) => {
              const isActive = restoreCategory === cat.key;
              const file = getCategoryFile(cat);
              // In single-image colony-count restore the Area+Color panels
              // read from restoredColonies + restoredSingleFrameRle directly
              // (no growth/pigmentation file is ever written), so don't gate
              // them on getCategoryFile or surface a "Compute" affordance.
              const isAlwaysAvailable = isSingleImageColonyRestore &&
                (cat.key === 'growth' || cat.key === 'color');
              const isAvailable = !!file || isAlwaysAvailable;
              return (
                <Box
                  key={cat.key}
                  onClick={() => isAvailable && handleCategorySelect(cat.key)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1.2,
                    mx: 0.5,
                    mb: 0.5,
                    cursor: isAvailable ? 'pointer' : 'default',
                    borderRadius: 1,
                    bgcolor: isActive ? 'rgba(124, 61, 237, 0.18)' : 'transparent',
                    borderLeft: isActive ? '3px solid #7c3bed' : '3px solid transparent',
                    opacity: isAvailable ? 1 : 0.4,
                    '&:hover': isAvailable ? { bgcolor: isActive ? 'rgba(124, 61, 237, 0.18)' : 'rgba(255,255,255,0.05)' } : {},
                    transition: 'all 0.15s',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#c9a5ff' : (isAvailable ? '#eee' : '#555'),
                      fontSize: '0.9rem'
                    }}>
                      {cat.label}
                    </Typography>
                    {isAvailable ? (
                      <Typography variant="caption" sx={{ color: isActive ? '#9e7ccc' : '#666', fontSize: '0.72rem' }}>
                        {cat.description}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                        {cat.key !== 'experiment' && cat.key !== 'objects' && cat.key !== 'spiral' && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={(e) => { e.stopPropagation(); recomputeCategory(cat.key); }}
                            disabled={!!computingCategory}
                            sx={{ fontSize: '0.68rem', color: '#64b5f6', p: 0, minWidth: 'auto', textTransform: 'none', lineHeight: 1.2 }}
                          >
                            {computingCategory === cat.key ? 'Computing...' : 'Compute'}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </div>

          {/* Right canvas / visualization area */}
          <div className='image-container' style={{ backgroundColor: '#0a0a0a', borderRadius: 8, overflow: 'hidden' }}>
            {renderPreview()}
          </div>
        </div>

        <React.Suspense fallback={null}>
          <HSVVisualization
            isOpen={showHSVVisualization}
            onClose={() => { setShowHSVVisualization(false); setRestorePigmentationData(null); }}
            objectMeta={objectMeta}
            onApiCall={handlePigmentationApiCall}
            preloadedData={restorePigmentationData}
          />
        </React.Suspense>
      </div>
    );
  }

  // --- Lab Assistant orchestration ---------------------------------------
  // Structured object metadata so the agent resolves descriptions to ids by
  // reasoning (no grounding model): id, name, centroid, area (px).
  const buildAgentObjects = () => {
    const out = [];
    for (const [oid, m] of Object.entries(objectMasksRef.current || {})) {
      if (Number(oid) === DISH_OBJ_ID || !m || !m.mask) continue;
      const { mask, width } = m;
      let sx = 0; let sy = 0; let n = 0;
      for (let i = 0; i < mask.length; i++) { if (mask[i]) { sx += i % width; sy += (i / width) | 0; n++; } }
      if (!n) continue;
      out.push({ obj_id: Number(oid), name: objectMetaRef.current[oid]?.name || `Object ${oid}`,
        centroid: [Math.round(sx / n), Math.round(sy / n)], area: n });
    }
    return out;
  };

  // Map a tool call to an existing handler. Returns a short result string fed
  // back to the agent. Point-and-click handlers are reused verbatim.
  const executeAgentTool = async (name, args) => {
    try {
      switch (name) {
        case 'count_colonies':
          handleColonyCountStart();
          return `Opened the colony-count flow (${args.dish_shape || 'auto'}). Confirm the dish on the canvas to run the trained detector.`;
        case 'remove_objects': {
          const ids = (args.obj_ids || []).map(Number);
          for (const id of ids) { await deleteObject(id); }
          bumpUi();
          return `Removed ${ids.length} object(s): ${ids.join(', ') || 'none'}.`;
        }
        case 'rename_object':
          updateObjectMeta(Number(args.obj_id), { name: String(args.name) }); bumpUi();
          return `Renamed object ${args.obj_id} to "${args.name}".`;
        case 'select_objects': {
          const ids = (args.obj_ids || []).map(Number);
          if (ids.length) setCurrentObjectId(ids[0]);
          bumpUi();
          return `Selected ${ids.length} object(s): ${ids.join(', ')}${ids.length > 1 ? ' (showing the first)' : ''}.`;
        }
        case 'add_object': {
          const imgW = imageCacheRef.current?.width || 0;
          const imgH = imageCacheRef.current?.height || 0;
          if (!imgW || !imgH) return 'Cannot add an object: frame not ready.';
          const sz = Math.max(20, Math.round(Math.min(imgW, imgH) * 0.03));
          const x = Math.max(0, Math.round(args.x - sz / 2));
          const y = Math.max(0, Math.round(args.y - sz / 2));
          const existing = Object.keys(objectMetaRef.current || {})
            .concat(Object.keys(objectMasksRef.current || {})).map(Number).filter(Number.isFinite);
          const newId = Math.max(nextObjectIdRef.current, existing.reduce((a, b) => Math.max(a, b), 0) + 1);
          const geom = { kind: 'rect', x, y, w: sz, h: sz };
          objectMasksRef.current[newId] = { mask: rasteriseShape(geom, imgW, imgH), width: imgW, height: imgH };
          objectMetaRef.current[newId] = { name: args.name || `Object ${newId}` };
          shapeGeomRef.current[newId] = geom;
          nextObjectIdRef.current = newId + 1;
          setObjectMasksCompat({ ...objectMasksRef.current }); bumpUi();
          return `Added object ${newId} at (${Math.round(args.x)}, ${Math.round(args.y)}). Refine its mask with a click if needed.`;
        }
        case 'save_analysis':
          handleSaveAnalysis();
          return `Started saving the analysis${args.name ? ` as "${args.name}"` : ''}.`;
        case 'propagate':
          return "Propagation isn't available from chat yet; add prompts and use the Track button.";
        default:
          return `Unknown tool: ${name}.`;
      }
    } catch (e) {
      return `Tool ${name} failed: ${e.message}`;
    }
  };

  const handleUploadContext = (text, filename) => {
    experimentContextRef.current = text;
    setExperimentContextName(filename || 'context');
    enqueueSnackbar(`Loaded experiment context: ${filename}`, { variant: 'success' });
  };

  const handleAgentSend = async (text) => {
    if (!agentUrl) { enqueueSnackbar('Lab Assistant not configured', { variant: 'warning' }); return; }
    let convo = [...agentMessagesRef.current, { role: 'user', content: text }];
    setAgentConvo(convo); setAgentBusy(true);
    try {
      for (let step = 0; step < 5; step++) {
        const res = await fetch(`${agentUrl}/chat`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: convo, image_url: currentFrameUrl || null, objects: buildAgentObjects(),
            experiment_context: experimentContextRef.current || null,
            frame_info: { frame_count: frameCount, current_index: currentIndex },
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          // Reserved chat pod is off: show "unavailable" and flip the panel so
          // the user can keep analyzing without the agent.
          if (data?.detail === 'chat_unavailable') {
            setAgentAvailable(false);
            setAgentConvo(convo);
            return;
          }
          // On-demand GPU not ready: show a friendly message, not a raw error.
          if (data?.detail === 'servers_busy') {
            setAgentConvo([...convo, { role: 'assistant', content: 'Servers are busy right now (no GPU available). Please try again in a few minutes.' }]);
            return;
          }
          if (data?.detail === 'warming_up') {
            setAgentConvo([...convo, { role: 'assistant', content: 'Starting the assistant — the model is warming up (~1-2 min). Please resend your message shortly.' }]);
            return;
          }
          throw new Error(data?.detail || 'agent error');
        }
        const msg = data.message || {};
        convo = [...convo, msg]; setAgentConvo(convo);
        const calls = msg.tool_calls || [];
        if (!calls.length) break;
        for (const c of calls) {
          let a = {}; try { a = JSON.parse(c.function?.arguments || '{}'); } catch (_) { a = {}; }
          const result = await executeAgentTool(c.function?.name, a);
          convo = [...convo, { role: 'tool', tool_call_id: c.id, content: result }];
        }
        setAgentConvo(convo);
      }
    } catch (e) {
      setAgentConvo([...agentMessagesRef.current, { role: 'assistant', content: `Sorry, I hit an error: ${e.message}` }]);
    } finally { setAgentBusy(false); }
  };

  return (
    <Grid container spacing={0}>
      {/* Lab Assistant chat (mira04 dev only; hidden unless REACT_APP_AGENT_URL set) */}
      {agentUrl && (
        <Box sx={{
          position: 'fixed', top: 64, right: 12, zIndex: 1300, width: 360, height: 460,
          bgcolor: 'background.paper', boxShadow: 4, borderRadius: 1.5, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <ChatAgentPanel
            messages={agentMessages}
            busy={agentBusy}
            available={agentAvailable}
            onSend={handleAgentSend}
            onUploadContext={handleUploadContext}
            contextName={experimentContextName}
          />
        </Box>
      )}
      {/* Global Top Loader */}
      {isGlobalLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '20px',
            color: '#fff',
          }}
        >
          <CircularProgress size={60} thickness={5} />
          <p style={{ marginTop: 16, fontSize: '1.1rem' }}>
           
            Hang tight… updating the mask from your selection — {autoSegProgress.progress}% complete.
          </p>
        </div>
      )}

      {/* ===== EDITING / REVIEW LAYOUT (2 ROWS) ===== */}
      {!showTimelineWithMasks && (
        <>
          {/* Row 1: Canvas (center) + Timeline (right) */}
          <Grid item xs={12}>
            <Box
              sx={{
                height: 'calc(100vh - 104px)', // leave room for the buttons row
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                overflow: 'hidden',
                bgcolor: 'background.default',
              }}
            >
              {/* Video + Canvas overlay area */}
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: 'black',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Frame canvas layer (replaces <video> element) */}
                <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                  <FrameCanvas
                    ref={frameCanvasRef}
                    frameBitmap={currentBitmap}
                    frameSize={frameSize}
                    overlayRef={canvasRef}
                    cursorStyle={(spiralAlignMode || rectAlignMode) ? 'grab' : dishSelectMode ? 'cell' : drawingMode !== 'sam' ? 'crosshair' : 'crosshair'}
                    onCanvasClick={(spiralAlignMode || rectAlignMode) ? undefined : (roiMode || drawingMode !== 'sam') ? undefined : handleFrameCanvasClick}
                    onCanvasMouseDown={spiralAlignMode ? handleSpiralMouseDown : rectAlignMode ? handleRectMouseDown : (roiMode || drawingMode !== 'sam') ? handleFrameCanvasMouseDown : undefined}
                    onCanvasMouseMove={spiralAlignMode ? handleSpiralMouseMove : rectAlignMode ? handleRectMouseMove : (roiMode || drawingMode !== 'sam') ? handleFrameCanvasMouseMove : undefined}
                    onCanvasMouseUp={spiralAlignMode ? handleSpiralMouseUp : rectAlignMode ? handleRectMouseUp : (roiMode || drawingMode !== 'sam') ? handleFrameCanvasMouseUp : undefined}
                    onCanvasWheel={spiralAlignMode ? handleSpiralWheel : undefined}
                    style={{ pointerEvents: serverReady || spiralAlignMode || rectAlignMode ? 'auto' : 'none' }}
                  />

                  {/* Circle alignment floating toolbar (Spiral / 90mm) */}
                  {spiralAlignMode && (
                    <AlignCircleToolbar
                      countMode={countMode}
                      onConfirm={handleAlignConfirm}
                      onCancel={handleAlignCancel}
                    />
                  )}

                  {/* Rectangular alignment floating toolbar */}
                  {rectAlignMode && (
                    <AlignRectangleToolbar
                      rectGrid={rectGrid}
                      onGridSizeChange={setGridSize}
                      onAddHorizontal={addHorizontalLine}
                      onAddVertical={addVerticalLine}
                      onConfirm={handleAlignConfirm}
                      onCancel={handleAlignCancel}
                    />
                  )}

                  {/* Timestamp badge */}
                  <TimestampOverlay
                    currentIndex={currentIndex}
                    frameCount={frameCount}
                    frameTimestamps={frameTimestamps}
                  />

                  {/* Dish selection overlay */}
                  {(dishSelectMode || dishMaskData) && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      bgcolor: 'rgba(0,0,0,0.75)',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      zIndex: 10,
                      border: '1px solid #444',
                    }}>
                      {dishSelectMode && (
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          Click on the dish
                        </Typography>
                      )}
                      {dishMaskData && !dishSelectMode && (
                        <>
                          <Typography variant="body2" sx={{ color: '#aaa' }}>
                            Dish selected —
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={handleAutoSegment}
                            sx={{ py: 0.3, px: 1.5, fontSize: '0.8rem' }}
                          >
                            Detect Colonies
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              delete objectMasksRef.current[DISH_OBJ_ID];
                              setDishMaskData(null);
                              bumpUi();
                              setDishSelectMode(true);
                              enqueueSnackbar('Click on the dish again.', { variant: 'info', autoHideDuration: 2000 });
                            }}
                            sx={{ py: 0.3, px: 1.5, fontSize: '0.8rem', borderColor: '#555', color: '#aaa' }}
                          >
                            Redo
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                              delete objectMasksRef.current[DISH_OBJ_ID];
                              setDishMaskData(null);
                              bumpUi();
                            }}
                            sx={{ py: 0.3, px: 1, fontSize: '0.75rem', color: '#666' }}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </Box>
                  )}

                  {/* Animated Progress Bar (top overlay) */}
                  {(propagationProgress.inProgress || drawingMasks) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        textAlign: 'center',
                        py: 1.5,
                        px: 2,
                        zIndex: 10,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ display: 'flex', justifyContent: 'center' }}>
                        {propagationProgress.inProgress
                          ? 'Processing Segmentation Across Frames...'
                          : 'Finalizing masks for all frames...'}
                      </Typography>
                      <Box sx={{ mt: 1.5, width: '80%', mx: 'auto' }}>
                        <LinearProgress
                          variant={propagationProgress.inProgress ? 'determinate' : 'indeterminate'}
                          value={propagationProgress.progress || 0}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: '#424242',
                            '& .MuiLinearProgress-bar': { transition: 'width 0.5s ease-in-out' },
                          }}
                        />
                        {propagationProgress.inProgress && (
                          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#ccc' }}>
                            {`Progress: ${Math.round(propagationProgress.progress)}% — Frame ${propagationProgress.message || '?'}`}
                          </Typography>
                        )}
                        {drawingMasks && (
                          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#ccc' }}>
                            This usually takes just a few seconds.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Frame scrubber */}
                <FrameScrubber
                  currentIndex={currentIndex}
                  frameCount={frameCount || localImages.length || 1}
                  fps={fps}
                  isPlaying={isPlaying}
                  onGoToFrame={goToFrame}
                  onTogglePlay={togglePlay}
                  frameTimestamps={frameTimestamps}
                  propagateStart={singleImageMode ? undefined : propagateStart}
                  propagateEnd={singleImageMode ? undefined : propagateEnd}
                  onPropagateStartChange={singleImageMode ? undefined : setPropagateStart}
                  onPropagateEndChange={singleImageMode ? undefined : setPropagateEnd}
                />
              </Box>

              {/* Single-image colony-count side panel: docked Colonies list on
                  top, scatter/distribution plots below. Lives alongside the
                  canvas (flex row sibling) so it never overlays the dish. */}
              {singleImageMode && (
                <Box
                  sx={{
                    width: 340,
                    flexShrink: 0,
                    borderLeft: '1px solid #2a2a2a',
                    bgcolor: '#1c1c1e',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}
                >
                  <Box sx={{ flex: '0 0 50%', minHeight: 200, borderBottom: '1px solid #2a2a2a' }}>
                    <ObjectManagerPanel
                      docked
                      objectMeta={objectMeta}
                      setObjectMeta={setObjectMeta}
                      deleteObject={deleteObject}
                      currentObjectId={currentObjectId}
                      setCurrentObjectId={setCurrentObjectId}
                      currentFrameIndex={currentIndex}
                      objectMasksForFrame={{ ...objectMasksRef.current }}
                      showSelectExisting={false}
                      onAddObject={handleNewObject}
                      onHoverObject={setHoveredObjectId}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 240 }}>
                    <ColonySummaryPanel
                      cpuUrl={cpuUrl}
                      sessionId={sessionIdQuery || sessionStorage.getItem('sessionId')}
                      frameIdx={toBackendIndex(currentIndex)}
                      s3Path={initialPath || (multiPaths.length > 0 ? multiPaths[0] : '')}
                      onHoverObject={setHoveredObjectId}
                      colonyCount={Object.keys(objectMasksRef.current || {}).filter(k => k !== String(DISH_OBJ_ID)).length}
                      // Refresh only when the colony set membership changes,
                      // never on every uiTick (uiTick bumps on hover/redraw
                      // too — passing it here flooded /phenotypic/measure).
                      refreshTick={objectIds.length}
                      // Single-image mode: masks live only in memory until the
                      // user explicitly saves. Encode them inline so the
                      // backend doesn't try (and fail) to read S3 RLE that
                      // doesn't exist yet.
                      getInlineMasks={() => {
                        const out = [];
                        const entries = Object.entries(objectMasksRef.current || {});
                        for (const [oid, m] of entries) {
                          if (String(oid) === String(DISH_OBJ_ID)) continue;
                          if (!m || !m.mask || !m.width || !m.height) continue;
                          try {
                            const rle = encodeCOCORLE(m.mask, m.width, m.height);
                            out.push({ obj_id: parseInt(oid, 10), rle });
                          } catch (e) { /* skip bad masks */ }
                        }
                        return out;
                      }}
                      onSave={handleSaveColonyCount}
                      saving={isSavingColonyCount}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Row 2: Toolbar */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                backgroundColor: '#0a0a0a',
                borderTop: '1px solid #222',
                color: 'white',
                '& .toolbar-group': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  borderRight: '1px solid #333',
                  '&:last-of-type': { borderRight: 'none' },
                },
                '& button': {
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: '6px',
                  py: 0.5,
                  px: 1.5,
                  minWidth: 'auto',
                },
              }}
            >
              {/* Detection mode selector */}
              <Box className="toolbar-group">
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mr: 0.5 }}>Mode</Typography>
                <Button
                  variant={drawingMode === 'sam' ? 'contained' : 'outlined'}
                  onClick={() => { setDrawingMode('sam'); setShapeDraft(null); }}
                  size="small"
                  sx={drawingMode === 'sam' ? { bgcolor: '#1976d2' } : { color: '#aaa', borderColor: '#555' }}
                >
                  Point
                </Button>
                <Button
                  variant={drawingMode === 'rect' ? 'contained' : 'outlined'}
                  onClick={() => { setDrawingMode('rect'); setShapeDraft(null); }}
                  size="small"
                  sx={drawingMode === 'rect' ? { bgcolor: '#1976d2' } : { color: '#aaa', borderColor: '#555' }}
                >
                  Rectangle
                </Button>
                <Button
                  variant={drawingMode === 'ellipse' ? 'contained' : 'outlined'}
                  onClick={() => { setDrawingMode('ellipse'); setShapeDraft(null); }}
                  size="small"
                  sx={drawingMode === 'ellipse' ? { bgcolor: '#1976d2' } : { color: '#aaa', borderColor: '#555' }}
                >
                  Ellipse
                </Button>
              </Box>

              {/* Point mode */}
              <Box className="toolbar-group">
                <Button
                  variant={mode === 1 ? 'contained' : 'outlined'}
                  color="success"
                  onClick={() => setMode(1)}
                  size="small"
                >
                  + Include
                </Button>
                <Button
                  variant={mode === 0 ? 'contained' : 'outlined'}
                  color="error"
                  onClick={() => setMode(0)}
                  size="small"
                >
                  - Exclude
                </Button>
                <Button variant="outlined" onClick={clearPoints} size="small" sx={{ color: '#aaa', borderColor: '#555' }}>
                  Clear
                </Button>
              </Box>

              {/* Mask visibility toggle */}
              <Box className="toolbar-group">
                <Button
                  variant={showMasks ? 'contained' : 'outlined'}
                  onClick={() => setShowMasks(v => !v)}
                  size="small"
                  sx={{ color: showMasks ? '#fff' : '#aaa', borderColor: '#555' }}
                >
                  {showMasks ? 'Masks On' : 'Masks Off'}
                </Button>
              </Box>

              {/* ROI & Auto-detection */}
              <Box className="toolbar-group">
                <ColonyPipelinePicker
                  value={colonyPipeline}
                  onChange={setColonyPipeline}
                  onRun={handleColonyCountStart}
                />
              </Box>

              {/* Tracking — hidden in single-image mode. Start/End markers
                  live on the timeline scrubber; this group keeps Stride + Track. */}
              {!singleImageMode && (
              <Box className="toolbar-group">
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mr: 0.5 }}>
                  Stride
                </Typography>
                <Box sx={{ width: 100 }}>
                  <Slider
                    size="small"
                    value={frameStride}
                    min={1}
                    max={30}
                    step={1}
                    onChange={(_, v) => setFrameStride(Array.isArray(v) ? v[0] : v)}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Button variant="contained" onClick={triggerPropagation} disabled={propagationProgress.inProgress} size="small">
                  Track
                </Button>
              </Box>
              )}

              {/* Session — hidden in single-image mode */}
              {!singleImageMode && (
              <Box className="toolbar-group" sx={{ borderRight: 'none !important' }}>
                <Button variant="outlined" color="error" onClick={handleEndSession} disabled={isEnding} size="small">
                  {isEnding ? 'Ending...' : 'End Session'}
                </Button>
              </Box>
              )}
            </Box>
          </Grid>
          {/* Multi-image (timelapse) mode keeps the floating ObjectManagerPanel.
              Single-image mode renders a docked side panel further down so the
              colony summary plots can sit next to it; this block stays hidden
              there to avoid duplicates. */}
          {!singleImageMode && (
            <ObjectManagerPanel
              objectMeta={objectMeta}               // snapshot from ref
              setObjectMeta={setObjectMeta}         // adapter that mutates ref + bumpUi
              deleteObject={deleteObject}
              currentObjectId={currentObjectId}
              setCurrentObjectId={setCurrentObjectId}
              currentFrameIndex={currentIndex}
              objectMasksForFrame={{ ...objectMasksRef.current }}
              showSelectExisting={false}            // Hide "Select Existing Object" for AI Analysis
              onAddObject={handleNewObject}
              onHoverObject={setHoveredObjectId}
            />
          )}
        </>

      )}



      {/* ===== MASKS-COMPLETE VIEW (still: timeline right, buttons below) ===== */}
      {showTimelineWithMasks && (
        <>
          {/* Row 1: Image (center) + Timeline (right) */}
          <Grid item xs={12}>
            <Box
              sx={{
                height: 'calc(100vh - 120px)',
                display: 'flex',
                flexDirection: 'row',
                overflow: 'hidden',
              }}
            >
              {/* Frame canvas + overlay (reused from editing mode) */}
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: 'black',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Per-frame correction toolbar — lives ABOVE the canvas (in the
                    column's normal flow) so it never sits over the petri-dish image.
                    Collapsible via the chevron so users can reclaim the strip when
                    they don't need it. */}
                {!spiralAlignMode && !rectAlignMode && (
                  <Box
                    sx={{
                      bgcolor: '#0a0a0a',
                      borderBottom: '1px solid #2a2a2a',
                      px: 1.5,
                      py: editToolbarCollapsed ? 0.4 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexShrink: 0,
                      minHeight: editToolbarCollapsed ? 28 : 44,
                    }}
                  >
                    {editToolbarCollapsed ? (
                      <>
                        <Typography variant="caption" sx={{ color: '#888', flex: 1 }}>
                          Per-frame edit (hidden)
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setEditToolbarCollapsed(false)}
                          sx={{
                            minWidth: 0, py: 0, px: 0.8, fontSize: '0.7rem',
                            color: '#aaa', textTransform: 'none',
                          }}
                        >
                          ▾ Show
                        </Button>
                      </>
                    ) : !editFrameMode ? (
                      <>
                        <Typography variant="caption" sx={{ color: '#bbb', mr: 0.5 }}>
                          Mask on frame {toBackendIndex(currentIndex)} looks wrong?
                        </Typography>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={null}
                          onChange={(_, v) => v && enterEditFrame(v)}
                          sx={{
                            '& .MuiToggleButton-root': {
                              color: '#ddd',
                              borderColor: '#444',
                              py: 0.25,
                              px: 1,
                              fontSize: '0.72rem',
                              textTransform: 'none',
                            },
                          }}
                        >
                          <ToggleButton value="retrack">Re-track from here</ToggleButton>
                          <ToggleButton value="single">Edit this frame only</ToggleButton>
                        </ToggleButtonGroup>
                        <Box sx={{ flex: 1 }} />
                        <Button
                          size="small"
                          onClick={() => setEditToolbarCollapsed(true)}
                          sx={{
                            minWidth: 0, py: 0, px: 0.8, fontSize: '0.7rem',
                            color: '#666', textTransform: 'none',
                          }}
                          title="Hide toolbar"
                        >
                          ▴ Hide
                        </Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="caption" sx={{ color: '#fbbf24', fontWeight: 600 }}>
                          {editFrameMode === 'retrack' ? 'Re-track' : 'Single-frame edit'} · frame {toBackendIndex(currentIndex)}
                        </Typography>
                        {/* Surface which object the next click/draw will
                            target. Without this, the user has to guess
                            which mask of the N propagated objects gets the
                            edit. Pick a different object in the side panel
                            to redirect edits. */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#90caf9', fontWeight: 600, ml: 0.5,
                            px: 0.75, py: 0.1, borderRadius: 0.5,
                            border: '1px solid rgba(144,202,249,0.4)',
                            bgcolor: 'rgba(144,202,249,0.08)',
                          }}
                          title="Pick a different object in the right panel to redirect edits."
                        >
                          editing: {objectMeta?.[currentObjectId]?.name || `Object ${currentObjectId}`}
                        </Typography>
                        <Button
                          size="small"
                          variant={mode === 1 ? 'contained' : 'outlined'}
                          color="success"
                          onClick={() => setMode(1)}
                          disabled={applyingCorrection}
                          sx={{ py: 0.25, px: 1.2, fontSize: '0.72rem', textTransform: 'none', ml: 0.5 }}
                        >
                          + Include
                        </Button>
                        <Button
                          size="small"
                          variant={mode === 0 ? 'contained' : 'outlined'}
                          color="error"
                          onClick={() => setMode(0)}
                          disabled={applyingCorrection}
                          sx={{ py: 0.25, px: 1.2, fontSize: '0.72rem', textTransform: 'none' }}
                        >
                          − Exclude
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={clearPoints}
                          disabled={applyingCorrection}
                          sx={{
                            py: 0.25, px: 1.2, fontSize: '0.72rem', textTransform: 'none',
                            color: '#aaa', borderColor: '#555',
                          }}
                        >
                          Clear
                        </Button>
                        <Box sx={{ width: 1, height: 18, bgcolor: '#333', mx: 0.5 }} />
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={applyEditFrame}
                          disabled={applyingCorrection}
                          sx={{ py: 0.25, px: 1.2, fontSize: '0.72rem', textTransform: 'none' }}
                        >
                          {applyingCorrection ? 'Applying…' : 'Apply'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={cancelEditFrame}
                          disabled={applyingCorrection}
                          sx={{
                            py: 0.25, px: 1.2, fontSize: '0.72rem', textTransform: 'none',
                            color: '#ddd', borderColor: '#555',
                          }}
                        >
                          Cancel
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button
                          size="small"
                          onClick={() => setEditToolbarCollapsed(true)}
                          disabled={applyingCorrection}
                          sx={{
                            minWidth: 0, py: 0, px: 0.8, fontSize: '0.7rem',
                            color: '#666', textTransform: 'none',
                          }}
                          title="Hide toolbar"
                        >
                          ▴ Hide
                        </Button>
                      </>
                    )}
                  </Box>
                )}
                <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                  <FrameCanvas
                    ref={frameCanvasRef}
                    frameBitmap={currentBitmap}
                    frameSize={frameSize}
                    overlayRef={canvasRef}
                    cursorStyle={(spiralAlignMode || rectAlignMode) ? 'grab' : editFrameMode ? 'crosshair' : 'default'}
                    onCanvasClick={(spiralAlignMode || rectAlignMode) ? undefined : editFrameMode && drawingMode === 'sam' && !roiMode ? handleFrameCanvasClick : undefined}
                    onCanvasMouseDown={spiralAlignMode ? handleSpiralMouseDown : rectAlignMode ? handleRectMouseDown : editFrameMode && (roiMode || drawingMode !== 'sam') ? handleFrameCanvasMouseDown : undefined}
                    onCanvasMouseMove={spiralAlignMode ? handleSpiralMouseMove : rectAlignMode ? handleRectMouseMove : editFrameMode && (roiMode || drawingMode !== 'sam') ? handleFrameCanvasMouseMove : undefined}
                    onCanvasMouseUp={spiralAlignMode ? handleSpiralMouseUp : rectAlignMode ? handleRectMouseUp : editFrameMode && (roiMode || drawingMode !== 'sam') ? handleFrameCanvasMouseUp : undefined}
                    onCanvasWheel={spiralAlignMode ? handleSpiralWheel : undefined}
                    style={{ pointerEvents: 'auto' }}
                  />
                  {/* Per-frame correction toolbar has been moved OUT of the canvas
                      overlay (see the Box rendered just above this canvas wrapper)
                      so it no longer blocks the petri-dish area. */}
                  {spiralAlignMode && (
                    <AlignCircleToolbar
                      countMode={countMode}
                      onConfirm={handleAlignConfirm}
                      onCancel={handleAlignCancel}
                    />
                  )}
                  {rectAlignMode && (
                    <AlignRectangleToolbar
                      rectGrid={rectGrid}
                      onGridSizeChange={setGridSize}
                      onAddHorizontal={addHorizontalLine}
                      onAddVertical={addVerticalLine}
                      onConfirm={handleAlignConfirm}
                      onCancel={handleAlignCancel}
                    />
                  )}
                  <TimestampOverlay
                    currentIndex={currentIndex}
                    frameCount={frameCount}
                    frameTimestamps={frameTimestamps}
                  />
                </Box>
                {/* Post-propagation scrubber: only step through frames that have
                    masks (propagation respects stride + [start, end] window).
                    The slider is positional (0..N-1 of masked frames); we map
                    each position back to the real display index so currentBitmap
                    and the mask overlay stay in sync. */}
                {(() => {
                  const hasMasked = maskedDisplayIndices.length > 0;
                  const scrubFrameCount = hasMasked
                    ? maskedDisplayIndices.length
                    : (frameCount || localImages.length || 1);
                  let scrubCurrent = hasMasked
                    ? maskedDisplayIndices.indexOf(currentIndex)
                    : currentIndex;
                  if (scrubCurrent < 0 && hasMasked) {
                    // currentIndex isn't masked — snap visually to nearest;
                    // the useEffect above will also move the real frame.
                    let nearestPos = 0;
                    let best = Math.abs(currentIndex - maskedDisplayIndices[0]);
                    for (let i = 1; i < maskedDisplayIndices.length; i++) {
                      const d = Math.abs(currentIndex - maskedDisplayIndices[i]);
                      if (d < best) { best = d; nearestPos = i; }
                    }
                    scrubCurrent = nearestPos;
                  }
                  return (
                    <FrameScrubber
                      currentIndex={scrubCurrent}
                      frameCount={scrubFrameCount}
                      fps={fps}
                      isPlaying={isPlaying}
                      onGoToFrame={(pos) => goToFrame(
                        hasMasked ? maskedDisplayIndices[pos] ?? maskedDisplayIndices[0] : pos
                      )}
                      onTogglePlay={togglePlay}
                      frameTimestamps={frameTimestamps}
                    />
                  );
                })()}
              </Box>
              {/* During per-frame correction, dock the ObjectManagerPanel
                  alongside the canvas so the user can pick WHICH object's
                  mask the brush/SAM clicks should affect. The drawing code
                  paths target `currentObjectId` — without a visible picker
                  on this view, propagation-edit mode silently routes all
                  edits to whichever object was last selected (default = 1).
                  Multi-image mode only: single-image mode already shows the
                  docked panel further up the tree. */}
              {editFrameMode && !singleImageMode && (
                <Box
                  sx={{
                    width: 320,
                    flexShrink: 0,
                    borderLeft: '1px solid #2a2a2a',
                    bgcolor: '#1c1c1e',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}
                >
                  <ObjectManagerPanel
                    docked
                    objectMeta={objectMeta}
                    setObjectMeta={setObjectMeta}
                    deleteObject={deleteObject}
                    currentObjectId={currentObjectId}
                    setCurrentObjectId={setCurrentObjectId}
                    currentFrameIndex={currentIndex}
                    objectMasksForFrame={{ ...objectMasksRef.current }}
                    showSelectExisting={false}
                    onAddObject={handleNewObject}
                    onHoverObject={setHoveredObjectId}
                  />
                </Box>
              )}
            </Box>
          </Grid>

          {/* Row 2: Analysis Toolbar */}
          <Grid item xs={12}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#0a0a0a',
                borderTop: '1px solid #222',
                '& .toolbar-group': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  borderRight: '1px solid #333',
                  '&:last-of-type': { borderRight: 'none' },
                },
                '& button': {
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: '6px',
                  py: 0.5,
                  px: 1.5,
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {/* Analysis actions */}
              <Box className="toolbar-group">
                <Button variant="contained" color="success" onClick={handleGrowthAnalysis} size="small">
                  Growth
                </Button>
                <Button variant="contained" size="small"
                  sx={{ backgroundColor: '#9c27b0', '&:hover': { backgroundColor: '#7b1fa2' }, color: '#fff' }}
                  onClick={handlePigmentationAnalysis}
                >
                  Color
                </Button>
                <Button variant="outlined" color="primary" onClick={handleRugosityAnalysis} size="small">
                  Rugosity
                </Button>
                <ColonyPipelinePicker
                  value={colonyPipeline}
                  onChange={setColonyPipeline}
                  onRun={handleColonyCountStart}
                />
                <Button variant="contained" size="small"
                  sx={{ backgroundColor: '#00695c', '&:hover': { backgroundColor: '#004d40' }, color: '#fff' }}
                  onClick={handleHSVVisualization}
                >
                  Explore
                </Button>
                <Button
                  variant="contained" size="small"
                  disabled={maskedVideoState.status !== 'idle' && maskedVideoState.status !== 'ready'}
                  startIcon={
                    (maskedVideoState.status === 'generating' || maskedVideoState.status === 'checking' || maskedVideoState.status === 'downloading')
                      ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null
                  }
                  sx={{ backgroundColor: '#7c4dff', '&:hover': { backgroundColor: '#651fff' }, color: '#fff' }}
                  onClick={async () => {
                    const sid = sessionIdQuery || sessionStorage.getItem('sessionId');
                    if (maskedVideoState.status === 'ready' && maskedVideoUrl) {
                      handleDownloadMaskedVideoUrl(maskedVideoUrl, `masked_video_${sid}.mp4`);
                      return;
                    }
                    const url = await fetchOrGenerateMaskedVideo(sid, 'petriboothproject');
                    if (url) handleDownloadMaskedVideoUrl(url, `masked_video_${sid}.mp4`);
                  }}
                >
                  {maskedVideoState.status === 'idle' ? 'Masked Video'
                    : maskedVideoState.status === 'checking' ? 'Checking...'
                    : maskedVideoState.status === 'generating' ? `Generating ${Math.round(maskedVideoState.progress || 0)}%`
                    : maskedVideoState.status === 'downloading' ? 'Downloading...'
                    : 'Download Video'}
                </Button>
              </Box>

              {/* Save & Export */}
              <Box className="toolbar-group">
                <Button variant="contained" size="small"
                  sx={{ backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' }, color: '#fff' }}
                  onClick={() => setShowSaveDialog(true)}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>

              {/* Session controls */}
              <Box className="toolbar-group" sx={{ borderRight: 'none !important' }}>
                <Button variant="outlined" color="primary" onClick={handleRedo} size="small">
                  Redo Masks
                </Button>
                <Button variant="outlined" color="error" onClick={handleEndSession} disabled={isEnding} size="small">
                  {isEnding ? 'Ending...' : 'End Session'}
                </Button>
              </Box>
            </Box>
          </Grid>
        </>
      )}

      {/* Charts/Modals */}
      <GrowthAnalysisChart
        isOpen={showGrowthChart}
        onClose={() => setShowGrowthChart(false)}
        csvUrl={growthData.csvUrl}
        objectMeta={objectMeta}
        timeInterval={growthData.timeInterval}
      />
      <RugosityAnalysisChart
        isOpen={showRugosityChart}
        onClose={() => setShowRugosityChart(false)}
        initialFrameIdx={localImages.length - 1}
        maxFrameIdx={localImages.length - 1}
        objectMeta={objectMeta}
        onApiCall={handleRugosityApiCall}
        cpuUrl={cpuUrl}
        sessionId={sessionIdQuery || sessionStorage.getItem('sessionId')}
        s3Path={initialPath || (multiPaths.length > 0 ? multiPaths[0] : '')}
        availableFrames={Object.keys(propagatedMasks)
          .map(Number)
          .sort((a, b) => a - b)}
      />

      <PigmentationAnalysisChart
        isOpen={showPigmentationChart}
        onClose={() => setShowPigmentationChart(false)}
        objectMeta={objectMeta}
        onApiCall={handlePigmentationApiCall}
      />

      <React.Suspense fallback={null}>
        <HSVVisualization
          isOpen={showHSVVisualization}
          onClose={() => { setShowHSVVisualization(false); setRestorePigmentationData(null); }}
          objectMeta={objectMeta}
          onApiCall={handlePigmentationApiCall}
          preloadedData={restorePigmentationData}
        />
      </React.Suspense>

      <SpiralAnalysisChart
        isOpen={showSpiralChart}
        onClose={() => setShowSpiralChart(false)}
        objectMeta={objectMeta}
        onApiCall={handleSpiralApiCall}
        initialFrameIdx={currentIndex}
        maxFrameIdx={localImages.length - 1}
        availableFrames={Array.from({ length: localImages.length }, (_, i) => i)}
        plateConfigOverride={spiralPlateConfig}
        frameImage={currentBitmap}
        autoDetectedColonies={spiralAutoColonies}
      />

      {/* Colony Count: dish-shape chooser */}
      <Dialog
        open={showCountModeDialog}
        onClose={() => setShowCountModeDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Select Dish Shape</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Choose the dish geometry for colony counting.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button variant="outlined" fullWidth size="large"
              onClick={() => handleCountModeSelect('90mm')}>
              90mm Round Dish
            </Button>
            <Button variant="outlined" fullWidth size="large"
              onClick={() => handleCountModeSelect('spiral')}>
              Spiral Plate
            </Button>
            <Button variant="outlined" fullWidth size="large"
              onClick={() => handleCountModeSelect('rectangular')}>
              Rectangular Dish
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCountModeDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Threshold dialog: opens after dish-shape selection when the
          colony pipeline is 'morphological'. Runs /morph_preview live as the
          user drags sliders, then dispatches /automask (90mm) or
          /automask_grid (rectangular) on Accept. */}
      <MorphThresholdDialog
        open={Boolean(morphDialogState)}
        onClose={() => setMorphDialogState(null)}
        gpuUrl={gpuUrl}
        sessionId={sessionIdQuery || sessionStorage.getItem('sessionId')}
        frameIdx={toBackendIndex(currentIndex)}
        roi={morphDialogState?.roi || null}
        frameBitmap={frameCanvasRef.current?.frameCanvas || null}
        onRun={async ({ pipeline_kwargs }) => {
          const state = morphDialogState;
          setMorphDialogState(null);
          const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
          if (!sessionId || !state) return;
          try {
            setIsGlobalLoading(true);
            setAutoSegProgress({ inProgress: true, progress: 0, message: 'Manual threshold colony count…' });
            const isGrid = state.mode === 'rectangular' && Array.isArray(state.cells);
            const endpoint = isGrid ? '/automask_grid' : '/automask';
            const body = {
              sessionId,
              frame_idx: toBackendIndex(currentIndex),
              pipeline: 'morphological',
              pipeline_kwargs,
              ...(isGrid ? { cells: state.cells } : { roi: state.roi }),
            };
            const startResp = await fetch(`${gpuUrl}${endpoint}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
            const startJson = await startResp.json();
            if (!startResp.ok) throw new Error(startJson.detail || startJson.error || 'Failed to start manual threshold task.');
            const result = await pollGpuTask(startJson.task_id, sessionId, (s) => {
              setAutoSegProgress({
                inProgress: s.stage !== 'complete' && s.stage !== 'error',
                progress: typeof s.progress === 'number' ? s.progress : 0,
                message: s.message || s.stage || 'Processing...',
              });
            });
            const masks = result?.result?.masks || [];
            if (masks.length === 0) {
              enqueueSnackbar('No components passed the threshold. Try adjusting sliders.', { variant: 'warning' });
              return;
            }
            const decoded = masks.map((m) => decodeCOCORLE({ counts: m.counts, size: m.size }));
            const maskMeta = masks.map((m) => extractMaskMeta(m));
            const kept = state.mode === '90mm' && state.dishMeta
              ? importMasksAsObjects(decoded, state.dishMeta, maskMeta)
              : importMasksAsObjects(decoded, null, maskMeta);
            enqueueSnackbar(`Detected ${kept ?? masks.length} colonies (manual threshold).`, { variant: 'success' });
          } catch (err) {
            console.error('manual threshold error:', err);
            enqueueSnackbar(`Manual threshold failed: ${err.message}.`, { variant: 'error' });
          } finally {
            setIsGlobalLoading(false);
            setAutoSegProgress({ inProgress: false, progress: 0, message: '' });
          }
        }}
      />

      {/* Find-All-Similar: pick exemplar + threshold, then dispatch automask
          with pipeline=find_similar. Mask is encoded RLE client-side so the
          backend doesn't depend on session.propagated_masks for this frame. */}
      <FindSimilarDialog
        open={Boolean(findSimilarDialogState)}
        onClose={() => setFindSimilarDialogState(null)}
        /* Shallow-spread so each parent re-render produces a new prop
           reference. Without this the dialog's internal useMemo keyed on
           [objectMeta, objectMasks] never recomputes because the parent
           memo returns the same mutable ref every time — so even with 3
           colonies on the canvas the dialog reads an empty objectIds set
           on first paint. Matches the ObjectManagerPanel pattern. */
        objectMeta={{ ...objectMeta }}
        objectMasks={{ ...objectMasks }}
        onRun={async ({ pipeline_kwargs }) => {
          const state = findSimilarDialogState;
          setFindSimilarDialogState(null);
          const sessionId = sessionIdQuery || sessionStorage.getItem('sessionId');
          if (!sessionId || !state) return;
          try {
            setIsGlobalLoading(true);
            setAutoSegProgress({ inProgress: true, progress: 0, message: 'Finding similar colonies…' });
            const isGrid = state.mode === 'rectangular' && Array.isArray(state.cells);
            const endpoint = isGrid ? '/automask_grid' : '/automask';
            const body = {
              sessionId,
              frame_idx: toBackendIndex(currentIndex),
              pipeline: 'find_similar',
              pipeline_kwargs,
              ...(isGrid ? { cells: state.cells } : { roi: state.roi }),
            };
            const startResp = await fetch(`${gpuUrl}${endpoint}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
            const startJson = await startResp.json();
            if (!startResp.ok) throw new Error(startJson.detail || startJson.error || 'Failed to start find-similar task.');
            const result = await pollGpuTask(startJson.task_id, sessionId, (s) => {
              setAutoSegProgress({
                inProgress: s.stage !== 'complete' && s.stage !== 'error',
                progress: typeof s.progress === 'number' ? s.progress : 0,
                message: s.message || s.stage || 'Processing...',
              });
            });
            const masks = result?.result?.masks || [];
            if (masks.length === 0) {
              enqueueSnackbar('No similar colonies found. Try lowering the threshold.', { variant: 'warning' });
              return;
            }
            // find_similar returns bbox-cropped RLEs (small payload, each
            // carries {bbox:[x,y,w,h], full_size:[H,W]}). Decoded mask coords
            // are bbox-local; set offsetX/offsetY so importMasksAsObjects'
            // dish-circle check runs in the same pixel space as cx/cy/r.
            const decoded = masks.map((m) => {
              const d = decodeCOCORLE({ counts: m.counts, size: m.size });
              if (d && Array.isArray(m.bbox) && Array.isArray(m.full_size)) {
                d.offsetX = m.bbox[0];
                d.offsetY = m.bbox[1];
                d.fullWidth = m.full_size[1];
                d.fullHeight = m.full_size[0];
              }
              return d;
            });
            const maskMeta = masks.map((m) => extractMaskMeta(m));
            const kept = state.mode === '90mm' && state.dishMeta
              ? importMasksAsObjects(decoded, state.dishMeta, maskMeta)
              : importMasksAsObjects(decoded, null, maskMeta);
            enqueueSnackbar(`Found ${kept ?? masks.length} similar colonies.`, { variant: 'success' });
          } catch (err) {
            console.error('find-similar error:', err);
            enqueueSnackbar(`Find-similar failed: ${err.message}.`, { variant: 'error' });
          } finally {
            setIsGlobalLoading(false);
            setAutoSegProgress({ inProgress: false, progress: 0, message: '' });
          }
        }}
      />

      {/* Spiral Plate Config Dialog */}
      <Dialog
        open={showSpiralConfigDialog}
        onClose={() => setShowSpiralConfigDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1600 }}
      >
        <DialogTitle>Spiral Plate Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Plate boundary aligned. Configure plating parameters below, then run auto-detection. You can correct the results by adding or removing colonies on the plate view.
            </Typography>
            {/* Confirmed plate boundary (read-only) */}
            <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                Plate Boundary (from alignment)
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#888', fontFamily: 'monospace' }}>
                Center: ({spiralPlateConfig.center_x}, {spiralPlateConfig.center_y}) | Radius: {spiralPlateConfig.radius}px
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Plate Diameter (mm)"
                type="number"
                size="small"
                value={spiralPlateConfig.plate_diameter_mm}
                onChange={(e) => setSpiralPlateConfig((c) => ({ ...c, plate_diameter_mm: Number(e.target.value) }))}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Total Volume (uL)"
                type="number"
                size="small"
                value={spiralPlateConfig.total_volume_uL}
                onChange={(e) => setSpiralPlateConfig((c) => ({ ...c, total_volume_uL: Number(e.target.value) }))}
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Sectors (rings)"
                type="number"
                size="small"
                value={spiralPlateConfig.num_sectors}
                onChange={(e) => setSpiralPlateConfig((c) => ({ ...c, num_sectors: Number(e.target.value) }))}
                inputProps={{ min: 2, max: 12 }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Quadrants"
                type="number"
                size="small"
                value={spiralPlateConfig.num_quadrants}
                onChange={(e) => setSpiralPlateConfig((c) => ({ ...c, num_quadrants: Number(e.target.value) }))}
                inputProps={{ min: 2, max: 8 }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSpiralConfigDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleSpiralConfigConfirm()}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Auto-Detect Colonies
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Analysis Dialog */}
      <Dialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        sx={{ zIndex: 1600 }}
      >
        <DialogTitle>Save Analysis</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Analysis Name"
            fullWidth
            variant="outlined"
            value={saveAnalysisName}
            onChange={(e) => setSaveAnalysisName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAnalysis(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveAnalysis} variant="contained" disabled={!saveAnalysisName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

    </Grid>
  );

};

export default Segmentation;